import React, { useState, useRef, useEffect } from 'react';
import {
  Container, Paper, AppBar, Toolbar, IconButton,
  Typography, Box, TextField, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Drawer, Divider
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import SettingsPanel from './SettingsPanel';
import ChatWindow, { normalizeOperatorDigest } from './ChatWindow';
import DemoStand from './DemoStand';
import OcmTimeline from './OcmTimeline';
import { pingApi, listModels, fetchStatus } from './diagnostic';
import { buildThinkerTickPayload, shouldDelayAutoThinkerAfterChat } from './tickPayloads';
import { computeApiBase } from './apiBase';
import './App.css';
const API_BASE = computeApiBase();

const FALLBACK_MODELS = [
  'qwen3:30b',
  'gpt-oss:20b',
  'qwen2.5:0.5b'
];

const getInitialNick = () =>
  localStorage.getItem('mutant-nick') || 'Гость';

const THREADS_KEY = 'mutant-threads';
const ACTIVE_THREAD_KEY = 'mutant-active-thread';
const LEGACY_HISTORY_KEY = 'mutant-history';
const ASKED_NAME_KEY = 'mutant-asked-name';
const AUTH_TOKEN_KEY = 'mutant-auth-token';
const RUNTIME_THREAD_ID = 'runtime-monitor';
const RUNTIME_THREAD_TITLE = '⚙ Runtime';
const RUNTIME_THREAD_HISTORY_LIMIT = 100;
const RUNTIME_MONITOR_CACHE_KEY = 'mutant-runtime-monitor-cache';
const RUNTIME_MONITOR_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const TRACE_DEBUG_KEY = 'fractal-trace-debug';

const isTraceDebugEnabled = () => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    return (
      localStorage.getItem(TRACE_DEBUG_KEY) === '1' ||
      params.get('debugTrace') === '1' ||
      params.get('trace') === 'debug'
    );
  } catch {
    return false;
  }
};

const makeThreadId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `t_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
};

const normalizeTaskQuery = (text) => {
  let q = String(text || '').trim();
  while (true) {
    const next = q.replace(/^проверка\s+запущена\s*:\s*/i, '').trim();
    if (next === q) break;
    q = next;
  }
  return q;
};

const buildManualTaskPayload = (query, {
  threadId = '',
  answerText = '',
  missingFields = [],
} = {}) => {
  const payload = {
    content: normalizeTaskQuery(query),
    kind: 'verify',
    priority: 1.0,
    title: 'manual',
    thread_id: threadId || undefined,
  };
  const answer = String(answerText || '').trim();
  const missing = Array.isArray(missingFields) ? missingFields.map((x) => String(x || '').trim()) : [];
  if (answer) {
    if (missing.includes('date')) payload.date_hint = answer;
    if (missing.includes('country')) payload.country_hint = answer;
    if (missing.includes('comparison_basis')) payload.comparison_basis = answer;
  }
  return payload;
};

const capRuntimeHistory = (history) => {
  const items = Array.isArray(history) ? history : [];
  return items.slice(-RUNTIME_THREAD_HISTORY_LIMIT);
};

const normalizeMonitorRows = (rows, limit = 24) => (
  Array.isArray(rows)
    ? rows.filter((row) => row && typeof row === 'object').slice(0, limit)
    : []
);

const traceHasRuntimeMonitor = (trace) => {
  const digest = normalizeOperatorDigest(trace);
  const monitor = digest.runtimeMonitor;
  return !!(
    monitor &&
    typeof monitor === 'object' &&
    (
      normalizeMonitorRows(monitor.flows, 1).length > 0 ||
      cleanMonitorText(monitor.current_ocm_label, 80) ||
      monitor.current?.flow_count !== undefined
    )
  );
};

const runtimeMonitorTraceFromStatus = (status) => {
  const src = status && typeof status === 'object' ? status : {};
  const monitorTrace = src.last_monitor_trace && typeof src.last_monitor_trace === 'object'
    ? src.last_monitor_trace
    : null;
  if (monitorTrace && traceHasRuntimeMonitor(monitorTrace)) return monitorTrace;
  const lastTrace = src.last_trace && typeof src.last_trace === 'object' ? src.last_trace : null;
  if (lastTrace && traceHasRuntimeMonitor(lastTrace)) return lastTrace;
  return null;
};

const loadRuntimeMonitorCache = () => {
  try {
    const raw = localStorage.getItem(RUNTIME_MONITOR_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.saved_at || 0);
    if (savedAt > 0 && Date.now() - savedAt > RUNTIME_MONITOR_CACHE_MAX_AGE_MS) return null;
    const trace = parsed?.last_trace && typeof parsed.last_trace === 'object' ? parsed.last_trace : null;
    if (!trace || !traceHasRuntimeMonitor(trace)) return null;
    return {
      last_trace: trace,
      runner_sequence_ocm: parsed?.runner_sequence_ocm && typeof parsed.runner_sequence_ocm === 'object'
        ? parsed.runner_sequence_ocm
        : null,
      saved_at: savedAt || Date.now(),
    };
  } catch {
    return null;
  }
};

const persistRuntimeMonitorCache = (status) => {
  const trace = runtimeMonitorTraceFromStatus(status);
  if (!trace) return null;
  const payload = {
    saved_at: Date.now(),
    last_trace: trace,
    runner_sequence_ocm: status?.runner_sequence_ocm && typeof status.runner_sequence_ocm === 'object'
      ? status.runner_sequence_ocm
      : null,
  };
  try {
    localStorage.setItem(RUNTIME_MONITOR_CACHE_KEY, JSON.stringify(payload));
  } catch {}
  return payload;
};

const cleanMonitorText = (value, limit = 260) => {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  return limit > 0 && text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
};

const asMonitorObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const asMonitorArray = (value) => (
  Array.isArray(value) ? value.filter((row) => row && typeof row === 'object') : []
);

const monitorFlowId = (row) => cleanMonitorText(row?.flow_id, 180);

const monitorRowMatchesFlow = (row, flowId) => {
  const fid = cleanMonitorText(flowId, 180);
  if (!fid || !row || typeof row !== 'object') return false;
  return cleanMonitorText(row.flow_id, 180) === fid ||
    cleanMonitorText(row.foreground_flow_id, 180) === fid ||
    cleanMonitorText(row.target_flow_id, 180) === fid ||
    cleanMonitorText(row.root_flow_id, 180) === fid ||
    cleanMonitorText(row.parent_flow_id, 180) === fid ||
    cleanMonitorText(row.carried_flow_id, 180) === fid ||
    (Array.isArray(row.carry_flow_ids) && row.carry_flow_ids.some((item) => cleanMonitorText(item, 180) === fid)) ||
    (Array.isArray(row.closed_flow_ids) && row.closed_flow_ids.some((item) => cleanMonitorText(item, 180) === fid)) ||
    (Array.isArray(row.awaiting_user_flow_ids) && row.awaiting_user_flow_ids.some((item) => cleanMonitorText(item, 180) === fid)) ||
    (Array.isArray(row.awaiting_tool_flow_ids) && row.awaiting_tool_flow_ids.some((item) => cleanMonitorText(item, 180) === fid));
};

const monitorValueText = (value, limit = 180) => {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) {
    return value.map((item) => cleanMonitorText(item, 80)).filter(Boolean).slice(0, 6).join(', ');
  }
  if (typeof value === 'object') {
    const packet = cleanMonitorText(value.packet_version || value.command_version || value.reason_code || value.status || '', limit);
    return packet || cleanMonitorText(JSON.stringify(value), limit);
  }
  return cleanMonitorText(value, limit);
};

const monitorRowSummary = (row, fields, limit = 260) => {
  if (!row || typeof row !== 'object') return '';
  const parts = [];
  for (const field of fields) {
    const value = monitorValueText(row[field], limit);
    if (value) parts.push(`${field}=${value}`);
  }
  return parts.join(', ');
};

const monitorVisualResultSummary = (feedback, limit = 340) => {
  const result = asMonitorObject(feedback?.result_packet);
  const fact = asMonitorObject(result.sensor_fact);
  const source = asMonitorObject(result.source_ref);
  const first = (...values) => {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return '';
  };
  if (!Object.keys(fact).length && !Object.keys(source).length && feedback?.source_kind) {
    return monitorRowSummary(feedback, [
      'action_id',
      'capability_id',
      'packet_version',
      'sensor_id',
      'source_kind',
      'status',
      'lifecycle_state',
      'fresh_capture',
      'stale_source',
      'fresh_capture_configured',
      'capture_readiness_status',
      'rtsp_url_count',
      'ffmpeg_available',
      'camera_channel',
      'analysis_buffered_at',
      'source_image_mtime',
      'analysis_image_path',
      'analysis_buffer_metadata_path',
      'summary_text',
    ], limit);
  }
  if (!Object.keys(fact).length && !Object.keys(source).length) return '';
  const row = {
    action_id: feedback?.action_id,
    capability_id: result.capability_id,
    packet_version: result.packet_version,
    sensor_id: first(fact.sensor_id, source.sensor_id),
    source_kind: first(fact.source_kind, source.source_kind),
    fresh_capture: first(fact.fresh_capture, source.fresh_capture),
    stale_source: first(fact.stale_source, source.stale_source),
    fresh_capture_configured: first(fact.fresh_capture_configured, source.fresh_capture_configured),
    capture_readiness_status: first(fact.capture_readiness_status, source.capture_readiness_status),
    rtsp_url_count: first(fact.rtsp_url_count, source.rtsp_url_count),
    ffmpeg_available: first(fact.ffmpeg_available, source.ffmpeg_available),
    camera_channel: first(fact.camera_channel, source.camera_channel, fact.channel, source.channel),
    analysis_buffered_at: first(fact.analysis_buffered_at, source.analysis_buffered_at),
    source_image_mtime: first(fact.source_image_mtime, source.source_image_mtime),
    analysis_image_path: first(fact.analysis_image_path, source.analysis_image_path, fact.image_path, source.image_path),
    analysis_buffer_metadata_path: first(fact.analysis_buffer_metadata_path, source.analysis_buffer_metadata_path),
  };
  return monitorRowSummary(row, [
    'action_id',
    'capability_id',
    'packet_version',
    'sensor_id',
    'source_kind',
    'fresh_capture',
    'stale_source',
    'fresh_capture_configured',
    'capture_readiness_status',
    'rtsp_url_count',
    'ffmpeg_available',
    'camera_channel',
    'analysis_buffered_at',
    'source_image_mtime',
    'analysis_image_path',
    'analysis_buffer_metadata_path',
  ], limit);
};

const uniqueMonitorRows = (rows, keyFields = ['flow_id', 'action_id', 'tail_id', 'update_index', 'reason_code']) => {
  const out = [];
  const seen = new Set();
  for (const row of rows) {
    const key = keyFields.map((field) => monitorValueText(row?.[field], 120)).join('|') || JSON.stringify(row).slice(0, 180);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
};

const buildMonitorFlowDetail = (trace, flow) => {
  const flowId = monitorFlowId(flow);
  const shadow = asMonitorObject(trace?.flow_shadow);
  const workPackage = asMonitorObject(shadow.work_package);
  const egress = asMonitorObject(shadow.l1_egress_package || shadow.egress_package);
  const command = asMonitorObject(egress.l1_command_packet);
  const actionBatch = asMonitorObject(command.action_command_batch);
  const accounting = asMonitorObject(shadow.flow_accounting);
  const actionShadow = asMonitorObject(shadow.action_dispatch_shadow);
  const actionBatchShadow = asMonitorObject(actionShadow.batch);
  const reality = asMonitorObject(shadow.reality_layer_packet);

  const registryRows = uniqueMonitorRows([
    ...asMonitorArray(shadow.registry?.flows),
    ...asMonitorArray(shadow.registry),
    ...asMonitorArray(workPackage.support_flows),
    ...asMonitorArray(workPackage.deferred_flows),
    ...asMonitorArray(workPackage.foreground_flow ? [workPackage.foreground_flow] : []),
    ...asMonitorArray(shadow.window),
  ].filter((row) => monitorRowMatchesFlow(row, flowId)));

  const obligationRows = asMonitorArray(workPackage.obligations)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const flowCommandRows = asMonitorArray(command.flow_commands)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const actionCommandRows = asMonitorArray(actionBatch.commands)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const tailCommandRows = asMonitorArray(command.tail_commands)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const branchDispatchRows = asMonitorArray(egress.branch_dispatches)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const dispatchCommandRows = asMonitorArray(actionBatchShadow.commands)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const feedbackRows = asMonitorArray(actionShadow.feedback_packets)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const evidenceRows = asMonitorArray(reality.evidence_packets)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const flowUpdateRows = asMonitorArray(egress.flow_updates)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const tailDispatchRows = asMonitorArray(egress.l4_tail_dispatches)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const rejectionRows = asMonitorArray(egress.validation?.action_capability_rejections)
    .filter((row) => monitorRowMatchesFlow(row, flowId));
  const suppressedRows = asMonitorArray(egress.suppressed_outputs)
    .filter((row) => asMonitorArray(row.rejections).some((rejection) => monitorRowMatchesFlow(rejection, flowId)));
  const handoff = asMonitorObject(egress.next_ocm_handoff);
  const handoffRows = monitorRowMatchesFlow(handoff, flowId) ? [handoff] : [];
  const gap = asMonitorObject(accounting.gaps);
  const ladder = asMonitorObject(accounting.action_ladder);

  const meta = [
    monitorRowSummary(flow, ['flow_id', 'lane', 'flow_kind', 'lifecycle_state', 'promotion_state'], 180),
    monitorRowSummary(flow, ['source_owners', 'packet_types', 'candidate_n', 'origin_ocm', 'last_processed_ocm', 'age_ocm'], 180),
    monitorRowSummary(flow, ['contains_user_signal', 'contains_action_outcome', 'contains_control_signal', 'needs_l1_now'], 180),
  ].filter(Boolean);

  const sections = [
    {
      title: 'Registry',
      lines: registryRows.map((row) => monitorRowSummary(row, [
        'link_key',
        'root_flow_id',
        'parent_flow_id',
        'object_id',
        'primary_summary',
        'member_count',
        'freshness_head',
      ], 320)).filter(Boolean),
    },
    {
      title: 'L1 delivery',
      lines: obligationRows.map((row) => monitorRowSummary(row, [
        'lane',
        'kind',
        'reason',
        'summary',
        'memory_scope',
        'l4_tail_policy',
      ], 320)).filter(Boolean),
    },
    {
      title: 'L1 commands',
      lines: [
        ...flowCommandRows.map((row) => monitorRowSummary(row, ['command_kind', 'status', 'lifecycle_state', 'reason_code'], 240)),
        ...actionCommandRows.map((row) => monitorRowSummary(row, ['action_id', 'branch', 'capability', 'task_kind', 'instruction', 'reason_code'], 320)),
        ...tailCommandRows.map((row) => monitorRowSummary(row, ['tail_id', 'tail_kind', 'check_condition', 'expected_outcome', 'reason_code'], 280)),
      ].filter(Boolean),
    },
    {
      title: 'Egress / dispatch',
      lines: [
        ...branchDispatchRows.map((row) => monitorRowSummary(row, ['dispatch_id', 'action_id', 'branch', 'capability', 'status', 'reason_code'], 260)),
        ...dispatchCommandRows.map((row) => monitorRowSummary(row, ['action_id', 'branch', 'capability', 'status', 'reason_code'], 260)),
        ...flowUpdateRows.map((row) => monitorRowSummary(row, ['update_kind', 'command_kind', 'status', 'reason_code'], 260)),
        ...tailDispatchRows.map((row) => monitorRowSummary(row, ['tail_id', 'tail_kind', 'check_condition', 'reopen_policy'], 260)),
        ...handoffRows.map((row) => monitorRowSummary(row, ['command_kind', 'raw_command_kind', 'carry_flow_ids', 'closed_flow_ids', 'reason_code'], 300)),
      ].filter(Boolean),
    },
    {
      title: 'Action feedback / reality',
      lines: [
        ...feedbackRows.map((row) => monitorRowSummary(row, ['action_id', 'controller', 'status', 'lifecycle_state', 'expected_result'], 260)),
        ...feedbackRows.map((row) => monitorVisualResultSummary(row, 420)),
        ...evidenceRows.map((row) => monitorRowSummary(row, ['evidence_id', 'capability_id', 'source_kind', 'claim_scope', 'evidence_status', 'confidence', 'summary'], 360)),
      ].filter(Boolean),
    },
    {
      title: 'Suppressed / rejected',
      lines: [
        ...suppressedRows.map((row) => monitorRowSummary(row, ['suppressed_type', 'source', 'reason_code', 'count'], 260)),
        ...rejectionRows.map((row) => monitorRowSummary(row, ['rejection_kind', 'action_id', 'branch', 'capability_id', 'reason_code'], 260)),
      ].filter(Boolean),
    },
    {
      title: 'Accounting',
      lines: [
        monitorRowSummary(accounting, ['packet_version', 'valid', 'notes'], 260),
        monitorRowSummary(ladder, ['l1_action_command_n', 'egress_branch_dispatch_n', 'action_dispatch_command_n', 'action_feedback_n', 'reality_evidence_n'], 220),
        monitorRowSummary(gap, ['l1_action_to_egress_gap_n', 'egress_to_dispatch_gap_n', 'dispatch_feedback_gap_n', 'feedback_to_reality_gap_n', 'l4_tail_dispatch_to_state_gap_n'], 220),
      ].filter(Boolean),
    },
  ].map((section) => ({
    ...section,
    lines: uniqueMonitorRows(section.lines.map((line) => ({ line })), ['line']).map((row) => row.line),
  })).filter((section) => section.lines.length > 0);

  return { meta, sections };
};

const normalizeRunnerSequenceSteps = (status) => {
  const src = status && typeof status === 'object' ? status : {};
  return normalizeMonitorRows(src.steps, 20).map((row, i) => {
    const stepIndex = Number(row.step_index ?? i);
    const ocmOrdinalRaw = row.ocm_ordinal ?? row.ocm_index ?? null;
    const ocmOrdinal = ocmOrdinalRaw === null || ocmOrdinalRaw === undefined || ocmOrdinalRaw === ''
      ? null
      : Number(ocmOrdinalRaw);
    const replyHead = cleanMonitorText(row.reply_head || row.reply_text || row.reply_user || '', 180);
    const road = row.test_drive_step && typeof row.test_drive_step === 'object'
      ? row.test_drive_step
      : {};
    return {
      key: `${stepIndex}-${row.started_at || row.finished_at || i}`,
      number: Number.isFinite(stepIndex) ? stepIndex + 1 : i + 1,
      status: cleanMonitorText(row.status || 'pending', 40),
      mode: cleanMonitorText(row.mode, 40),
      ocm_id: cleanMonitorText(row.ocm_id || row.focused_ocm_id || '', 100),
      ocm_ordinal: Number.isFinite(ocmOrdinal) ? ocmOrdinal : null,
      query_source: cleanMonitorText(row.query_source, 100),
      stop_reason: cleanMonitorText(row.stop_reason, 100),
      terminal_kind: cleanMonitorText(row.terminal_kind, 80),
      error: cleanMonitorText(row.error, 140),
      reply_head: replyHead,
      has_user_signal: !!row.has_user_signal,
      has_explicit_query: !!row.has_explicit_query,
      road: {
        packet_version: cleanMonitorText(road.packet_version, 80),
        phase: cleanMonitorText(road.phase, 40),
        foreground_flow_id: cleanMonitorText(road.foreground_flow_id, 120),
        semantic_delta: cleanMonitorText(road.semantic_delta, 320),
        next_move_kind: cleanMonitorText(road.next_move_kind, 60),
        flow_fate: cleanMonitorText(road.flow_fate, 60),
        action_id: cleanMonitorText(road.action_id, 120),
        capability_id: cleanMonitorText(road.capability_id, 120),
        action_instruction: cleanMonitorText(road.action_instruction, 260),
        payload_present: !!road.payload_present,
        payload_keys: Array.isArray(road.payload_keys) ? road.payload_keys.map((x) => cleanMonitorText(x, 60)).filter(Boolean) : [],
        payload_preview: road.payload_preview && typeof road.payload_preview === 'object' ? road.payload_preview : {},
        branch_plan_status: cleanMonitorText(road.branch_plan_status, 60),
        branch_plan_source: cleanMonitorText(road.branch_plan_source, 100),
        payload_fingerprint: cleanMonitorText(road.payload_fingerprint, 80),
        branch_plan_missing_inputs: Array.isArray(road.branch_plan_missing_inputs)
          ? road.branch_plan_missing_inputs.map((x) => cleanMonitorText(x, 80)).filter(Boolean)
          : [],
        action_status: cleanMonitorText(road.action_status, 60),
        observation_summary: cleanMonitorText(road.observation_summary, 320),
        observation_files: Array.isArray(road.observation_files)
          ? road.observation_files.map((x) => cleanMonitorText(x, 180)).filter(Boolean)
          : [],
        evidence_ids: Array.isArray(road.evidence_ids) ? road.evidence_ids.map((x) => cleanMonitorText(x, 120)).filter(Boolean) : [],
        l4_match: cleanMonitorText(road.l4_match, 60),
        l4_prediction_error: cleanMonitorText(road.l4_prediction_error, 220),
        contract_gap_n: Number(road.contract_gap_n || 0),
        rejection_reason: cleanMonitorText(road.rejection_reason, 180),
      },
    };
  });
};

const runnerSequenceHasMonitorValue = (status) => {
  const src = status && typeof status === 'object' ? status : {};
  const state = String(src.status || '').trim().toLowerCase();
  return !!(
    String(src.run_id || '').trim() ||
    String(src.thread_id || '').trim() ||
    normalizeMonitorRows(src.steps, 1).length > 0 ||
    Number(src.completed_steps || 0) > 0 ||
    Number(src.max_steps || 0) > 0 ||
    ['queued', 'running', 'stopping', 'completed', 'failed', 'stopped'].includes(state)
  );
};

const statusTraceMatchesThread = (status, requestedThreadId = '') => {
  const requested = cleanMonitorText(requestedThreadId, 180);
  if (!requested) return false;
  const trace = status?.last_trace && typeof status.last_trace === 'object' ? status.last_trace : {};
  const rt = trace._runtime && typeof trace._runtime === 'object' ? trace._runtime : {};
  const traceThread = cleanMonitorText(
    trace.thread_id ||
    status?.last_meta?.thread_id ||
    rt.participant_window_thread_id ||
    rt.participant_window_packet?.window_thread_id ||
    '',
    180
  );
  return !!traceThread && traceThread === requested;
};

const makeParticipantId = () => `speaker:${makeThreadId()}`;

const normalizeParticipantName = (value, fallback = '') => {
  const clean = String(value || '').trim();
  if (clean) return clean.slice(0, 120);
  const fb = String(fallback || '').trim();
  return (fb || 'Гость').slice(0, 120);
};

const readParticipantFromMeta = (meta) => {
  const src = meta && typeof meta === 'object' ? meta : {};
  const participant = src.participant && typeof src.participant === 'object' ? src.participant : {};
  return {
    id: String(src.participant_id || participant.id || '').trim(),
    name: String(src.participant_name || participant.display_name || participant.name || '').trim(),
  };
};

const buildThreadParticipant = (thread, fallbackName = '') => {
  const src = thread && typeof thread === 'object' ? thread : {};
  const metaParticipant = readParticipantFromMeta(src.serverMeta || src.meta || {});
  const id = String(src.participantId || metaParticipant.id || (src.id ? `thread:${src.id}` : '') || makeParticipantId()).trim();
  const name = normalizeParticipantName(
    src.participantName || metaParticipant.name || (!isGenericThreadTitle(src.title) ? src.title : '') || src.ownerDisplayName || src.ownerLogin,
    fallbackName
  );
  return { id, name };
};

const normalizeThreadRecord = (thread) => {
  if (!thread || typeof thread !== 'object') return null;
  const id = String(thread.id || '').trim();
  if (!id) return null;
  const isRuntime = id === RUNTIME_THREAD_ID;
  const participant = isRuntime ? { id: '', name: '' } : buildThreadParticipant({ ...thread, id }, getInitialNick());
  return {
    ...thread,
    id,
    title: isRuntime ? RUNTIME_THREAD_TITLE : (String(thread.title || 'Чат').trim() || 'Чат'),
    participantId: participant.id,
    participantName: participant.name,
    history: isRuntime ? capRuntimeHistory(thread.history) : (Array.isArray(thread.history) ? thread.history : [])
  };
};

const normalizeServerMessage = (msg, fallbackUsername = '') => {
  const src = msg && typeof msg === 'object' ? msg : {};
  const role = String(src.role || '').trim().toLowerCase();
  const meta = src.meta && typeof src.meta === 'object' ? src.meta : {};
  const operatorDigest = meta.operator_digest && typeof meta.operator_digest === 'object'
    ? meta.operator_digest
    : null;
  const participant = readParticipantFromMeta(meta);
  const text = String(src.content || '').trim();
  return {
    id: src.id,
    role: role === 'assistant' ? 'llm' : (role || 'system'),
    text,
    timestamp: src.ts || new Date().toISOString(),
    meta,
    username: String(participant.name || src.username || fallbackUsername || '').trim(),
    thread_id: String(meta.thread_id || operatorDigest?.thread_id || '').trim(),
    origin: 'server',
    trace: operatorDigest ? {
      reply_text: text,
      reply_user: text,
      operator_digest: operatorDigest,
      _runtime: {
        source: 'server_history',
        trace_source: 'message_meta.operator_digest',
      },
    } : undefined,
  };
};

const normalizeServerChatRecord = (chat) => {
  const src = chat && typeof chat === 'object' ? chat : {};
  const id = String(src.thread_id || '').trim();
  if (!id) return null;
  const serverMeta = src.meta || {};
  const participant = buildThreadParticipant({
    id,
    title: src.title,
    serverMeta,
    ownerDisplayName: src.owner_display_name,
    ownerLogin: src.owner_login,
  });
  return {
    id,
    title: String(src.title || 'Чат').trim() || 'Чат',
    participantId: participant.id,
    participantName: participant.name,
    history: [],
    serverManaged: true,
    serverLoaded: false,
    serverPreview: String(src.preview || '').trim(),
    serverMeta,
    ownerLogin: String(src.owner_login || '').trim(),
    ownerDisplayName: String(src.owner_display_name || '').trim(),
    access: String(src.access || 'owner').trim() || 'owner',
    lastTs: src.last_ts || null,
    messageCount: Number(src.message_count || 0),
  };
};

const isGenericThreadTitle = (value) => {
  const clean = String(value || '').trim().toLowerCase();
  if (!clean) return true;
  return /^чат(?:\s+\d+)?$/.test(clean) || clean === 'без названия';
};

const buildThreadDisplayTitle = (thread) => {
  const src = thread && typeof thread === 'object' ? thread : {};
  const participant = buildThreadParticipant(src, '');
  if (participant.name && participant.name !== 'Гость') return participant.name;
  const explicit = String(src.title || '').trim();
  if (explicit && !isGenericThreadTitle(explicit)) return explicit;
  const preview = String(src.serverPreview || '').trim();
  if (preview) return preview.slice(0, 44);
  const history = Array.isArray(src.history) ? src.history : [];
  const recent = [...history]
    .reverse()
    .find((msg) => msg && msg.role !== 'system' && String(msg.text || '').trim());
  if (recent) return String(recent.text || '').trim().slice(0, 44);
  const ownerName = String(src.ownerDisplayName || src.ownerLogin || '').trim();
  if (ownerName) return `Диалог ${ownerName}`;
  return 'Новый чат';
};

const buildThreadMetaLabel = (thread) => {
  const src = thread && typeof thread === 'object' ? thread : {};
  const history = Array.isArray(src.history) ? src.history : [];
  const count = Number(src.messageCount || history.filter((msg) => msg && msg.role !== 'system').length || 0);
  const participant = buildThreadParticipant(src, '');
  if (participant.name && count > 0) return `${participant.name} · ${count} сообщений`;
  if (participant.name && participant.name !== 'Гость') return participant.name;
  const ownerName = String(src.ownerDisplayName || src.ownerLogin || '').trim();
  if (ownerName && count > 0) return `${ownerName} · ${count} сообщений`;
  if (ownerName) return ownerName;
  if (count > 0) return `${count} сообщений`;
  return 'Пусто';
};

const _threadTsValue = (value) => {
  if (!value) return 0;
  const ms = Date.parse(String(value));
  return Number.isFinite(ms) ? ms : 0;
};

const _threadLastActivityTs = (thread) => {
  const src = thread && typeof thread === 'object' ? thread : {};
  const history = Array.isArray(src.history) ? src.history : [];
  const historyTs = history.reduce((best, item) => {
    const ts = _threadTsValue(item?.timestamp || item?.ts || '');
    return ts > best ? ts : best;
  }, 0);
  const serverTs = _threadTsValue(src.lastTs || src.last_ts || '');
  return Math.max(historyTs, serverTs);
};

export const buildStatusTraceFingerprint = (status, requestedThreadId = '') => {
  const src = status && typeof status.last_trace === 'object' ? status.last_trace : null;
  if (!src) return '';
  const rt = src && typeof src._runtime === 'object' ? src._runtime : {};
  const reply = String(src.reply_user || src.reply_text || '').trim();
  const turnType = String(src?.dialogue_turn?.turn_type || src.turn_type || '').trim();
  const questions = (
    Array.isArray(src.question_items)
      ? src.question_items.map((it) => String(it?.text || '').trim()).filter(Boolean)
      : []
  );
  const fallbackQuestions = (
    Array.isArray(src.questions_to_user)
      ? src.questions_to_user.map((it) => String(it || '').trim()).filter(Boolean)
      : []
  );
  const awaiting = !!(
    src.awaiting_user_answer ||
    src?.user_interaction?.awaiting_user_answer ||
    rt.awaiting_user_answer
  );
  const traceThreadId = String(
    src.thread_id ||
    status?.last_meta?.thread_id ||
    requestedThreadId ||
    ''
  ).trim();
  const stamp = String(
    status?.last_response?.timestamp ||
    status?.last_response?.ts ||
    ''
  ).trim();
  const source = String(rt.source || status?.last_meta?.source || '').trim();
  const mode = String(rt.mode || status?.last_meta?.mode || '').trim();
  const toolPacket = String(rt.tool_transfer_packet_type || '').trim();
  const query = String(rt.query || status?.last_meta?.query || '').trim();
  return JSON.stringify({
    thread_id: traceThreadId,
    stamp,
    source,
    mode,
    turn_type: turnType,
    tool_packet: toolPacket,
    reply,
    query,
    questions: questions.length > 0 ? questions : fallbackQuestions,
    awaiting,
  });
};

const isAwaitUserMaintenanceHoldTrace = (value) => {
  const src = value && typeof value === 'object' ? value : {};
  const parsed = src.parsed && typeof src.parsed === 'object' ? src.parsed : {};
  const arb = src.arbiter && typeof src.arbiter === 'object'
    ? src.arbiter
    : (parsed.arbiter && typeof parsed.arbiter === 'object' ? parsed.arbiter : parsed);
  const runtime = {
    ...(src._runtime && typeof src._runtime === 'object' ? src._runtime : {}),
    ...(parsed._runtime && typeof parsed._runtime === 'object' ? parsed._runtime : {}),
    ...(arb?._runtime && typeof arb._runtime === 'object' ? arb._runtime : {}),
  };
  if (runtime.reply_suppressed_by_maintenance_hold || runtime.chat_surface_suppressed) return true;
  const routeFamily = String(runtime.terminal_route_family || runtime.route_family || '').trim().toLowerCase();
  const decision = String(runtime.ocm_continuity_decision || '').trim().toLowerCase();
  const querySource = String(runtime.query_final_source || runtime.query_source || '').trim().toLowerCase();
  const reason = String(
    runtime.reply_suppress_reason ||
    runtime.dialogue_turn_reason ||
    runtime.terminal_reason_code ||
    ''
  ).trim().toLowerCase();
  return routeFamily === 'maintenance_hold'
    && decision === 'maintenance_mode'
    && (querySource.includes('await_user') || reason.includes('await_user'));
};

const sortThreadsByRecency = (items) => {
  const src = Array.isArray(items) ? items.filter(Boolean) : [];
  const runtime = src.find((t) => t?.id === RUNTIME_THREAD_ID);
  const normal = src
    .filter((t) => t?.id !== RUNTIME_THREAD_ID)
    .slice()
    .sort((a, b) => {
      const dt = _threadLastActivityTs(b) - _threadLastActivityTs(a);
      if (dt !== 0) return dt;
      const bc = Number(b?.messageCount || 0);
      const ac = Number(a?.messageCount || 0);
      if (bc !== ac) return bc - ac;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
  return runtime ? [runtime, ...normal] : normal;
};

const resolvePreferredActiveThread = (threads, requestedActive) => {
  const requested = String(requestedActive || '').trim();
  if (requested && requested !== RUNTIME_THREAD_ID && threads.some((t) => t?.id === requested)) {
    return requested;
  }
  return threads[1]?.id || threads[0]?.id || RUNTIME_THREAD_ID;
};

const loadThreads = () => {
  const normalizeLoadedThreads = (arr) => {
    const src = Array.isArray(arr) ? arr : [];
    const runtimeRaw = src.find((t) => t && t.id === RUNTIME_THREAD_ID);
    const runtimeThread = normalizeThreadRecord(runtimeRaw) || {
      id: RUNTIME_THREAD_ID,
      title: RUNTIME_THREAD_TITLE,
      history: []
    };
    const nonRuntime = src
      .filter((t) => t && t.id !== RUNTIME_THREAD_ID)
      .map(normalizeThreadRecord)
      .filter(Boolean);
    return sortThreadsByRecency([runtimeThread, ...nonRuntime]);
  };
  try {
    const raw = localStorage.getItem(THREADS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.threads) && parsed.threads.length > 0) {
        const threads = normalizeLoadedThreads(parsed.threads);
        const requestedActive = String(localStorage.getItem(ACTIVE_THREAD_KEY) || parsed.threads[0]?.id || '').trim();
        const active = resolvePreferredActiveThread(threads, requestedActive);
        return { threads, activeThreadId: active };
      }
      if (Array.isArray(parsed) && parsed.length > 0) {
        const threads = normalizeLoadedThreads(parsed);
        const requestedActive = String(localStorage.getItem(ACTIVE_THREAD_KEY) || parsed[0]?.id || '').trim();
        const active = resolvePreferredActiveThread(threads, requestedActive);
        return { threads, activeThreadId: active };
      }
    }
  } catch {}

  let legacyHistory = [];
  try {
    const legacyRaw = localStorage.getItem(LEGACY_HISTORY_KEY);
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw);
      if (Array.isArray(parsed)) legacyHistory = parsed;
    }
  } catch {}

  const id = makeThreadId();
  const baseThread = {
    id,
    title: 'Собеседник 1',
    participantId: `thread:${id}`,
    participantName: 'Собеседник 1',
    history: legacyHistory
  };
  return {
    threads: sortThreadsByRecency([{ id: RUNTIME_THREAD_ID, title: RUNTIME_THREAD_TITLE, history: [] }, baseThread]),
    activeThreadId: id
  };
};

const inferOwnerTone = (source) => {
  const s = String(source || '').trim().toLowerCase();
  if (s.startsWith('rivet:')) return 'rivet';
  if (s.startsWith('ocm_continuity:')) return 'continuity';
  if (s.startsWith('ocm_outcome_handoff:')) return 'handoff';
  if (s.startsWith('focus_selector:')) return 'focus';
  if (s.startsWith('input')) return 'input';
  return 'neutral';
};

const describeOwnerTone = (tone) => {
  switch (String(tone || '').trim()) {
    case 'rivet':
      return 'RIVET lane';
    case 'continuity':
      return 'continuity lane';
    case 'handoff':
      return 'handoff lane';
    case 'focus':
      return 'focus lane';
    case 'input':
      return 'input lane';
    default:
      return 'neutral lane';
  }
};

const inferToolBranchTone = (branch) => {
  switch (String(branch || '').trim().toLowerCase()) {
    case 'clarify':
      return 'amber';
    case 'compare':
      return 'focus';
    case 'verify':
      return 'slate';
    default:
      return 'input';
  }
};

const describeToolBranch = (branch) => {
  switch (String(branch || '').trim().toLowerCase()) {
    case 'clarify':
      return 'clarify';
    case 'compare':
      return 'compare';
    case 'verify':
      return 'verify';
    default:
      return 'none';
  }
};

const looksExecutorGarbage = (text) => {
  const raw = String(text || '').trim();
  const low = raw.toLowerCase();
  if (!low) return false;
  const markers = [
    'leave a reply',
    'cancel reply',
    'your email address will not be published',
    'required fields are marked',
    'save my name, email, and website',
    'search for:',
    'subscribe to email updates',
    'archives select month',
  ];
  if (markers.some((marker) => low.includes(marker))) return true;
  const markdownLinks = (raw.match(/\[[^\]]*\]\([^)]+\)/g) || []).length;
  const imageLinks = (raw.match(/!\[\]\(/g) || []).length;
  const urls = (raw.match(/https?:\/\//g) || []).length;
  const headings = (raw.match(/(^|\s)#{1,6}\s/g) || []).length;
  const bullets = (raw.match(/(^|\s)[*•]\s/g) || []).length;
  const sentenceish = (raw.match(/[.!?](\s|$)/g) || []).length;
  const words = (low.match(/[a-zа-яё0-9]{3,}/gi) || []).length;
  const artifactScore = markdownLinks + imageLinks + urls + headings + Math.max(0, bullets - 2);
  if (words <= 20 && artifactScore >= 2) return true;
  if (artifactScore >= 6 && sentenceish <= 2) return true;
  if (artifactScore >= 4 && (artifactScore / Math.max(1, words)) >= 0.08) return true;
  if (/(advertisement|video duration|download the .* app|live updates?)/i.test(raw)) return true;
  if (low.includes('play-arrow') || low.includes('content feed')) return true;
  return false;
};

const buildExecutorResultText = (result) => {
  const res = result && typeof result === 'object' ? result : {};
  const research = res.research && typeof res.research === 'object' ? res.research : {};
  const summary = research.summary && typeof research.summary === 'object' ? research.summary : {};
  const verdict = String(res.verdict || '').trim().toLowerCase();
  const enoughEvidence = !!summary.enough_evidence;
  const summaryText = String(res.summary || '').trim();
  const answerText = String(summary.answer || '').trim();
  const analysisText = String(summary.analysis || '').trim();
  const cleanAnswer = looksExecutorGarbage(answerText) ? '' : answerText;
  const cleanSummary = looksExecutorGarbage(summaryText) ? '' : summaryText;
  const summaryLooksSubstantive = /[.!?]/.test(cleanSummary) && cleanSummary.split(/\s+/).filter(Boolean).length >= 6;
  const summaryLooksHonestInsufficiency = /(недостаточно|не удалось|нужна внешняя|требует проверки|уточните|источники найдены)/i.test(cleanSummary);
  if (cleanAnswer && enoughEvidence) {
    return cleanAnswer;
  }
  if (cleanSummary && verdict !== 'insufficient') {
    return cleanSummary;
  }
  if (cleanSummary && verdict === 'insufficient' && (summaryLooksSubstantive || summaryLooksHonestInsufficiency)) {
    return cleanSummary;
  }
  if (analysisText && !looksExecutorGarbage(analysisText) && verdict === 'insufficient') {
    return `Недостаточно надежных и релевантных источников для уверенного ответа. ${analysisText}`.trim();
  }
  if (verdict === 'insufficient') {
    return 'Недостаточно надежных и релевантных источников для уверенного ответа. Нужна внешняя перепроверка или уточнение запроса.';
  }
  return cleanSummary || cleanAnswer || 'Проверка завершена, но пригодного краткого ответа не собралось.';
};

const buildExecutorSourcesText = (evidence, limit = 3) => {
  const rows = Array.isArray(evidence) ? evidence : [];
  return rows
    .slice(0, limit)
    .map((e) => {
      const title = String(e?.title || e?.url || 'Источник').trim();
      const url = String(e?.url || '').trim();
      return url ? `• ${title}\n  ${url}` : `• ${title}`;
    })
    .join('\n');
};

const buildToolBranchResult = (packet) => {
  const src = packet && typeof packet === 'object' ? packet : {};
  const research = src.research && typeof src.research === 'object' ? src.research : {};
  const groupedEvidence = Array.isArray(research.grouped_evidence) ? research.grouped_evidence : [];
  const displayEvidence = Array.isArray(src.display_evidence) ? src.display_evidence : [];
  return {
    summary: String(src.summary || src.answer || '').trim(),
    verdict: String(src.verdict || '').trim(),
    evidence: displayEvidence.length > 0 ? displayEvidence : (groupedEvidence.length > 0 ? groupedEvidence : (Array.isArray(src.evidence) ? src.evidence : [])),
    research,
  };
};

const OCM_PHASES = [
  { key: 'input', label: 'Вход' },
  { key: 'memory', label: 'Память' },
  { key: 'context', label: 'Контекст' },
  { key: 'l1', label: 'L1' },
  { key: 'perception', label: 'VLM' },
  { key: 'search', label: 'Vane' },
  { key: 'action', label: 'Action' },
  { key: 'output', label: 'Output' },
];

const _phaseText = (value) => String(value || '').trim().toLowerCase();

const inferOcmPhaseKey = ({
  tickActive,
  packetStatus,
  rawStage,
  contour,
  process,
  role,
  headline,
  detail,
  lastTickStage,
  lastTickError,
  runtime,
} = {}) => {
  const text = [
    rawStage,
    contour,
    process,
    role,
    headline,
    detail,
    lastTickStage,
    lastTickError,
  ].map(_phaseText).filter(Boolean).join(' ');
  const rt = runtime && typeof runtime === 'object' ? runtime : {};
  if (/(vlm|perception|sensor|camera|vision)/.test(text)) return 'perception';
  if (/(vane|search|research|browser|web_evidence|fetch)/.test(text)) return 'search';
  if (/(action_dispatch|action|tool|executor|adapter|capability|work_order)/.test(text)) return 'action';
  if (/(output_controller|reply|finalize|surface|user_reply)/.test(text)) return 'output';
  if (/(egress|l1_egress|handoff)/.test(text)) return 'output';
  if (/(llm_call|role:|pre:role|post:role|thinker|interpret|planner|critic|decide|context_router|l1)/.test(text)) return 'l1';
  if (/(context_builder|build_context|retrieval|rag|working_context)/.test(text)) return 'context';
  if (/(memory_loom|get_working_memory|memory|wm)/.test(text)) return 'memory';
  if (/(collect_pending_inputs|input|ingress|user_signal|dialogue|queue|child_boot|run_atomic_tick_engine|runner_tick)/.test(text)) return 'input';
  if (Number(rt.action_dispatch_command_n || 0) > 0 || Number(rt.action_dispatch_feedback_n || 0) > 0) return 'action';
  if (Number(rt.l1_action_command_n || 0) > 0 || String(rt.l1_egress_package_version || '').trim()) return 'output';
  if (Number(rt.flow_shadow_flow_count || 0) > 0) return 'l1';
  if (Number(rt.ingress_shadow_candidates_n || 0) > 0) return 'input';
  if (tickActive || packetStatus === 'running') return 'input';
  return '';
};

const isMissingContinuationHold = (value) =>
  String(value || '').trim().toLowerCase() === 'missing_continuation_query';

const buildOcmPhaseSnapshot = ({
  tickActive,
  packetStatus,
  phaseKey,
  headline,
  detail,
  elapsedS,
  timeoutS,
  lastTickError,
} = {}) => {
  const activeIndex = OCM_PHASES.findIndex((item) => item.key === phaseKey);
  const benignHold = isMissingContinuationHold(lastTickError) && !tickActive;
  const errorActive = !benignHold && (!!String(lastTickError || '').trim() || packetStatus === 'error');
  const status = errorActive ? 'error' : (tickActive || packetStatus === 'running' ? 'running' : 'idle');
  const steps = OCM_PHASES.map((item, index) => {
    let state = 'pending';
    if (activeIndex < 0) {
      state = status === 'idle' ? 'idle' : 'pending';
    } else if (index < activeIndex) {
      state = 'done';
    } else if (index === activeIndex) {
      state = status === 'error' ? 'error' : (status === 'running' ? 'active' : 'last');
    }
    return { ...item, state };
  });
  const active = OCM_PHASES[activeIndex] || null;
  const phaseLabel = active ? active.label : (status === 'idle' ? 'Ожидание' : 'Цикл');
  const display = String(headline || '').trim();
  const extra = String(detail || '').trim();
  return {
    status,
    phaseKey,
    phaseLabel,
    detail: extra || display,
    elapsedS: Number(elapsedS || 0),
    timeoutS: Number(timeoutS || 0),
    steps,
  };
};

const _trimPreview = (value, max = 220) => {
  const text = String(value || '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
};

const _pickTraceQuestion = (trace) => {
  const src = trace && typeof trace === 'object' ? trace : {};
  const fromItems = Array.isArray(src.question_items)
    ? src.question_items.map((it) => String(it?.text || '').trim()).filter(Boolean)
    : [];
  if (fromItems.length > 0) return fromItems[0];
  const fromQuestions = Array.isArray(src.questions_to_user)
    ? src.questions_to_user.map((it) => String(it || '').trim()).filter(Boolean)
    : [];
  if (fromQuestions.length > 0) return fromQuestions[0];
  const fromPending = Array.isArray(src.user_interaction?.pending_questions)
    ? src.user_interaction.pending_questions.map((it) => String(it || '').trim()).filter(Boolean)
    : [];
  return fromPending[0] || '';
};

const _lastArrayItem = (items) => {
  const arr = Array.isArray(items) ? items.filter((item) => item && typeof item === 'object') : [];
  return arr.length ? arr[arr.length - 1] : {};
};

const _packetVersion = (packet) => (
  packet && typeof packet === 'object'
    ? String(packet.packet_version || packet.packet_type || '').trim()
    : ''
);

const _actionProofStep = (key, label, state, count = 0) => ({
  key,
  label,
  state,
  count: Number(count || 0),
});

const buildLastActionProofSnapshot = ({
  l1DecideTerminalKind,
  l1EgressBranchDispatchN,
  actionDispatchCommandN,
  actionDispatchWorkOrderN,
  actionDispatchWorkOrderAdapterN,
  actionDispatchWorkOrderBlockedN,
  actionDispatchFeedbackN,
  actionDispatchCompletedN,
  actionDispatchFailedN,
  actionDispatchBlockedN,
  actionDispatchCapabilityLeaseBlockedN,
  realityLayerEvidenceN,
  postActionResultSurfaceStatus,
  postActionResultSurfaceSource,
  outputControllerSource,
  lastActionDestination,
  lastAdapterResultPacket,
  lastAdapterFailurePacket,
  flowAccountingL1ActionGapN,
  flowAccountingEgressDispatchGapN,
  flowAccountingFeedbackGapN,
  flowAccountingFeedbackRealityGapN,
}) => {
  const terminalKind = String(l1DecideTerminalKind || '').trim();
  const surfaceStatus = String(postActionResultSurfaceStatus || '').trim();
  const hasActionSignal = (
    terminalKind === 'dispatch_action' ||
    Number(l1EgressBranchDispatchN || 0) > 0 ||
    Number(actionDispatchCommandN || 0) > 0 ||
    Number(actionDispatchWorkOrderN || 0) > 0 ||
    Number(actionDispatchFeedbackN || 0) > 0 ||
    Number(realityLayerEvidenceN || 0) > 0 ||
    surfaceStatus === 'completed'
  );
  if (!hasActionSignal) return null;

  const gapN = (
    Number(flowAccountingL1ActionGapN || 0) +
    Number(flowAccountingEgressDispatchGapN || 0) +
    Number(flowAccountingFeedbackGapN || 0) +
    Number(flowAccountingFeedbackRealityGapN || 0)
  );
  const blockedN = (
    Number(actionDispatchWorkOrderBlockedN || 0) +
    Number(actionDispatchBlockedN || 0) +
    Number(actionDispatchCapabilityLeaseBlockedN || 0)
  );
  const failedN = Number(actionDispatchFailedN || 0);
  const completedN = Number(actionDispatchCompletedN || 0);
  const realityN = Number(realityLayerEvidenceN || 0);
  const adapterN = Number(actionDispatchWorkOrderAdapterN || 0);
  const feedbackN = Number(actionDispatchFeedbackN || 0);
  const workOrderN = Number(actionDispatchWorkOrderN || 0);
  const commandN = Number(actionDispatchCommandN || 0);
  const egressN = Number(l1EgressBranchDispatchN || 0);

  let status = 'running';
  let statusLabel = 'в процессе';
  if (failedN > 0 || gapN > 0) {
    status = 'error';
    statusLabel = failedN > 0 ? 'ошибка' : 'разрыв';
  } else if (blockedN > 0 && completedN <= 0) {
    status = 'blocked';
    statusLabel = 'блок';
  } else if ((completedN > 0 || surfaceStatus === 'completed') && realityN > 0) {
    status = 'completed';
    statusLabel = 'завершено';
  } else if (feedbackN > 0) {
    status = 'feedback';
    statusLabel = 'feedback';
  }

  const adapterState = failedN > 0 || (blockedN > 0 && adapterN <= 0) ? 'error' : (adapterN > 0 ? 'done' : (workOrderN > 0 ? 'active' : 'idle'));
  const feedbackState = failedN > 0 ? 'error' : (completedN > 0 || surfaceStatus === 'completed' ? 'done' : (feedbackN > 0 ? 'active' : 'idle'));
  const realityState = gapN > 0 ? 'error' : (realityN > 0 ? 'done' : (completedN > 0 ? 'active' : 'idle'));

  const detail = [
    String(lastActionDestination || '').trim(),
    String(lastAdapterResultPacket || '').trim(),
    String(lastAdapterFailurePacket || '').trim(),
  ].filter(Boolean).join(' · ');
  const surface = String(postActionResultSurfaceSource || outputControllerSource || '').trim();

  return {
    status,
    statusLabel,
    detail,
    surface,
    commandN,
    workOrderN,
    adapterN,
    feedbackN,
    completedN,
    realityN,
    gapN,
    blockedN,
    failedN,
    steps: [
      _actionProofStep('l1', 'L1', terminalKind === 'dispatch_action' ? 'done' : (commandN > 0 ? 'active' : 'idle'), terminalKind === 'dispatch_action' ? 1 : 0),
      _actionProofStep('egress', 'Egress', egressN > 0 ? 'done' : (commandN > 0 ? 'active' : 'idle'), egressN),
      _actionProofStep('action', 'Action', commandN > 0 ? 'done' : 'idle', commandN),
      _actionProofStep('adapter', 'Adapter', adapterState, adapterN),
      _actionProofStep('feedback', 'Feedback', feedbackState, feedbackN),
      _actionProofStep('reality', 'Reality', realityState, realityN),
    ],
  };
};

const _branchActionStatePreview = (trace) => {
  const ingress = trace?.ingress_shadow && typeof trace.ingress_shadow === 'object' ? trace.ingress_shadow : {};
  const rows = Array.isArray(ingress.branch_action_state_preview) ? ingress.branch_action_state_preview : [];
  return rows.slice(0, 2).map((row) => {
    const src = row && typeof row === 'object' ? row : {};
    const actionId = String(src.action_id || '').trim();
    const controller = String(src.controller || '').trim();
    const status = String(src.status || '').trim();
    const kind = String(src.payload_kind || '').trim();
    const searchOutcome = String(src.search_outcome || '').trim();
    const failureKind = String(src.failure_kind || '').trim();
    const evidenceN = Number(src.search_evidence_n || 0);
    const recencyTicks = Number(src.recency_ticks || 0);
    const parts = [];
    if (actionId) parts.push(actionId);
    if (controller) parts.push(`controller=${controller}`);
    if (status) parts.push(`status=${status}`);
    if (kind) parts.push(`kind=${kind}`);
    if (searchOutcome) parts.push(`search=${searchOutcome}`);
    if (evidenceN > 0) parts.push(`evidence=${evidenceN}`);
    if (failureKind) parts.push(`failure=${failureKind}`);
    if (recencyTicks > 0) parts.push(`age=${recencyTicks}`);
    return parts.join(' ');
  }).filter(Boolean);
};

export const buildLiveTraceSnapshot = (status) => {
  const src = status && typeof status === 'object' ? status : {};
  if (!src.ok) return null;
  const trace = src.last_trace && typeof src.last_trace === 'object' ? src.last_trace : {};
  const runtime = trace._runtime && typeof trace._runtime === 'object' ? trace._runtime : {};
  const lastMeta = src.last_meta && typeof src.last_meta === 'object' ? src.last_meta : {};
  const traceParticipant = trace.participant && typeof trace.participant === 'object' ? trace.participant : {};
  const runtimeParticipant = runtime.participant_window_packet && typeof runtime.participant_window_packet === 'object'
    ? runtime.participant_window_packet
    : {};
  const interlocutorSignal = runtime.interlocutor_signal_packet && typeof runtime.interlocutor_signal_packet === 'object'
    ? runtime.interlocutor_signal_packet
    : (trace.interlocutor_signal_packet && typeof trace.interlocutor_signal_packet === 'object' ? trace.interlocutor_signal_packet : {});
  const interlocutorParticipant = interlocutorSignal.participant && typeof interlocutorSignal.participant === 'object'
    ? interlocutorSignal.participant
    : {};
  const participantId = String(
    runtime.participant_id ||
    trace.participant_id ||
    lastMeta.participant_id ||
    interlocutorSignal.participant_id ||
    runtimeParticipant.id ||
    traceParticipant.id ||
    interlocutorParticipant.id ||
    ''
  ).trim();
  const participantName = String(
    runtime.participant_name ||
    trace.participant_name ||
    lastMeta.participant_name ||
    interlocutorSignal.participant_name ||
    runtimeParticipant.display_name ||
    traceParticipant.display_name ||
    traceParticipant.name ||
    interlocutorParticipant.display_name ||
    interlocutorParticipant.name ||
    ''
  ).trim();
  const flowShadow = trace.flow_shadow && typeof trace.flow_shadow === 'object' ? trace.flow_shadow : {};
  const actionDispatchShadow = (
    flowShadow.action_dispatch_shadow && typeof flowShadow.action_dispatch_shadow === 'object'
      ? flowShadow.action_dispatch_shadow
      : (trace.action_dispatch_shadow && typeof trace.action_dispatch_shadow === 'object' ? trace.action_dispatch_shadow : {})
  );
  const actionDispatchValidation = actionDispatchShadow.validation && typeof actionDispatchShadow.validation === 'object'
    ? actionDispatchShadow.validation
    : {};
  const capabilityRegistry = actionDispatchValidation.capability_registry && typeof actionDispatchValidation.capability_registry === 'object'
    ? actionDispatchValidation.capability_registry
    : {};
  const capabilityStatusCounts = capabilityRegistry.status_counts && typeof capabilityRegistry.status_counts === 'object'
    ? capabilityRegistry.status_counts
    : {};
  const actionBatch = actionDispatchShadow.batch && typeof actionDispatchShadow.batch === 'object'
    ? actionDispatchShadow.batch
    : {};
  const actionCommands = Array.isArray(actionBatch.commands) ? actionBatch.commands.filter((item) => item && typeof item === 'object') : [];
  const actionFeedbackRows = Array.isArray(actionDispatchShadow.feedback_packets)
    ? actionDispatchShadow.feedback_packets.filter((item) => item && typeof item === 'object')
    : [];
  const lastActionCommand = _lastArrayItem(actionCommands);
  const lastActionFeedback = _lastArrayItem(actionFeedbackRows);
  const lastActionResultPacket = lastActionFeedback.result_packet && typeof lastActionFeedback.result_packet === 'object'
    ? lastActionFeedback.result_packet
    : {};
  const lastActionFailurePacket = lastActionFeedback.failure_packet && typeof lastActionFeedback.failure_packet === 'object'
    ? lastActionFeedback.failure_packet
    : {};
  const l1EgressPackage = flowShadow.l1_egress_package && typeof flowShadow.l1_egress_package === 'object'
    ? flowShadow.l1_egress_package
    : (trace.l1_egress_package && typeof trace.l1_egress_package === 'object' ? trace.l1_egress_package : {});
  const nextOcmHandoff = l1EgressPackage.next_ocm_handoff && typeof l1EgressPackage.next_ocm_handoff === 'object'
    ? l1EgressPackage.next_ocm_handoff
    : {};
  const routeState = trace.route_state && typeof trace.route_state === 'object' ? trace.route_state : {};
  const routeShadow = trace.route_authority_shadow && typeof trace.route_authority_shadow === 'object'
    ? trace.route_authority_shadow
    : {};
  const selected = routeShadow.selected && typeof routeShadow.selected === 'object' ? routeShadow.selected : {};
  const tickActive = !!(src.tick_in_progress || src.tick_lock_held);
  const routeFamily = String(routeState.route_family || runtime.route_family || '').trim();
  const toolBranch = String(runtime.tool_preflight_branch || '').trim();
  const toolDecision = String(runtime.tool_preflight_decision || trace.tool_preflight?.decision || '').trim();
  const turnType = String(trace.dialogue_turn?.turn_type || trace.turn_type || '').trim();
  const owner = String(src.owner || src.tick_owner || lastMeta.source || runtime.source || '').trim();
  const tickStagePacket = src.tick_stage_packet && typeof src.tick_stage_packet === 'object'
    ? src.tick_stage_packet
    : {};
  const tickStageCurrent = tickStagePacket.current && typeof tickStagePacket.current === 'object'
    ? tickStagePacket.current
    : {};
  const tickStageDisplay = tickStagePacket.display && typeof tickStagePacket.display === 'object'
    ? tickStagePacket.display
    : {};
  const stage = String(tickStagePacket.stage_name || src.tick_stage || src.last_tick_stage || src.stage || '').trim();
  const runtimeStageHeadline = String(tickStageDisplay.headline || tickStageCurrent.headline || tickStagePacket.child_stage_last || '').trim();
  const runtimeStageDetail = String(tickStageDisplay.detail || tickStageCurrent.detail || '').trim();
  const runtimeStageContour = String(tickStageCurrent.contour || '').trim();
  const runtimeStageRole = String(tickStageCurrent.role || '').trim();
  const runtimeStageProcess = String(tickStageCurrent.process || '').trim();
  const runtimeStageAttempt = String(tickStageCurrent.attempt || '').trim();
  const runtimeStageRaw = String(tickStageCurrent.raw_stage || tickStagePacket.child_stage_last || '').trim();
  const runtimeStageElapsedS = Number(tickStagePacket.elapsed_s || 0);
  const runtimeStageTimeoutS = Number(tickStageCurrent.timeout_s || 0);
  const runtimeStagePacketVersion = String(tickStagePacket.packet_version || '').trim();
  const rawRuntimeStagePacketStatus = String(tickStagePacket.status || '').trim();
  const rawRuntimeStageLastError = String(tickStagePacket.last_tick_error || src.last_tick_error || '').trim();
  const missingContinuationHold = isMissingContinuationHold(rawRuntimeStageLastError) && !tickActive;
  const runtimeStagePacketStatus = missingContinuationHold ? 'idle' : rawRuntimeStagePacketStatus;
  const runtimeStageLastError = missingContinuationHold ? '' : rawRuntimeStageLastError;
  const runtimePhaseKey = inferOcmPhaseKey({
    tickActive,
    packetStatus: runtimeStagePacketStatus,
    rawStage: runtimeStageRaw || stage,
    contour: runtimeStageContour,
    process: runtimeStageProcess,
    role: runtimeStageRole,
    headline: runtimeStageHeadline,
    detail: runtimeStageDetail,
    lastTickStage: tickStagePacket.last_tick_stage || src.last_tick_stage,
    lastTickError: runtimeStageLastError,
    runtime,
  });
  const runtimePhase = buildOcmPhaseSnapshot({
    tickActive,
    packetStatus: runtimeStagePacketStatus,
    phaseKey: runtimePhaseKey,
    headline: runtimeStageHeadline,
    detail: runtimeStageDetail,
    elapsedS: runtimeStageElapsedS,
    timeoutS: runtimeStageTimeoutS,
    lastTickError: runtimeStageLastError,
  });
  const mode = String(lastMeta.mode || runtime.mode || '').trim();
  const selectedSource = String(selected.source || '').trim();
  const query = _trimPreview(runtime.query || lastMeta.query || '');
  const reply = isAwaitUserMaintenanceHoldTrace(trace) ? '' : _trimPreview(trace.reply_user || trace.reply_text || '');
  const question = _trimPreview(runtime.tool_preflight_question || _pickTraceQuestion(trace) || '');
  const ingressBranchActionStatePreview = _branchActionStatePreview(trace);
  const ingressCandidates = Number(runtime.ingress_shadow_candidates_n || 0);
  const ingressBranchActionStateN = Number(runtime.ingress_shadow_branch_action_state_n || 0);
  const flowCount = Number(runtime.flow_shadow_flow_count || 0);
  const activeFlowKind = String(runtime.flow_shadow_active_flow_kind || '').trim();
  const flowAccountingVersion = String(runtime.ocm_flow_accounting_version || '').trim();
  const flowAccountingDeliveredN = Number(runtime.ocm_flow_accounting_delivered_to_l1_flow_n || 0);
  const flowAccountingObligationN = Number(runtime.ocm_flow_accounting_obligation_n || 0);
  const flowAccountingNotDeliveredN = Number(runtime.ocm_flow_accounting_not_delivered_flow_n || 0);
  const flowAccountingOrphanCandidateN = Number(runtime.ocm_flow_accounting_orphan_candidate_n || 0);
  const flowAccountingL1ActionGapN = Number(runtime.ocm_flow_accounting_l1_action_to_egress_gap_n || 0);
  const flowAccountingEgressDispatchGapN = Number(runtime.ocm_flow_accounting_egress_to_dispatch_gap_n || 0);
  const flowAccountingFeedbackGapN = Number(runtime.ocm_flow_accounting_dispatch_feedback_gap_n || 0);
  const flowAccountingFeedbackRealityGapN = Number(runtime.ocm_flow_accounting_feedback_to_reality_gap_n || 0);
  const attentionGateContract = String(runtime.flow_shadow_attention_appraisal_role_contract || '').trim();
  const attentionGateDeliveryN = Number(runtime.flow_shadow_attention_appraisal_delivery_decision_n || 0);
  const attentionGateBindingWarningN = Number(runtime.flow_shadow_attention_appraisal_binding_warning_n || 0);
  const l1Packet = trace.l1_lane_controller && typeof trace.l1_lane_controller === 'object'
    ? trace.l1_lane_controller
    : {};
  const l1Reader = trace.l1_lane_reader && typeof trace.l1_lane_reader === 'object'
    ? trace.l1_lane_reader
    : {};
  const l1ReaderProjection = l1Reader.projection && typeof l1Reader.projection === 'object'
    ? l1Reader.projection
    : {};
  const l1ReaderSource = String(l1ReaderProjection.role_query_source || '').trim();
  const l1ReaderElided = !!l1ReaderProjection.raw_user_signal_elided;
  const l1ActiveObject = String(runtime.l1_lane_active_object_id || l1Packet.active_object_id || '').trim();
  const l1ReplySource = String(runtime.l1_lane_reply_source || l1Packet.egress?.reply_source || '').trim();
  const l1TerminalPacket = String(runtime.l1_lane_terminal_tool_packet_type || l1Packet.terminal_tool?.packet_type || '').trim();
  const l1CloseStatus = String(runtime.l1_lane_close_status || l1Packet.closure?.status || '').trim();
  const l1MemorySuppressed = Number(runtime.l1_lane_memory_suppressed_n || l1Packet.egress?.memory_write_suppressed_n || 0);
  const l1EgressVersion = String(runtime.l1_egress_package_version || '').trim();
  const l1EgressIntegrityVersion = String(runtime.l1_egress_integrity_audit_version || '').trim();
  const l1EgressIntegrityValid = runtime.l1_egress_integrity_valid === undefined
    ? null
    : !!runtime.l1_egress_integrity_valid;
  const l1EgressIntegrityGapN = Number(runtime.l1_egress_integrity_gap_n || 0);
  const l1EgressIntegrityRequiredGapN = Number(runtime.l1_egress_integrity_required_l1_decision_gap_n || 0);
  const l1EgressFlowUpdateN = Number(runtime.l1_egress_flow_update_n || 0);
  const l1EgressSuppressedN = Number(runtime.l1_egress_suppressed_n || 0);
  const l1EgressNextOcm = !!runtime.l1_egress_next_ocm_handoff;
  const l1EgressDecisionKind = String(runtime.l1_egress_decision_kind || '').trim();
  const l1BranchOutcomeDecisionKind = String(runtime.l1_branch_outcome_decision_kind || '').trim();
  const l1DecideTerminalKind = String(runtime.l1_decide_contract_terminal_kind || '').trim();
  const l1EgressBranchDispatchN = Number(runtime.l1_egress_branch_dispatch_n || 0);
  const l1ActionCommandN = Number(runtime.l1_action_command_n || 0);
  const l1DirectNoopRepair = !!runtime.l1_command_direct_action_noop_repair;
  const l1DirectNoopReason = String(runtime.l1_command_direct_action_noop_reason || '').trim();
  const actionDispatchVersion = String(runtime.action_dispatch_shadow_version || '').trim();
  const actionDispatchCommandN = Number(runtime.action_dispatch_command_n || 0);
  const actionDispatchWorkOrderN = Number(runtime.action_dispatch_work_order_n || 0);
  const actionDispatchWorkOrderAdapterN = Number(runtime.action_dispatch_work_order_adapter_execution_n || 0);
  const actionDispatchWorkOrderShadowN = Number(runtime.action_dispatch_work_order_shadow_n || 0);
  const actionDispatchWorkOrderBlockedN = Number(runtime.action_dispatch_work_order_blocked_n || 0);
  const actionDispatchDeliveryOk = !!runtime.action_dispatch_delivery_ok;
  const actionDispatchFeedbackN = Number(runtime.action_dispatch_feedback_n || 0);
  const actionDispatchQueuedN = Number(runtime.action_dispatch_feedback_queued_n || 0);
  const actionDispatchCompletedN = Number(runtime.action_dispatch_feedback_completed_n || 0);
  const actionDispatchFailedN = Number(runtime.action_dispatch_feedback_failed_n || 0);
  const actionDispatchSearchExecutedN = Number(runtime.action_dispatch_search_executed_n || 0);
  const actionDispatchBlockedN = Number(runtime.action_dispatch_blocked_n || 0);
  const actionDispatchCapabilityLeaseN = Number(runtime.action_dispatch_capability_lease_n || 0);
  const actionDispatchCapabilityLeaseBlockedN = Number(runtime.action_dispatch_capability_lease_blocked_n || 0);
  const actionDispatchCapabilityLeaseReleasedN = Number(runtime.action_dispatch_capability_lease_released_n || 0);
  const capabilityRegistryKnownN = Number(runtime.capability_registry_known_n || capabilityRegistry.capability_n || 0);
  const capabilityRegistryAvailableN = Number(runtime.capability_registry_available_n || capabilityRegistry.available_n || 0);
  const capabilityRegistryShadowN = Number(runtime.capability_registry_shadow_n || capabilityRegistry.shadow_n || 0);
  const capabilityRegistryBusyN = Number(runtime.capability_registry_busy_n || capabilityRegistry.busy_n || 0);
  const capabilityRegistryBlockedN = Number(runtime.capability_registry_blocked_n || capabilityRegistry.blocked_n || 0);
  const capabilityRegistryOfflineN = Number(capabilityStatusCounts.offline || 0);
  const lastActionBranch = String(lastActionCommand.branch || '').trim();
  const lastActionCapability = String(lastActionCommand.capability || lastActionResultPacket.capability_id || '').trim();
  const lastActionTaskKind = String(lastActionCommand.task_kind || lastActionResultPacket.task_kind || '').trim();
  const lastActionDestination = [lastActionBranch, lastActionCapability, lastActionTaskKind].filter(Boolean).join('/');
  const lastAdapterResultPacket = _packetVersion(lastActionResultPacket);
  const lastAdapterFailurePacket = _packetVersion(lastActionFailurePacket) || String(lastActionFailurePacket.failure_kind || '').trim();
  const realityLayerEvidenceN = Number(runtime.reality_layer_evidence_n || 0);
  const postActionResultSurfaceStatus = String(runtime.post_action_result_surface_status || '').trim();
  const postActionResultSurfaceSource = String(runtime.post_action_result_surface_source || '').trim();
  const outputControllerSource = String(runtime.output_controller_source || '').trim();
  const lastActionProof = buildLastActionProofSnapshot({
    l1DecideTerminalKind,
    l1EgressBranchDispatchN,
    actionDispatchCommandN,
    actionDispatchWorkOrderN,
    actionDispatchWorkOrderAdapterN,
    actionDispatchWorkOrderBlockedN,
    actionDispatchFeedbackN,
    actionDispatchCompletedN,
    actionDispatchFailedN,
    actionDispatchBlockedN,
    actionDispatchCapabilityLeaseBlockedN,
    realityLayerEvidenceN,
    postActionResultSurfaceStatus,
    postActionResultSurfaceSource,
    outputControllerSource,
    lastActionDestination,
    lastAdapterResultPacket,
    lastAdapterFailurePacket,
    flowAccountingL1ActionGapN,
    flowAccountingEgressDispatchGapN,
    flowAccountingFeedbackGapN,
    flowAccountingFeedbackRealityGapN,
  });
  const nextOcmPosture = String(runtime.l1_effective_next_ocm_command_kind || nextOcmHandoff.command_kind || '').trim();
  const actionLadderCompleteN = Number(runtime.action_ladder_complete_to_reality_n || 0);
  const actionLadderReadyForIngressN = Number(runtime.action_ladder_ready_for_next_ingress_n || 0);
  const l4TailLifecycleCompleteAckN = Number(runtime.l4_tail_lifecycle_complete_ack_n || 0);
  const l4TailLifecycleReturnIngressN = Number(runtime.l4_tail_lifecycle_return_ingress_n || 0);
  const l4TailLifecycleReturnAckGapN = Number(runtime.l4_tail_lifecycle_return_ack_gap_n || 0);
  const memoryControllerVersion = String(runtime.memory_controller_shadow_version || '').trim();
  const memoryControllerAcceptedN = Number(runtime.memory_controller_accepted_n || 0);
  const memoryControllerRejectedN = Number(runtime.memory_controller_rejected_n || 0);
  const memoryControllerRetrievalN = Number(runtime.memory_controller_retrieval_request_n || 0);
  const searchControllerVersion = String(runtime.search_controller_packet_version || '').trim();
  const searchControllerOutcome = String(runtime.search_controller_outcome || '').trim();
  const searchControllerFailureKind = String(runtime.search_controller_failure_kind || '').trim();
  const searchControllerEvidenceN = Number(runtime.search_controller_evidence_n || 0);
  const emergencyControllerVersion = String(runtime.emergency_controller_version || '').trim();
  const emergencyBranchFailureN = Number(runtime.emergency_branch_failure_n || 0);
  const emergencySignalN = Number(runtime.emergency_signal_n || 0);
  const emergencyHaltRecommended = !!runtime.emergency_halt_recommended;
  const branchActionStateVersion = String(runtime.branch_action_state_patch_version || '').trim();
  const branchActionStateActionN = Number(runtime.branch_action_state_action_n || 0);
  const branchActionStateQueuedN = Number(runtime.branch_action_state_queued_n || 0);
  const branchActionStateCompletedN = Number(runtime.branch_action_state_completed_n || 0);
  const branchActionStateBlockedN = Number(runtime.branch_action_state_blocked_n || 0);
  const branchActionStateFailureN = Number(runtime.branch_action_state_failure_n || 0);
  const branchActionStateOutcomeDigestN = Number(runtime.branch_action_state_outcome_digest_n || 0);
  const branchActionStateZombieRiskN = Number(runtime.branch_action_state_zombie_risk_n || 0);
  const awaiting = !!(
    trace.awaiting_user_answer ||
    trace.user_interaction?.awaiting_user_answer ||
    runtime.awaiting_user_answer
  );
  if (!(tickActive || runtimeStagePacketVersion || routeFamily || toolBranch || toolDecision || turnType || query || reply || question || l1ActiveObject || l1ReplySource || l1ReaderSource || flowAccountingVersion || l1EgressIntegrityVersion || actionLadderCompleteN || l4TailLifecycleReturnIngressN || capabilityRegistryKnownN || lastActionDestination || lastActionProof || nextOcmPosture)) {
    return null;
  }
  const headline = tickActive
    ? `Сейчас: ${runtimeStageHeadline || stage || 'tick'}`
    : `Последний шаг: ${routeFamily || turnType || stage || 'idle'}`;
  return {
    tickActive,
    headline,
    stage: stage || (tickActive ? 'started' : ''),
    runtimeStagePacketVersion,
    runtimeStageHeadline,
    runtimeStageDetail,
    runtimeStageContour,
    runtimeStageRole,
    runtimeStageProcess,
    runtimeStageAttempt,
    runtimeStageRaw,
    runtimeStageElapsedS,
    runtimeStageTimeoutS,
    owner,
    runtimeStagePacketStatus,
    runtimeStageLastError,
    runtimePhase,
    participantId,
    participantName,
    mode,
    routeFamily,
    selectedSource,
    toolBranch,
    toolDecision,
    turnType,
    query,
    reply,
    question,
    ingressCandidates,
    ingressBranchActionStateN,
    ingressBranchActionStatePreview,
    flowCount,
    activeFlowKind,
    flowAccountingVersion,
    flowAccountingDeliveredN,
    flowAccountingObligationN,
    flowAccountingNotDeliveredN,
    flowAccountingOrphanCandidateN,
    flowAccountingL1ActionGapN,
    flowAccountingEgressDispatchGapN,
    flowAccountingFeedbackGapN,
    flowAccountingFeedbackRealityGapN,
    attentionGateContract,
    attentionGateDeliveryN,
    attentionGateBindingWarningN,
    l1ReaderSource,
    l1ReaderElided,
    l1ActiveObject,
    l1ReplySource,
    l1TerminalPacket,
    l1CloseStatus,
    l1MemorySuppressed,
    l1EgressVersion,
    l1EgressIntegrityVersion,
    l1EgressIntegrityValid,
    l1EgressIntegrityGapN,
    l1EgressIntegrityRequiredGapN,
    l1EgressFlowUpdateN,
    l1EgressSuppressedN,
    l1EgressNextOcm,
    l1EgressDecisionKind,
    l1BranchOutcomeDecisionKind,
    l1DecideTerminalKind,
    l1EgressBranchDispatchN,
    l1ActionCommandN,
    l1DirectNoopRepair,
    l1DirectNoopReason,
    actionDispatchVersion,
    actionDispatchCommandN,
    actionDispatchWorkOrderN,
    actionDispatchWorkOrderAdapterN,
    actionDispatchWorkOrderShadowN,
    actionDispatchWorkOrderBlockedN,
    actionDispatchDeliveryOk,
    actionDispatchFeedbackN,
    actionDispatchQueuedN,
    actionDispatchCompletedN,
    actionDispatchFailedN,
    actionDispatchSearchExecutedN,
    actionDispatchBlockedN,
    actionDispatchCapabilityLeaseN,
    actionDispatchCapabilityLeaseBlockedN,
    actionDispatchCapabilityLeaseReleasedN,
    capabilityRegistryKnownN,
    capabilityRegistryAvailableN,
    capabilityRegistryShadowN,
    capabilityRegistryBusyN,
    capabilityRegistryBlockedN,
    capabilityRegistryOfflineN,
    lastActionDestination,
    lastAdapterResultPacket,
    lastAdapterFailurePacket,
    realityLayerEvidenceN,
    postActionResultSurfaceStatus,
    postActionResultSurfaceSource,
    outputControllerSource,
    lastActionProof,
    nextOcmPosture,
    actionLadderCompleteN,
    actionLadderReadyForIngressN,
    l4TailLifecycleCompleteAckN,
    l4TailLifecycleReturnIngressN,
    l4TailLifecycleReturnAckGapN,
    memoryControllerVersion,
    memoryControllerAcceptedN,
    memoryControllerRejectedN,
    memoryControllerRetrievalN,
    searchControllerVersion,
    searchControllerOutcome,
    searchControllerFailureKind,
    searchControllerEvidenceN,
    emergencyControllerVersion,
    emergencyBranchFailureN,
    emergencySignalN,
    emergencyHaltRecommended,
    branchActionStateVersion,
    branchActionStateActionN,
    branchActionStateQueuedN,
    branchActionStateCompletedN,
    branchActionStateBlockedN,
    branchActionStateFailureN,
    branchActionStateOutcomeDigestN,
    branchActionStateZombieRiskN,
    awaiting,
  };
};

const isMeaningfulTraceRuntimeOutput = (trace) => {
  const src = trace && typeof trace === 'object' ? trace : {};
  if (isAwaitUserMaintenanceHoldTrace(src)) return false;
  if (String(src.reply_user || '').trim()) return true;
  if (String(src.reply_text || '').trim()) return true;
  if (Array.isArray(src.questions_to_user) && src.questions_to_user.some((q) => String(q || '').trim())) return true;
  if (Array.isArray(src.question_items) && src.question_items.some((it) => String(it?.text || '').trim())) return true;
  if (src.tool_preflight && typeof src.tool_preflight === 'object') {
    const decision = String(src.tool_preflight.decision || '').trim();
    if (decision && decision !== 'no_tool_needed') return true;
  }
  const runtime = src._runtime && typeof src._runtime === 'object' ? src._runtime : {};
  const awaiting = !!(
    src.awaiting_user_answer ||
    src?._runtime?.awaiting_user_answer ||
    src?.user_interaction?.awaiting_user_answer
  );
  if (awaiting && String(runtime.tool_preflight_question || '').trim()) return true;
  const branch = String(runtime.tool_preflight_branch || '').trim().toLowerCase();
  if (branch && !['none', 'idle', 'no_tool_needed'].includes(branch)) return true;
  return false;
};

export const isStatusDialogueFallbackTrace = (trace) => {
  const rt = trace && typeof trace === 'object' && trace._runtime && typeof trace._runtime === 'object'
    ? trace._runtime
    : {};
  return (
    String(rt.source || '').trim().toLowerCase() === 'status' &&
    String(rt.trace_fallback || '').trim().toLowerCase() === 'dialogue_history_temp'
  );
};

const buildInspectorSections = (trace) => {
  const src = trace && typeof trace === 'object' ? trace : {};
  const out = [];
  if (src.tool_preflight) out.push({ title: 'Tool Preflight', payload: src.tool_preflight });
  if (src.l1_lane_reader) out.push({ title: 'L1 Lane Reader', payload: src.l1_lane_reader });
  if (src.l1_lane_controller) out.push({ title: 'L1 Lane Controller', payload: src.l1_lane_controller });
  if (src.ingress_shadow) out.push({ title: 'Ingress Shadow', payload: src.ingress_shadow });
  if (src.flow_shadow) out.push({ title: 'Flow Shadow', payload: src.flow_shadow });
  if (src.suture) out.push({ title: 'SUTURE', payload: src.suture });
  if (src.rivet) out.push({ title: 'RIVET', payload: src.rivet });
  if (src.ocm_outcome_handoff) out.push({ title: 'OHB', payload: src.ocm_outcome_handoff });
  if (src.continuation_diff) out.push({ title: 'Continuation Diff', payload: src.continuation_diff });
  return out;
};

const App = () => {
  const initialRuntimeMonitorCache = loadRuntimeMonitorCache();
  const [view, setView] = useState('chat');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const initialThreads = loadThreads();
  const [threads, setThreads] = useState(initialThreads.threads);
  const [activeThreadId, setActiveThreadId] = useState(initialThreads.activeThreadId);
  const [autoscroll, setAutoscroll] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [username, setUsername] = useState(getInitialNick());
  const [authToken, setAuthToken] = useState(() => localStorage.getItem(AUTH_TOKEN_KEY) || '');
  const [authAccount, setAuthAccount] = useState(null);
  const [authBootstrapRequired, setAuthBootstrapRequired] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [managedAccounts, setManagedAccounts] = useState([]);
  const [askNameOpen, setAskNameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [selectedModel, setSelectedModel] = useState(FALLBACK_MODELS[0]);
  const [modelsList, setModelsList] = useState(FALLBACK_MODELS);
  const [activeEngineModel, setActiveEngineModel] = useState('');
  const [lastTickModel, setLastTickModel] = useState('');
  const [engineSwitching, setEngineSwitching] = useState(false);
  const [statusServer, setStatusServer] = useState(true);
  const [statusDetail, setStatusDetail] = useState('ok');
  const [loading, setLoading] = useState(false);
  const [tickBusy, setTickBusy] = useState(false);
  const [taskStats, setTaskStats] = useState({ pending: null });
  const [taskList, setTaskList] = useState([]);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskQuick, setTaskQuick] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [lastEvidence, setLastEvidence] = useState([]);
  const [debugPrompt, setDebugPrompt] = useState('');
  const [lastRunnerStatus, setLastRunnerStatus] = useState(null);
  const [liveTraceStatus, setLiveTraceStatus] = useState(null);
  const [liveTraceForMonitor, setLiveTraceForMonitor] = useState(() => initialRuntimeMonitorCache?.last_trace || null);
  const [uiDialogOpen, setUiDialogOpen] = useState(false);
  const [uiQueueThreadId, setUiQueueThreadId] = useState('');
  const [uiPendingItems, setUiPendingItems] = useState([]);
  const [uiAnswerDraftById, setUiAnswerDraftById] = useState({});
  const [uiActionBusy, setUiActionBusy] = useState(false);
  const [manualTaskPendingByThread, setManualTaskPendingByThread] = useState({});
  const [traceDrawerOpen, setTraceDrawerOpen] = useState(false);
  const [runtimeMonitorTab, setRuntimeMonitorTab] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('roadtest') === '1' ? 'sequence' : 'flows';
    } catch {
      return 'flows';
    }
  });

  const [runnerMode, setRunnerMode] = useState('manual');
  const [runnerInterval, setRunnerInterval] = useState(0);
  const [paused, setPaused] = useState(false);
  const [runnerMaxCycles, setRunnerMaxCycles] = useState(null);
  const [runnerCycleCount, setRunnerCycleCount] = useState(0);
  const [runnerSettingsHydrated, setRunnerSettingsHydrated] = useState(false);
  const [runnerSequenceStatus, setRunnerSequenceStatus] = useState(() => initialRuntimeMonitorCache?.runner_sequence_ocm || null);
  const [roadTestSeed, setRoadTestSeed] = useState('');
  const [roadTestSteps, setRoadTestSteps] = useState(6);
  const [roadTestInterval, setRoadTestInterval] = useState(0);
  const [filterDuplicates, setFilterDuplicates] = useState(true);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const tickBusyRef = useRef(false);
  const autoTickInFlightRef = useRef(false);
  const statusBusyRef = useRef(false);
  const lastTickFinishedRef = useRef(null);
  const lastTraceKeyRef = useRef('');
  const lastRuntimeTraceSignatureRef = useRef('');
  const lastKpiKeyRef = useRef('');
  const lastFullMonitorStatusAtRef = useRef(0);
  const lastFullMonitorStatusRef = useRef(null);
  const prevRunnerModeRef = useRef('manual');
  const lastRunnerStatusRef = useRef(null);
  const runnerCycleCountRef = useRef(0);
  const lastAutoDuplicateNoticeAtRef = useRef(0);
  const postChatCooldownUntilRef = useRef(0);
  const autoAwaitingContinuationRef = useRef({});
  const threadsRef = useRef(initialThreads.threads);
  const ocmSeqRef = useRef({ byThread: {} });
  const activeThreadIdRef = useRef(initialThreads.activeThreadId);
  const recoveredChatPendingRef = useRef({});

  const activeThread = threads.find(t => t.id === activeThreadId) || threads[0];
  const history = activeThread?.history || [];
  const activeParticipant = buildThreadParticipant(activeThread, username);
  const traceDebugEnabled = isTraceDebugEnabled();
  const activeManualTaskPending = manualTaskPendingByThread?.[activeThreadId] || null;
  const normalThreads = threads.filter((t) => t.id !== RUNTIME_THREAD_ID);
  const latestAssistantEntry = [...history].reverse().find((msg) => msg && msg.role === 'llm' && msg.trace);
  const latestTrace = latestAssistantEntry?.trace || null;
  const monitorTrace = liveTraceForMonitor && !isStatusDialogueFallbackTrace(liveTraceForMonitor)
    ? liveTraceForMonitor
    : latestTrace;
  const latestOperatorDigest = normalizeOperatorDigest(monitorTrace);
  const latestRuntimeMonitor = latestOperatorDigest.runtimeMonitor || null;
  const latestRuntimeMonitorFlows = normalizeMonitorRows(latestRuntimeMonitor?.flows, 16);
  const latestRuntimeMonitorTabs = normalizeMonitorRows(latestRuntimeMonitor?.tabs, 6);
  const runnerSequenceSteps = normalizeRunnerSequenceSteps(runnerSequenceStatus);
  const runnerSequenceCompletedSteps = Number(runnerSequenceStatus?.completed_steps || 0);
  const runnerSequenceMaxSteps = Number(runnerSequenceStatus?.max_steps || 0);
  const runnerSequenceCurrentStep = Number.isFinite(Number(runnerSequenceStatus?.current_step_index))
    ? Number(runnerSequenceStatus.current_step_index) + 1
    : runnerSequenceCompletedSteps;
  const runnerSequenceVisible = !!(
    runnerSequenceStatus?.run_id ||
    runnerSequenceStatus?.status ||
    runnerSequenceSteps.length > 0
  );
  const baseRuntimeMonitorTabs = latestRuntimeMonitorTabs.length > 0
    ? latestRuntimeMonitorTabs
    : [
      { id: 'flows', label: 'Потоки', count: latestRuntimeMonitorFlows.length },
      { id: 'chain', label: 'Цепочка', count: latestOperatorDigest.chain.length },
      { id: 'status', label: 'Статус', count: latestOperatorDigest.contourStatus.length },
      { id: 'gaps', label: 'Разрывы', count: latestOperatorDigest.openGaps.length },
    ];
  const visibleRuntimeMonitorTabs = uniqueMonitorRows([
    ...baseRuntimeMonitorTabs,
    { id: 'sequence', label: 'Road Test / OCM run', count: runnerSequenceSteps.length },
  ], ['id']);
  const activeRuntimeMonitorTab = visibleRuntimeMonitorTabs.some((tab) => String(tab.id || '').trim() === runtimeMonitorTab)
    ? runtimeMonitorTab
    : String(latestRuntimeMonitor?.active_tab || 'flows');
  const monitorRuntime = monitorTrace?._runtime || {};
  const monitorThreadId = String(
    monitorTrace?.thread_id ||
    monitorRuntime.participant_window_thread_id ||
    latestRuntimeMonitor?.thread_id ||
    ''
  ).trim();
  const monitorParticipantName = String(
    monitorRuntime.participant_name ||
    monitorRuntime.participant_window_packet?.display_name ||
    ''
  ).trim();
  const monitorL2AdvisoryN = Number(
    latestRuntimeMonitor?.current?.l2_advisory_n ??
    monitorRuntime.l2_flow_advisory_n ??
    monitorRuntime.l2_tactical_advisory_n ??
    0
  );
  const monitorL2ShadowN = Number(
    latestRuntimeMonitor?.current?.l2_llm_shadow_advisory_n ??
    monitorRuntime.l2_flow_advisory_llm_shadow_n ??
    monitorRuntime.l2_tactical_advisory_n ??
    0
  );
  const monitorL2PromptAttached = !!monitorRuntime.l2_tactical_prompt_attached;
  const monitorL2RolePresent = !!(
    monitorRuntime.l2_tactical_role_present ||
    monitorRuntime.l2_flow_advisory_version ||
    monitorL2AdvisoryN > 0 ||
    monitorL2ShadowN > 0
  );
  const monitorL2Source = String(
    latestRuntimeMonitor?.current?.l2_advisory_source ||
    monitorRuntime.l2_flow_advisory_source ||
    monitorRuntime.l2_tactical_prompt_version ||
    ''
  ).trim();
  const monitorL2Tone = monitorL2AdvisoryN > 0
    ? 'active'
    : (monitorL2RolePresent || monitorL2PromptAttached ? 'ready' : 'idle');
  const monitorL2Label = monitorL2Tone === 'active'
    ? 'active'
    : (monitorL2Tone === 'ready' ? 'ready' : 'idle');
  const monitorRetainedRegistryN = monitorRuntime?.ocm_registry_n;
  const monitorL1ContractRequiredText = monitorRuntime?.l1_action_contract_required !== undefined
    ? String(monitorRuntime.l1_action_contract_required)
    : '';
  const monitorL1ContractValidText = monitorRuntime?.l1_action_contract_valid !== undefined
    ? String(monitorRuntime.l1_action_contract_valid)
    : '';
  const monitorL1ContractGapN = Number(
    monitorRuntime?.l1_action_contract_gap_n ??
    monitorRuntime?.l1_action_contract_gap_total ??
    0
  );
  const monitorL1ContractDecision = String(monitorRuntime?.l1_action_contract_decision || '').trim();
  const monitorL1ContractVisible = !!(
    monitorL1ContractRequiredText ||
    monitorL1ContractValidText ||
    monitorL1ContractGapN > 0 ||
    monitorL1ContractDecision
  );
  const latestRuntime = latestTrace?._runtime || {};
  const latestContinuityOwner = String(
    latestRuntime.query_final_source ||
    latestRuntime.query_source ||
    'input'
  ).trim() || 'input';
  const latestContinuityMove = String(
    latestRuntime.rivet_task_seed ||
    latestRuntime.suture_dialogue_move_candidate ||
    latestRuntime.ohb_dialogue_move_candidate ||
    ''
  ).trim();
  const latestContinuityReason = String(
    latestRuntime.rivet_task_reason ||
    latestRuntime.suture_reason_code ||
    latestRuntime.ohb_reason_code ||
    ''
  ).trim();
  const latestWinner = String(
    latestRuntime.reply_authority_semantic_source ||
    ''
  ).trim();
  const latestToolBranch = String(
    latestRuntime.tool_preflight_branch ||
    ''
  ).trim() || 'none';
  const latestToolDecision = String(
    latestRuntime.tool_preflight_decision ||
    latestTrace?.tool_preflight?.decision ||
    ''
  ).trim();
  const latestToolQuestion = String(
    latestRuntime.tool_preflight_question ||
    ''
  ).trim();
  const latestToolTone = inferToolBranchTone(latestToolBranch);
  const latestToolLabel = describeToolBranch(latestToolBranch);
  const latestOwnerTone = inferOwnerTone(latestContinuityOwner);
  const latestOwnerLabel = describeOwnerTone(latestOwnerTone);
  const latestL1LaneObject = String(latestRuntime.l1_lane_active_object_id || latestTrace?.l1_lane_controller?.active_object_id || '').trim();
  const latestL1LaneSource = String(latestRuntime.l1_lane_reply_source || latestTrace?.l1_lane_controller?.egress?.reply_source || 'idle').trim();
  const latestL1LaneTerminal = String(latestRuntime.l1_lane_terminal_tool_packet_type || latestTrace?.l1_lane_controller?.terminal_tool?.packet_type || '').trim();
  const latestL1LaneClose = String(latestRuntime.l1_lane_close_status || latestTrace?.l1_lane_controller?.closure?.status || '').trim();
  const latestL1ReaderSource = String(latestTrace?.l1_lane_reader?.projection?.role_query_source || latestRuntime.role_query_source || '').trim();
  const latestL1ReaderElided = !!(latestTrace?.l1_lane_reader?.projection?.raw_user_signal_elided || latestRuntime.role_raw_user_signal_elided);
  const semanticTimeline = [...history]
    .filter((msg) => msg && msg.role === 'llm' && msg.trace)
    .slice(-4)
    .reverse()
    .map((msg, idx) => {
      const rt = msg.trace?._runtime || {};
      const move = String(
        rt.rivet_task_seed ||
        rt.suture_dialogue_move_candidate ||
        rt.ohb_dialogue_move_candidate ||
        msg.trace?.reply_user ||
        msg.text ||
        ''
      ).trim();
      const owner = String(
        rt.query_final_source ||
        rt.query_source ||
        'input'
      ).trim() || 'input';
      return {
        id: `${msg.timestamp || idx}_${idx}`,
        move: move.slice(0, 180),
        owner,
        tone: inferOwnerTone(owner),
      };
    })
    .filter((item) => item.move);
  const sutureMeta = latestTrace?.suture?.meta || {};
  const rivetMeta = latestTrace?.rivet?.meta || {};
  const ohbMeta = latestTrace?.ocm_outcome_handoff || {};
  const continuityHealth = (() => {
    if (uiPendingItems.length > 0) {
      return { label: 'waiting', tone: 'amber', detail: 'ожидает пользователя' };
    }
    if (tickBusy) {
      return { label: 'busy', tone: 'orange', detail: 'тик выполняется' };
    }
    if (latestContinuityMove && latestWinner) {
      return { label: 'alive', tone: 'green', detail: 'carry зафиксирован' };
    }
    if (latestTrace) {
      return { label: 'stale', tone: 'slate', detail: 'нет свежего carry move' };
    }
    return { label: 'idle', tone: 'slate', detail: 'ветка ещё не прогрета' };
  })();
  const visibleMessageCount = history.filter((msg) => msg && msg.role !== 'system').length;
  const threadShortId = String(activeThreadId || '').trim()
    ? String(activeThreadId).trim().slice(0, 12)
    : 'нет';
  const operatorLabel = String(username || '').trim() || 'Гость';
  const inspectorSections = buildInspectorSections(latestTrace);
  const traceDrawerSections = [
    {
      title: 'Runtime Snapshot',
      payload: {
        owner: latestContinuityOwner || 'input',
        owner_family: latestOwnerLabel,
        winner: latestWinner || 'n/a',
        carry_move: latestContinuityMove || '',
        carry_reason: latestContinuityReason || '',
        health: continuityHealth.label,
      },
    },
    ...inspectorSections,
  ];
  const autoRunnerActive = runnerMode === 'auto' && !paused;
  const runnerLampText = autoRunnerActive ? 'AUTO' : (runnerMode === 'auto' ? 'PAUSED' : 'MANUAL');
  const runnerLampColor = autoRunnerActive ? '#6BD6FF' : (runnerMode === 'auto' ? '#B9C0CA' : '#7CFF6B');
  const sequenceRunning = !!runnerSequenceStatus?.running;
  const sequenceStatusText = sequenceRunning
    ? `SEQ ${Number(runnerSequenceStatus?.completed_steps || 0)}/${Number(runnerSequenceStatus?.max_steps || 0)}`
    : 'SEQ idle';
  const sequenceLampColor = sequenceRunning ? '#6BD6FF' : '#B9C0CA';
  const tickLampText = tickBusy ? 'BUSY' : 'FREE';
  const tickLampColor = tickBusy ? '#FFB84D' : '#7CFF6B';
  const serverLampText = statusServer ? 'ONLINE' : 'OFFLINE';
  const serverLampColor = statusServer ? '#7CFF6B' : '#FF7070';
  const AUTO_LOOP_IDLE_MS = 1000;
  const AUTO_LOOP_DEFAULT_MS = 900;
  const AUTO_LOOP_REPEAT_MS = 3500;
  const AUTO_LOOP_AWAIT_USER_MS = 5000;
  const AUTO_LOOP_POST_CHAT_COOLDOWN_MS = 3000;
  const hasCollapsedPendingQuestions = (
    !uiDialogOpen &&
    uiPendingItems.length > 0 &&
    String(activeThreadId || '') === String(uiQueueThreadId || '')
  );

  const updateActiveHistory = (updater) => {
    setThreads(prev => sortThreadsByRecency(prev.map(t => {
      if (t.id !== activeThreadId) return t;
      const nextHistory = typeof updater === 'function' ? updater(t.history || []) : updater;
      const nextPreviewItem = [...(Array.isArray(nextHistory) ? nextHistory : [])]
        .reverse()
        .find((msg) => msg && msg.role !== 'system' && String(msg.text || '').trim());
      return {
        ...t,
        history: nextHistory,
        lastTs: new Date().toISOString(),
        serverPreview: nextPreviewItem ? String(nextPreviewItem.text || '').trim() : t.serverPreview,
      };
    })));
  };

  const updateThreadHistory = (threadId, updater) => {
    const targetThreadId = String(threadId || '').trim();
    if (!targetThreadId) return;
    setThreads(prev => sortThreadsByRecency(prev.map(t => {
      if (t.id !== targetThreadId) return t;
      const nextHistory = typeof updater === 'function' ? updater(t.history || []) : updater;
      const nextPreviewItem = [...(Array.isArray(nextHistory) ? nextHistory : [])]
        .reverse()
        .find((msg) => msg && msg.role !== 'system' && String(msg.text || '').trim());
      return {
        ...t,
        history: nextHistory,
        lastTs: new Date().toISOString(),
        serverPreview: nextPreviewItem ? String(nextPreviewItem.text || '').trim() : t.serverPreview,
      };
    })));
  };

  const getParticipantForThread = (threadId) => {
    const target = String(threadId || '').trim();
    const thread = (threadsRef.current || threads || []).find((t) => String(t?.id || '').trim() === target) || activeThread;
    return buildThreadParticipant(thread, username);
  };

  const buildParticipantFieldsForThread = (threadId) => {
    const participant = getParticipantForThread(threadId);
    return {
      participant_id: participant.id,
      participant_name: participant.name,
      speaker_id: participant.id,
      speaker_name: participant.name,
    };
  };

  const updateActiveParticipantName = (value) => {
    if (!activeThreadId || activeThreadId === RUNTIME_THREAD_ID) return;
    const clean = normalizeParticipantName(value, username);
    setThreads(prev => prev.map(t => {
      if (!t || t.id !== activeThreadId) return t;
      const nextTitle = isGenericThreadTitle(t.title) || String(t.title || '').trim() === String(t.participantName || '').trim()
        ? clean
        : t.title;
      return {
        ...t,
        title: nextTitle,
        participantName: clean,
        participantId: String(t.participantId || `thread:${t.id}`).trim(),
      };
    }));
    if (authAccount && activeThreadId && activeThreadId !== RUNTIME_THREAD_ID) {
      const participant = buildParticipantFieldsForThread(activeThreadId);
      axios.patch(
        `${API_BASE}/chats/${encodeURIComponent(activeThreadId)}`,
        {
          title: clean,
          participant_id: participant.participant_id,
          participant_name: clean,
        },
        { headers: buildAuthHeaders() }
      ).catch(() => {});
    }
  };

  const markAcceptedChatTickPending = ({ threadId, text, username: messageUsername, errorMessage, jobId }) => {
    const targetThreadId = String(threadId || '').trim();
    if (!targetThreadId) return false;
    const pendingId = `recovered_chat_${targetThreadId}_${Date.now()}`;
    recoveredChatPendingRef.current = {
      ...(recoveredChatPendingRef.current || {}),
      [targetThreadId]: {
        id: pendingId,
        text: String(text || '').trim(),
        startedAt: Date.now(),
        errorMessage: String(errorMessage || '').trim(),
        jobId: String(jobId || '').trim(),
      },
    };
    maybeRenameThread(text);
    updateThreadHistory(targetThreadId, (prev) => {
      const history = Array.isArray(prev) ? prev : [];
      const alreadyHasUser = [...history].reverse().some((item) => (
        item &&
        item.role === 'user' &&
        String(item.text || '').trim() === String(text || '').trim()
      ));
      const next = alreadyHasUser ? [...history] : [
        ...history,
        {
          role: 'user',
          text,
          username: messageUsername,
          image: null,
          timestamp: new Date().toISOString(),
          thread_id: targetThreadId,
        },
      ];
      return [
        ...next,
        {
          role: 'system',
          text: '⏳ Backend принял запрос. Жду завершения тика и подтяну ответ сюда.',
          timestamp: new Date().toISOString(),
          thread_id: targetThreadId,
          origin: 'user',
          meta: { pending_recovered_chat_id: pendingId, async_job_id: String(jobId || '').trim() },
        },
      ];
    });
    setStatusServer(true);
    setStatusDetail('busy');
    setTickBusySafe(true);
    return true;
  };

  const recoverAcceptedChatTickFromStatus = async ({ threadId, text, username: messageUsername, errorMessage }) => {
    const targetThreadId = String(threadId || '').trim();
    if (!targetThreadId) return false;
    let status = null;
    try {
      status = await fetchStatus(targetThreadId);
    } catch {
      status = null;
    }
    const tickThreadId = String(status?.tick_thread_id || status?.last_meta?.thread_id || '').trim();
    const backendAccepted = !!(
      status?.ok &&
      (status?.tick_in_progress || status?.tick_lock_held) &&
      (!tickThreadId || tickThreadId === targetThreadId)
    );
    if (!backendAccepted) return false;
    return markAcceptedChatTickPending({
      threadId: targetThreadId,
      text,
      username: messageUsername,
      errorMessage,
    });
  };

  const maybeAppendRecoveredChatReplyFromStatus = (status, traceKey) => {
    const trace = status?.last_trace;
    if (!trace || !traceKey) return;
    if (isStatusDialogueFallbackTrace(trace)) return;
    const threadId = String(
      trace.thread_id ||
      status?.last_meta?.thread_id ||
      ''
    ).trim();
    if (!threadId) return;
    const pending = (recoveredChatPendingRef.current || {})[threadId];
    if (!pending) return;
    const replyText = String(trace.reply_user || trace.reply_text || status?.last_response?.text || '').trim();
    if (!replyText) return;
    recoveredChatPendingRef.current = {
      ...(recoveredChatPendingRef.current || {}),
      [threadId]: undefined,
    };
    delete recoveredChatPendingRef.current[threadId];
    updateThreadHistory(threadId, (prev) => {
      const history = Array.isArray(prev) ? prev : [];
      const alreadyHasReply = history.some((item) => (
        item &&
        item.role === 'llm' &&
        String(item?.meta?.status_trace_key || '') === traceKey
      ));
      if (alreadyHasReply) return history;
      const withoutPendingLine = history.filter((item) => (
        !(
          item &&
          item.role === 'system' &&
          String(item?.meta?.pending_recovered_chat_id || '') === String(pending.id || '')
        )
      ));
      return [
        ...withoutPendingLine,
        {
          role: 'llm',
          text: replyText,
          timestamp: new Date().toISOString(),
          trace,
          model: status?.last_meta?.model || selectedModel,
          thread_id: threadId,
          origin: 'user',
          meta: {
            recovered_from_status: true,
            status_trace_key: traceKey,
          },
        },
      ];
    });
    setLastRunnerStatus({ text: replyText, timestamp: Date.now() });
  };

  const setTickBusySafe = (value) => {
    tickBusyRef.current = value;
    setTickBusy(value);
  };

  const clearAutoAwaitingContinuation = (threadId = '') => {
    const key = String(threadId || '').trim();
    if (!key) {
      autoAwaitingContinuationRef.current = {};
      return;
    }
    const next = { ...(autoAwaitingContinuationRef.current || {}) };
    delete next[key];
    autoAwaitingContinuationRef.current = next;
  };

  const markAutoAwaitingContinuation = (threadId = '', detail = '') => {
    const key = String(threadId || '').trim();
    if (!key) return false;
    const current = autoAwaitingContinuationRef.current || {};
    const alreadyWaiting = !!current[key];
    autoAwaitingContinuationRef.current = {
      ...current,
      [key]: {
        detail: String(detail || '').trim() || 'no_autonomous_query_available',
        since: Date.now(),
      },
    };
    return !alreadyWaiting;
  };

  const resolveAutoThreadId = () => {
    const current = String(activeThreadIdRef.current || activeThreadId || '').trim();
    if (current && current !== RUNTIME_THREAD_ID) return current;
    const fallback = (threadsRef.current || []).find((t) => t && t.id && t.id !== RUNTIME_THREAD_ID);
    return String(fallback?.id || '').trim();
  };

  const resolveStatusThreadId = () => {
    const current = String(activeThreadIdRef.current || activeThreadId || '').trim();
    if (current === RUNTIME_THREAD_ID) return '';
    return resolveAutoThreadId() || current;
  };

const isBusyResponse = (res) =>
  res?.data?.ok === false && res?.data?.error === 'busy';

const isMissingContinuationResponse = (res) =>
  res?.data?.ok === false && res?.data?.error === 'missing_continuation_query';

const isTerminalToolPacketType = (value) => {
  const packetType = String(value || '').trim().toLowerCase();
  return ['answer_packet', 'insufficient_evidence_packet', 'conflict_packet', 'blocked_packet'].includes(packetType);
};

const getAutoHoldDetailFromTrace = (trace) => {
  const tr = trace && typeof trace === 'object' ? trace : {};
  const rt = tr._runtime && typeof tr._runtime === 'object' ? tr._runtime : {};
  const ui = tr.user_interaction && typeof tr.user_interaction === 'object' ? tr.user_interaction : {};
  const questions = Array.isArray(tr.question_items) ? tr.question_items : [];
  const packetType = String(rt.tool_transfer_packet_type || tr.tool_transfer_packet_type || '').trim().toLowerCase();
  const querySource = String(rt.query_source || '').trim().toLowerCase();
  const ocmCompletionReason = String(rt.ocm_completion_reason || '').trim().toLowerCase();
  const failureKind = String(rt.failure_kind || '').trim().toLowerCase();

  if (questions.length > 0 || tr.awaiting_user_answer || ui.awaiting_user_answer || rt.awaiting_user_answer) {
    return 'awaiting_user_answer';
  }
  if (isTerminalToolPacketType(packetType)) {
    return packetType;
  }
  if (failureKind === 'missing_continuation_query') {
    return 'missing_continuation_query';
  }
  if (
    querySource.includes('subject_veto')
    || querySource.includes('wait_user_signal')
    || ocmCompletionReason.includes('subject_veto')
    || ocmCompletionReason.includes('wait_user_signal')
  ) {
    return 'control_hold';
  }
  return '';
};

  const buildAuthHeaders = (tokenOverride = null) => {
    const token = String(tokenOverride ?? authToken ?? '').trim();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const mergeServerChatList = (items, requestedActive = '') => {
    const runtimeThread = normalizeThreadRecord((threadsRef.current || []).find((t) => t?.id === RUNTIME_THREAD_ID)) || {
      id: RUNTIME_THREAD_ID,
      title: RUNTIME_THREAD_TITLE,
      history: []
    };
    const existingById = new Map((threadsRef.current || []).map((t) => [String(t?.id || ''), t]));
    const serverThreads = (Array.isArray(items) ? items : [])
      .map(normalizeServerChatRecord)
      .filter(Boolean)
      .map((thread) => {
        const prev = existingById.get(thread.id);
        if (!prev) return thread;
        return {
          ...thread,
          history: Array.isArray(prev.history) ? prev.history : [],
          serverLoaded: !!prev.serverLoaded,
        };
      });
    const nextThreads = sortThreadsByRecency([runtimeThread, ...serverThreads]);
    setThreads(nextThreads);
    const desired = String(requestedActive || activeThreadIdRef.current || '').trim();
    const active = resolvePreferredActiveThread(nextThreads, desired);
    setActiveThreadId(active);
    return { nextThreads, active };
  };

  const loadServerChatHistory = async (threadId, tokenOverride = null) => {
    const target = String(threadId || '').trim();
    if (!target || target === RUNTIME_THREAD_ID) return null;
    const res = await axios.get(`${API_BASE}/chats/${encodeURIComponent(target)}`, {
      headers: buildAuthHeaders(tokenOverride),
    });
    const chat = res?.data?.chat;
    if (!chat) return null;
    const participant = buildThreadParticipant({
      id: target,
      title: chat.title,
      serverMeta: chat.meta || {},
      ownerDisplayName: chat.owner_display_name,
      ownerLogin: chat.owner_login,
    }, username);
    const mappedHistory = (Array.isArray(chat.messages) ? chat.messages : []).map((msg) =>
      normalizeServerMessage(msg, participant.name || chat.owner_display_name || chat.owner_login || '')
    );
    setThreads((prev) => sortThreadsByRecency(prev.map((t) => (
      t?.id === target
        ? {
            ...(t || {}),
            title: String(chat.title || t?.title || 'Чат').trim() || 'Чат',
            participantId: participant.id,
            participantName: participant.name,
            history: mappedHistory,
            serverManaged: true,
            serverLoaded: true,
            ownerLogin: String(chat.owner_login || '').trim(),
            ownerDisplayName: String(chat.owner_display_name || '').trim(),
            access: String(chat.access || t?.access || 'owner').trim() || 'owner',
            serverMeta: chat.meta || {},
            serverPreview: String(chat.preview || '').trim() || String(t?.serverPreview || '').trim(),
            lastTs: chat.last_ts || t?.lastTs || null,
            messageCount: Number(chat.messages?.length || t?.messageCount || 0),
          }
        : t
    ))));
    return chat;
  };

  const syncServerChats = async ({ requestedActive = '', tokenOverride = null, createIfEmpty = true } = {}) => {
    const headers = buildAuthHeaders(tokenOverride);
    const res = await axios.get(`${API_BASE}/chats`, { headers });
    const chats = Array.isArray(res?.data?.chats) ? res.data.chats : [];
    if (chats.length === 0 && createIfEmpty) {
      const participantName = normalizeParticipantName(username, `Собеседник ${normalThreads.length + 1}`);
      const createRes = await axios.post(
        `${API_BASE}/chats`,
        {
          title: participantName,
          participant_id: makeParticipantId(),
          participant_name: participantName,
        },
        { headers }
      );
      const created = createRes?.data?.chat;
      const nextItems = created ? [created] : [];
      const merged = mergeServerChatList(nextItems, created?.thread_id || requestedActive);
      if (created?.thread_id) {
        await loadServerChatHistory(created.thread_id, tokenOverride);
      }
      return merged;
    }
    const merged = mergeServerChatList(chats, requestedActive);
    if (merged.active && merged.active !== RUNTIME_THREAD_ID) {
      const activeThread = (merged.nextThreads || []).find((t) => t?.id === merged.active);
      if (!activeThread?.serverLoaded) {
        await loadServerChatHistory(merged.active, tokenOverride);
      }
    }
    return merged;
  };

  const refreshManagedAccounts = async (tokenOverride = null) => {
    const headers = buildAuthHeaders(tokenOverride);
    try {
      const res = await axios.get(`${API_BASE}/auth/accounts`, { headers });
      setManagedAccounts(Array.isArray(res?.data?.accounts) ? res.data.accounts : []);
    } catch {
      setManagedAccounts([]);
    }
  };

  const handleAuthLogin = async ({ login, password }) => {
    setAuthBusy(true);
    setAuthError('');
    try {
      const res = await axios.post(`${API_BASE}/auth/login`, { login, password });
      const token = String(res?.data?.token || '').trim();
      const account = res?.data?.account || null;
      if (!token || !account) throw new Error('login_failed');
      setAuthToken(token);
      setAuthAccount(account);
      setAuthBootstrapRequired(false);
      setUsername(String(account.display_name || account.login || username || 'Гость').trim() || 'Гость');
      await syncServerChats({ tokenOverride: token });
      if (account.is_master) await refreshManagedAccounts(token);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'login_failed';
      setAuthError(String(detail));
      return { ok: false, error: String(detail) };
    } finally {
      setAuthBusy(false);
    }
  };

  const handleAuthBootstrap = async ({ login, password, displayName }) => {
    setAuthBusy(true);
    setAuthError('');
    try {
      const res = await axios.post(`${API_BASE}/auth/bootstrap`, {
        login,
        password,
        display_name: displayName,
      });
      const token = String(res?.data?.token || '').trim();
      const account = res?.data?.account || null;
      if (!token || !account) throw new Error('bootstrap_failed');
      setAuthToken(token);
      setAuthAccount(account);
      setAuthBootstrapRequired(false);
      setUsername(String(account.display_name || account.login || 'Гость').trim() || 'Гость');
      await syncServerChats({ tokenOverride: token });
      await refreshManagedAccounts(token);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'bootstrap_failed';
      setAuthError(String(detail));
      return { ok: false, error: String(detail) };
    } finally {
      setAuthBusy(false);
    }
  };

  const handleAuthLogout = async () => {
    const token = String(authToken || '').trim();
    try {
      if (token) {
        await axios.post(`${API_BASE}/auth/logout`, {}, { headers: buildAuthHeaders(token) });
      }
    } catch {}
    setAuthToken('');
    setAuthAccount(null);
    setManagedAccounts([]);
    setAuthError('');
    const initial = loadThreads();
    setThreads(initial.threads);
    setActiveThreadId(initial.activeThreadId);
  };

  const handleAuthCreateAccount = async ({ login, password, displayName, isMaster = false }) => {
    setAuthBusy(true);
    setAuthError('');
    try {
      await axios.post(
        `${API_BASE}/auth/accounts`,
        {
          login,
          password,
          display_name: displayName,
          is_master: !!isMaster,
        },
        { headers: buildAuthHeaders() }
      );
      await refreshManagedAccounts();
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'account_create_failed';
      setAuthError(String(detail));
      return { ok: false, error: String(detail) };
    } finally {
      setAuthBusy(false);
    }
  };

  const updateActiveTitle = (title) => {
    if (!title) return;
    setThreads(prev => prev.map(t => (
      t.id === activeThreadId ? { ...t, title } : t
    )));
    if (authAccount && activeThreadId && activeThreadId !== RUNTIME_THREAD_ID) {
      axios.patch(
        `${API_BASE}/chats/${encodeURIComponent(activeThreadId)}`,
        { title },
        { headers: buildAuthHeaders() }
      ).catch(() => {});
    }
  };

  const handleNewThread = async () => {
    const title = `Собеседник ${normalThreads.length + 1}`;
    const participantId = makeParticipantId();
    if (authAccount) {
      try {
        const res = await axios.post(
          `${API_BASE}/chats`,
          { title, participant_id: participantId, participant_name: title },
          { headers: buildAuthHeaders() }
        );
        const chat = normalizeServerChatRecord(res?.data?.chat);
        if (chat) {
          setThreads((prev) => sortThreadsByRecency([...prev.filter((t) => t?.id !== chat.id), chat]));
          setActiveThreadId(chat.id);
          await loadServerChatHistory(chat.id);
          return;
        }
      } catch (e) {
        appendRuntimeEvent('⚠ Не удалось создать серверный чат', {
          source: 'frontend:thread_create',
          error: String(e?.response?.data?.detail || e?.message || e),
        });
      }
    }
    const id = makeThreadId();
    setThreads(prev => sortThreadsByRecency([...prev, {
      id,
      title,
      participantId,
      participantName: title,
      history: [],
      lastTs: new Date().toISOString()
    }]));
    setActiveThreadId(id);
  };

  const handleDeleteActiveThread = async (threadIdOverride = '') => {
    const targetThread = String(threadIdOverride || activeThreadId || '').trim();
    if (!targetThread || targetThread === RUNTIME_THREAD_ID) return;
    const ok = window.confirm('Удалить это окно собеседника? История по нему исчезнет из UI.');
    if (!ok) return;

    const kept = (threadsRef.current || threads || []).filter((t) => t && t.id !== targetThread);
    const normalKept = kept.filter((t) => t.id !== RUNTIME_THREAD_ID);
    let nextThreads = kept;
    let nextActive = normalKept[0]?.id || '';
    if (!nextActive) {
      const id = makeThreadId();
      nextThreads = [...kept, {
        id,
        title: 'Собеседник 1',
        participantId: makeParticipantId(),
        participantName: 'Собеседник 1',
        history: []
      }];
      nextActive = id;
    }
    setThreads(nextThreads);
    setActiveThreadId(nextActive);
    if (uiQueueThreadId === targetThread) {
      setUiDialogOpen(false);
      setUiQueueThreadId('');
      setUiPendingItems([]);
      setUiAnswerDraftById({});
    }
    appendRuntimeEvent('🗑 Чат удален', { thread_id: targetThread, source: 'frontend:thread_delete' });

    try {
      if (authAccount) {
        await axios.delete(`${API_BASE}/chats/${encodeURIComponent(targetThread)}`, {
          headers: buildAuthHeaders(),
        });
      } else {
        await axios.post(`${API_BASE}/runner/delete_thread`, { thread_id: targetThread });
      }
    } catch {
      appendRuntimeEvent('⚠ Не удалось удалить чат на backend', {
        thread_id: targetThread,
        source: 'frontend:thread_delete',
      });
    }
  };

  const appendRuntimeEvent = (text, meta = {}, overrides = {}) => {
    const payload = {
      role: overrides.role || 'system',
      text,
      timestamp: new Date().toISOString(),
      thread_id: RUNTIME_THREAD_ID,
      origin: overrides.origin || 'runtime',
      meta,
      trace: overrides.trace
    };
    setThreads((prev) => {
      const idx = prev.findIndex((t) => t.id === RUNTIME_THREAD_ID);
      if (idx === -1) {
        return [{
          id: RUNTIME_THREAD_ID,
          title: RUNTIME_THREAD_TITLE,
          history: capRuntimeHistory([payload])
        }, ...prev];
      }
      const next = [...prev];
      const runtime = next[idx];
      next[idx] = {
        ...runtime,
        history: capRuntimeHistory([...(runtime.history || []), payload])
      };
      return next;
    });
  };

  const emitRuntimeTraceEvent = (trace, meta = {}, overrides = {}) => {
    const src = trace && typeof trace === 'object' ? trace : {};
    if (isStatusDialogueFallbackTrace(src)) return;
    if (isAwaitUserMaintenanceHoldTrace(src)) return;
    const replyText = String(src.reply_user || src.reply_text || '').trim();
    const questionTexts = Array.isArray(src.question_items)
      ? src.question_items.map((it) => String(it?.text || '').trim()).filter(Boolean).slice(0, 3)
      : [];
    const fallbackQuestions = Array.isArray(src.questions_to_user)
      ? src.questions_to_user.map((q) => String(q || '').trim()).filter(Boolean).slice(0, 3)
      : [];
    const awaiting = !!(
      src.awaiting_user_answer ||
      src?._runtime?.awaiting_user_answer ||
      src?.user_interaction?.awaiting_user_answer
    );
    if (!(replyText || isMeaningfulTraceRuntimeOutput(src))) return;
    const signature = JSON.stringify({
      thread_id: String(meta.thread_id || src.thread_id || '').trim(),
      goal_id: String(meta.goal_id || src.goal_id || '').trim(),
      turn_type: String(src?.dialogue_turn?.turn_type || src.turn_type || '').trim(),
      reply: replyText,
      questions: questionTexts.length > 0 ? questionTexts : fallbackQuestions,
      awaiting,
    });
    if (signature && signature === lastRuntimeTraceSignatureRef.current) return;
    lastRuntimeTraceSignatureRef.current = signature;
    appendRuntimeEvent(
      replyText || '🧠 Tick output',
      meta,
      { role: 'llm', origin: overrides.origin || 'auto', trace: src }
    );
  };

  const _fmtNum = (v, digits = 2) => {
    if (v === null || v === undefined) return 'n/a';
    const n = Number(v);
    if (Number.isNaN(n)) return 'n/a';
    return n.toFixed(digits);
  };

  const _formatKpiLine = (kpi, opts = {}) => {
    if (!kpi) return null;
    const w = kpi.window ?? 10;
    const progressClass = String(opts.progressClass || '').trim();
    if (progressClass === 'conversation_turn') {
      return (
        `📊 Chat KPI(w=${w}): ` +
        `coh=${_fmtNum(kpi.coherence_avg)} ` +
        `use=${_fmtNum(kpi.usefulness_avg)} ` +
        `novelty_hit=${_fmtNum(kpi.novelty_hit_rate)} ` +
        `wmf=${_fmtNum(kpi.wm_patch_failed_rate)} ` +
        `sbn=${_fmtNum(kpi.scaffold_blocked_avg)} ` +
        `progress=chat_turn`
      );
    }
    return (
      `📊 KPI(w=${w}): ` +
      `repeat=${_fmtNum(kpi.plan_repeat_rate)} ` +
      `novelty_hit=${_fmtNum(kpi.novelty_hit_rate)} ` +
      `chain=${_fmtNum(kpi.chain_completion_rate)} ` +
      `stall=${_fmtNum(kpi.stall_rate)} ` +
      `act=${_fmtNum(kpi.real_actions_avg)} ` +
      `fb=${_fmtNum(kpi.feedback_new_avg)} ` +
      `wmf=${_fmtNum(kpi.wm_patch_failed_rate)} ` +
      `sbn=${_fmtNum(kpi.scaffold_blocked_avg)} ` +
      `occl=${_fmtNum(kpi.ocm_close_rate)}`
    );
  };

  const _fmtBool = (v) => (v ? '1' : '0');

  const _formatOcmFocusRef = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (!/^[A-Za-z0-9:_-]+$/.test(raw)) {
      let hash = 0;
      for (let i = 0; i < raw.length; i += 1) {
        hash = ((hash * 31) + raw.charCodeAt(i)) >>> 0;
      }
      return `h${hash.toString(36)}`;
    }
    if (raw.length <= 28) return raw;
    return `${raw.slice(0, 12)}...${raw.slice(-8)}`;
  };

  const _resolveOcmSeq = (meta, rt) => {
    const threadId = String(meta?.thread_id || rt?.thread_id || 'system');
    const focusedId = String(rt?.ocm_focused_id || '').trim();
    const opened = Number(rt?.ocm_focus_opened_at_tick ?? -1);
    const key = focusedId || (opened >= 0 ? `opened:${opened}` : '');
    if (!key) return null;
    const root = ocmSeqRef.current || { byThread: {} };
    const byThread = root.byThread || {};
    const cur = byThread[threadId] || { seqByKey: {}, nextSeq: 1 };
    if (!cur.seqByKey[key]) {
      cur.seqByKey[key] = cur.nextSeq;
      cur.nextSeq += 1;
    }
    byThread[threadId] = cur;
    ocmSeqRef.current = { byThread };
    return Number(cur.seqByKey[key] || 0) || null;
  };

  const _formatRuntimeFocusLine = (meta, rt) => {
    if (!rt) return null;
    const mode = rt.mode || meta?.mode || 'unknown';
    const src = rt.source || meta?.source || 'unknown';
    const q = (rt.query || meta?.query || '').toString().trim();
    const pm = (rt.priority_mode || '').toString().trim();
    const ab = rt.ab || null;
    const abTag = (ab && ab.enabled) ? `ab=${ab.experiment_id || 'exp'}:v${ab.variant ?? 0}` : '';
    const oq = rt.open_questions_n ?? null;
    const pn = rt.plan_n ?? null;
    const fbd = rt.fallback_defaults_n ?? null;
    const tc = rt.taskifier_created ?? null;
    const ex = `ex=${_fmtBool(rt.executor_started)}:${_fmtBool(rt.executor_timed_out)}`;
    const fb = rt.feedback_new_n ?? null;
    const act = rt.real_actions_n ?? null;
    const db = `deb=${_fmtBool(rt.debate_round2_ran)}`;
    const cr = rt.compliance_rate ?? null;
    const why = `whyQ=${_fmtBool(rt.why_next_question)}`;
    const qprog = `qpr=${_fmtBool(rt.question_progress)}`;
    const qesc = `qesc=${_fmtBool(rt.question_escalated)}`;
    const qstreak = rt.question_no_progress_streak ?? null;
    const srr = rt.semantic_regression_rate ?? null;
    const tfp = `tfp=${_fmtBool(rt.trajectory_force_pivot)}`;
    const prState = (rt.poison_recovery_state || '').toString().trim();
    const pr = prState ? `pr=${prState}` : '';
    const pq = (rt.poison_quarantined_n ?? null);
    const steer = (rt.steering_op || '').toString().trim();
    const steerQ = (rt.steering_query || '').toString().trim();
    const sm = (rt.subject_mode || '').toString().trim();
    const sallow = `sok=${_fmtBool(rt.subject_allow_thinking)}`;
    const ocm = (rt.ocm_phase || '').toString().trim();
    const ocmc = rt.ocm_phase_code ?? null;
    const ocmf = `ocf=${_fmtBool(rt.ocm_forced_close)}`;
    const ocmId = (rt.ocm_focused_id || '').toString().trim();
    const ocmOpened = rt.ocm_focus_opened_at_tick ?? null;
    const ocmAge = rt.ocm_focus_age_ticks ?? null;
    const ocmNew = (ocmAge !== null && Number(ocmAge) === 0);
    const ocmStatus = (rt.ocm_focus_status || '').toString().trim();
    const ocmSeq = _resolveOcmSeq(meta, rt);
    const ocmEnd = String(ocm || '').toLowerCase() === 'close' || !!rt.ocm_forced_close;
    const ctype = (rt.clarify_type || '').toString().trim();
    const uiPending = rt.user_interaction_pending_n ?? null;
    const uiAuto = _fmtBool(rt.user_interaction_autonomous_if_no_reply);
    const uiSoft = _fmtBool(rt.await_user_guard_soft_wait_autonomous);
    const ccMove = (rt.conversation_contract_move_family || '').toString().trim();
    const ccRel = (rt.conversation_contract_user_relation_mode || '').toString().trim();
    const ccInit = (rt.conversation_contract_initiative_balance || '').toString().trim();
    const sutureMode = rt.suture_enabled ? (rt.suture_observer_only ? 'obs' : 'live') : '';
    const sutureValid = rt.suture_packet_valid;
    const sutureReason = (rt.suture_reason_code || '').toString().trim();
    const scontract = (rt.subject_contract || '').toString().trim();
    const santicontract = (rt.subject_anticontract || '').toString().trim();
    const scontractTag = scontract ? `sct="${scontract.slice(0, 48)}"` : '';
    const santicontractTag = santicontract ? `sac="${santicontract.slice(0, 48)}"` : '';
    const model = (rt.model || '').toString().trim();
    const qsrc = (rt.query_source || '').toString().trim();
    const mop = (rt.mutation_operator || '').toString().trim();
    const mdst = rt.mutation_distance ?? null;
    const escs = (rt.escalation_source || '').toString().trim();
    const esco = (rt.escalation_op || '').toString().trim();
    const icr = (rt.intent_contract_reason || '').toString().trim();
    const icc = `icc=${_fmtBool(rt.intent_contract_clamped)}`;

    if (
      String(mode).toLowerCase() === 'thinker' &&
      String(src).toLowerCase() === 'post_chat_thinker' &&
      !q &&
      oq === null &&
      pn === null &&
      tc === null &&
      fb === null &&
      act === null &&
      !runtimeHasMeaningfulFocus(rt)
    ) {
      return null;
    }

    const head = `🧭 ${mode}/${src}${pm ? ` pm=${pm}` : ''}${abTag ? ` ${abTag}` : ''}`;
    const tail =
      `oq=${oq ?? 'n/a'} plan=${pn ?? 'n/a'} ` +
      `${fbd !== null ? `fbd=${fbd} ` : ''}` +
      `tasks+${tc ?? 'n/a'} ${ex} fb=${fb ?? 'n/a'} act=${act ?? 'n/a'} ` +
      `${db} c=${_fmtNum(cr, 2)} ${why} ${qprog} ${qesc}` +
      `${qstreak !== null ? ` qnp=${qstreak}` : ''} ` +
      `sr=${_fmtNum(srr, 2)} ${tfp}` +
      `${pr ? ` ${pr}` : ''}` +
      `${pq !== null ? ` pq=${pq}` : ''}` +
      `${sm ? ` sm=${sm}` : ''}` +
      ` ${sallow}` +
      `${ocm ? ` ocm=${ocm}` : ''}` +
      `${ocmc !== null ? ` ocmc=${ocmc}` : ''}` +
      `${ocmSeq !== null ? ` ocm#=${ocmSeq}` : ''}` +
      `${ocmId ? ` ocm_ref=${_formatOcmFocusRef(ocmId)}` : ''}` +
      `${ocmOpened !== null ? ` ocm_open=${ocmOpened}` : ''}` +
      `${ocmAge !== null ? ` ocm_age=${ocmAge}` : ''}` +
      `${ocmStatus ? ` ocm_st=${ocmStatus}` : ''}` +
      `${ocmNew ? ' ocm_new=1' : ''}` +
      `${ocmEnd ? ' ocm_end=1' : ''}` +
      ` ${ocmf}` +
      `${model ? ` mdl=${model}` : ''}` +
      `${qsrc ? ` qsrc=${qsrc}` : ''}` +
      `${mop ? ` mop=${mop}` : ''}` +
      `${mdst !== null ? ` md=${_fmtNum(mdst, 2)}` : ''}` +
      `${escs ? ` esc=${escs}` : ''}` +
      `${esco ? ` eco=${esco}` : ''}` +
      `${icr ? ` icr=${icr}` : ''}` +
      ` ${icc}` +
      `${ctype ? ` cty=${ctype}` : ''}` +
      `${uiPending !== null ? ` uiq=${uiPending}` : ''}` +
      ` uia=${uiAuto}` +
      ` uisw=${uiSoft}` +
      `${ccMove ? ` cmf=${ccMove}` : ''}` +
      `${ccRel ? ` crm=${ccRel}` : ''}` +
      `${ccInit ? ` cib=${ccInit}` : ''}` +
      `${sutureMode ? ` sut=${sutureMode}` : ''}` +
      `${sutureValid !== undefined ? ` suv=${_fmtBool(sutureValid)}` : ''}` +
      `${sutureReason ? ` src=${sutureReason}` : ''}` +
      `${mode !== 'chat' && scontractTag ? ` ${scontractTag}` : ''}` +
      `${mode !== 'chat' && santicontractTag ? ` ${santicontractTag}` : ''}` +
      `${steer ? ` steer=${steer}` : ''}` +
      `${steerQ ? ` steer_q="${steerQ.slice(0, 60)}"` : ''}`;
    if (q) return `${head} q="${q.slice(0, 80)}" ${tail}`;
    return `${head} ${tail}`;
  };

  function runtimeHasMeaningfulFocus(rt) {
    if (!rt || typeof rt !== 'object') return false;
    const checks = [
      rt.query,
      rt.query_source,
      rt.rivet_task_seed,
      rt.suture_dialogue_move_candidate,
      rt.ohb_dialogue_move_candidate,
      rt.ocm_focused_id,
      rt.tool_preflight_branch,
      rt.tool_preflight_question,
      rt.subject_contract,
      rt.steering_query,
    ];
    return checks.some((value) => String(value || '').trim());
  }

  const appendRuntimeKpiForTick = async (tickFinishedAt, extraMeta = {}) => {
    const key = String(tickFinishedAt || '');
    if (!key) return;
    if (key === lastKpiKeyRef.current) return;
    lastKpiKeyRef.current = key;
    try {
      const res = await axios.get(`${API_BASE}/metrics`);
      const progressClass = String(res?.data?.last_tick?.quality?.progress_metric_class || '').trim();
      const classKpi = progressClass ? res?.data?.kpi_by_progress_class?.[progressClass]?.kpi : null;
      const kpi = classKpi || res?.data?.kpi;
      const line = _formatKpiLine(kpi, { progressClass });
      if (!line) return;
      appendRuntimeEvent(line, { source: 'backend:metrics', tick_finished_at: key, progress_metric_class: progressClass, ...extraMeta });
    } catch {
      // No KPI line if metrics are unavailable; keep runtime chat clean.
    }
  };

  const maybeRenameThread = (text) => {
    if (!text) return;
    if (!activeThread) return;
    const current = activeThread.title || '';
    if (isGenericThreadTitle(current) && (activeThread.history || []).length === 0) {
      updateActiveTitle(text.slice(0, 40));
    }
  };

  const confirmName = () => {
    const val = nameDraft.trim();
    if (val) setUsername(val);
    try { localStorage.setItem(ASKED_NAME_KEY, '1'); } catch {}
    setAskNameOpen(false);
  };

  const skipName = () => {
    try { localStorage.setItem(ASKED_NAME_KEY, '1'); } catch {}
    setAskNameOpen(false);
  };

  useEffect(() => {
    document.body.style.background = darkMode ? '#191C21' : '#f4f4f4';
    document.body.style.color = darkMode ? '#f5f5f5' : '#222';
  }, [darkMode]);

  useEffect(() => {
    if (username) localStorage.setItem('mutant-nick', username);
  }, [username]);

  useEffect(() => {
    try {
      if (authToken) localStorage.setItem(AUTH_TOKEN_KEY, authToken);
      else localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch {}
    const defaults = axios.defaults || {};
    const headers = defaults.headers || {};
    const common = headers.common || {};
    if (!defaults.headers) defaults.headers = headers;
    if (!defaults.headers.common) defaults.headers.common = common;
    if (authToken) defaults.headers.common.Authorization = `Bearer ${authToken}`;
    else delete defaults.headers.common.Authorization;
  }, [authToken]);

  useEffect(() => {
    let mounted = true;
    const token = String(authToken || '').trim();
    if (!token) {
      setAuthAccount(null);
      setManagedAccounts([]);
      axios.get(`${API_BASE}/auth/session`).then((res) => {
        if (!mounted) return;
        setAuthBootstrapRequired(!!res?.data?.bootstrap_required);
      }).catch(() => {
        if (!mounted) return;
        setAuthBootstrapRequired(false);
      });
      return undefined;
    }
    const loadSession = async () => {
      try {
        const res = await axios.get(`${API_BASE}/auth/session`, { headers: buildAuthHeaders(token) });
        if (!mounted) return;
        if (res?.data?.authenticated) {
          const account = res.data.account || null;
          setAuthAccount(account);
          setAuthBootstrapRequired(false);
          if (account?.display_name) setUsername(String(account.display_name).trim() || 'Гость');
          await syncServerChats({ tokenOverride: token });
          if (account?.is_master) await refreshManagedAccounts(token);
          return;
        }
        setAuthBootstrapRequired(!!res?.data?.bootstrap_required);
        setAuthAccount(null);
        setManagedAccounts([]);
        setAuthToken('');
      } catch {
        if (!mounted) return;
        setAuthAccount(null);
        setManagedAccounts([]);
      }
    };
    loadSession();
    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    try {
      const asked = localStorage.getItem(ASKED_NAME_KEY);
      if (!asked && (!username || username === 'Гость')) {
        setAskNameOpen(true);
      }
    } catch {}
  }, []);

  useEffect(() => {
    setThreads((prev) => {
      const runtime = prev.find((t) => t && t.id === RUNTIME_THREAD_ID);
      if (runtime) {
        return prev.map((t) => (
          t?.id === RUNTIME_THREAD_ID
            ? { ...t, title: RUNTIME_THREAD_TITLE, history: capRuntimeHistory(t.history) }
            : t
        ));
      }
      return [{ id: RUNTIME_THREAD_ID, title: RUNTIME_THREAD_TITLE, history: [] }, ...prev];
    });
  }, []);

  useEffect(() => {
    try {
      const persistedThreads = (threads || [])
        .map(normalizeThreadRecord)
        .filter(Boolean);
      localStorage.setItem(THREADS_KEY, JSON.stringify({ threads: persistedThreads }));
      localStorage.setItem(ACTIVE_THREAD_KEY, activeThreadId || '');
    } catch {}
  }, [threads, activeThreadId]);

  useEffect(() => {
    if (threads.length === 0) return;
    if (!threads.find(t => t.id === activeThreadId)) {
      setActiveThreadId(threads[0].id);
    }
  }, [threads, activeThreadId]);

  useEffect(() => {
    const loadModels = async () => {
      try {
        const res = await axios.get(`${API_BASE}/engine`);
        const data = res?.data || {};
        const engineModels = Array.isArray(data.models) ? data.models : [];
        const currentModel = String(data.current_model || '').trim();
        if (engineModels.length > 0) {
          setModelsList(engineModels);
          if (!engineModels.includes(selectedModel)) {
            setSelectedModel(currentModel || engineModels[0]);
          }
        } else {
          setModelsList(FALLBACK_MODELS);
        }
        if (currentModel) {
          setActiveEngineModel(currentModel);
          if (!selectedModel) setSelectedModel(currentModel);
        }
        return;
      } catch {
        // fallback below
      }
      listModels().then((models) => {
        if (models.length > 0) {
          setModelsList(models);
          if (!models.includes(selectedModel)) {
            setSelectedModel(models[0]);
          }
        } else {
          setModelsList(FALLBACK_MODELS);
        }
      });
    };
    loadModels();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let mounted = true;
    const refreshEngine = async () => {
      try {
        const res = await axios.get(`${API_BASE}/engine`);
        if (!mounted) return;
        const current = String(res?.data?.current_model || '').trim();
        if (current) setActiveEngineModel(current);
      } catch {
        // ignore transient engine/status errors
      }
    };
    refreshEngine();
    const t = setInterval(refreshEngine, 8000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/runner/settings`, { headers: buildAuthHeaders() }).then(res => {
      const s = res.data;
      if (!s) return;
      const mode = (s.mode || 'manual');
      setRunnerMode(mode);
      setRunnerInterval(Number(s.interval) || 60);
      setPaused(mode === 'manual' ? true : !!s.paused);
      setRunnerMaxCycles(s.max_cycles === undefined ? null : s.max_cycles);
      setRunnerCycleCount(Number(s.cycle_count) || 0);
      setRunnerSequenceStatus(s.runner_sequence_ocm || null);
      if (s.model) setSelectedModel(s.model);
      setRunnerSettingsHydrated(true);
    }).catch(() => {});
  }, [authToken]);

  useEffect(() => {
    const prev = prevRunnerModeRef.current;
    if (runnerMode === 'auto' && prev !== 'auto') {
      // New auto session starts from zero local cycles.
      setRunnerCycleCount(0);
      runnerCycleCountRef.current = 0;
      setPaused(false);
      clearAutoAwaitingContinuation();
    }
    prevRunnerModeRef.current = runnerMode;
  }, [runnerMode]);

  useEffect(() => {
    lastRunnerStatusRef.current = lastRunnerStatus;
  }, [lastRunnerStatus]);

  useEffect(() => {
    runnerCycleCountRef.current = Number(runnerCycleCount || 0);
  }, [runnerCycleCount]);

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  useEffect(() => {
    const active = (threads || []).find((t) => t?.id === activeThreadId);
    if (!authAccount || !active || active.id === RUNTIME_THREAD_ID || active.serverLoaded) return;
    loadServerChatHistory(active.id).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAccount, activeThreadId, threads]);

  const refreshTaskStats = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/stats`);
      if (res?.data?.ok) setTaskStats(res.data);
    } catch {
      setTaskStats({ pending: null });
    }
  };

  const refreshTaskList = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/list`);
      if (res?.data?.ok) setTaskList(res.data.items || []);
    } catch {
      setTaskList([]);
    }
  };

  const runExecutorOnce = async (targetTaskId = null, { includeAuto = false } = {}) => {
    try {
      setTaskBusy(true);
      setTaskStatus('🔎 Проверка запущена…');
      appendRuntimeEvent('🔎 Executor run start', { source: 'frontend:executor', task_id: targetTaskId || null });
      const startRes = await axios.post(`${API_BASE}/tasks/run`, {
        include_auto: !!includeAuto,
        limit: 1,
        thread_id: activeThreadId || undefined
      });
      const alreadyRunning = startRes?.data?.started === false && startRes?.data?.reason === 'already_running';
      if (alreadyRunning) {
        setTaskStatus('⏳ Проверка уже выполняется, жду результат…');
      }
      let res = null;
      let last = null;
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        await refreshTaskStats();
        res = await axios.get(`${API_BASE}/tasks/list`);
        if (res?.data?.ok) setTaskList(res.data.items || []);
        const items = (res?.data?.items || []);
        last = targetTaskId ? items.find((x) => x.id === targetTaskId) : items[0];
        if (last && (last.status === 'done' || last.status === 'blocked')) break;
        setTaskStatus(`⏳ Выполняется… ${i + 1}s`);
      }
      if (!last || last.status === 'in_progress' || last.status === 'pending') {
        setTaskStatus('⚠️ Проверка выполняется слишком долго. Повтори запуск или уточни запрос.');
        appendRuntimeEvent('⚠️ Executor run timeout', { source: 'frontend:executor', task_id: targetTaskId || null });
        return;
      }
        if (last?.result?.evidence?.length) {
          setTaskStatus(`✅ Найдено источников: ${last.result.evidence.length}`);
          setLastEvidence(last.result.evidence);
          const executorText = buildExecutorResultText(last.result);
          if (executorText) {
            const sourcesText = buildExecutorSourcesText(last.result.evidence);
            updateActiveHistory(prev => [...prev, {
              role: 'llm',
              text: sourcesText ? `${executorText}\n\nИсточники:\n${sourcesText}` : executorText,
              timestamp: new Date().toISOString(),
              thread_id: activeThreadId,
              origin: 'system'
            }]);
          }
          appendRuntimeEvent('✅ Executor run done', {
            source: 'frontend:executor',
            task_id: last?.id || targetTaskId || null,
            evidence_n: (last?.result?.evidence || []).length
          });
      } else if (last?.result?.error) {
        setTaskStatus(`⚠️ Ошибка: ${last.result.error}`);
        setLastEvidence([]);
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: `⚠️ Evidence error: ${last.result.error}`,
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'system'
        }]);
        appendRuntimeEvent('❌ Executor run error', {
          source: 'frontend:executor',
          task_id: last?.id || targetTaskId || null,
          error: last.result.error
        });
      } else {
        setTaskStatus('✅ Проверка завершена');
        setLastEvidence([]);
        appendRuntimeEvent('✅ Executor run done', { source: 'frontend:executor', task_id: last?.id || targetTaskId || null });
      }
    } catch (e) {
      appendRuntimeEvent('❌ Executor run error', { source: 'frontend:executor', error: String(e) });
    }
    finally {
      setTaskBusy(false);
    }
  };

  const enqueueTask = async (text, options = {}) => {
    try {
      const q = normalizeTaskQuery(text);
      if (!q) return null;
      const targetThreadId = String(options.threadId || activeThreadId || '').trim();
      const res = await axios.post(
        `${API_BASE}/tasks/enqueue`,
        buildManualTaskPayload(q, {
          threadId: targetThreadId,
          answerText: options.answerText || '',
          missingFields: options.missingFields || [],
        })
      );
      if (res?.data?.queued === false) {
        const clarifyText = res?.data?.question_to_user || res?.data?.summary || 'Нужны уточнения перед проверкой.';
        setTaskStatus(clarifyText);
        if (res?.data?.question_to_user) {
          setManualTaskPendingByThread((prev) => ({
            ...(prev || {}),
            [targetThreadId]: {
              base_query: q,
              question: String(res.data.question_to_user || '').trim(),
              missing_fields: Array.isArray(res.data.missing_fields) ? res.data.missing_fields : [],
            },
          }));
          updateActiveHistory(prev => [...prev, {
            role: 'system',
            text: `Уточнение перед проверкой: ${res.data.question_to_user}`,
            timestamp: new Date().toISOString(),
            thread_id: targetThreadId,
            origin: 'system'
          }]);
          appendRuntimeEvent('ℹ️ Task needs clarify', {
            source: 'frontend:tasks',
            question: res.data.question_to_user,
            missing_fields: res.data.missing_fields || []
          });
        } else if (res?.data?.summary) {
          setManualTaskPendingByThread((prev) => {
            const next = { ...(prev || {}) };
            delete next[targetThreadId];
            return next;
          });
          appendRuntimeEvent('ℹ️ Task skipped', {
            source: 'frontend:tasks',
            summary: res.data.summary,
            tool_decision: res.data.tool_decision || null
          });
        }
        return {
          queued: false,
          id: null,
          ...res.data,
        };
      }
      setManualTaskPendingByThread((prev) => {
        const next = { ...(prev || {}) };
        delete next[targetThreadId];
        return next;
      });
      await refreshTaskStats();
      await refreshTaskList();
      return {
        queued: true,
        id: res?.data?.id || null,
        ...res?.data,
      };
    } catch {
      return null;
    }
  };

  const resolveManualTask = async (text, options = {}) => {
    const q = normalizeTaskQuery(text);
    if (!q) return null;
    const targetThreadId = String(options.threadId || activeThreadIdRef.current || activeThreadId || '').trim();
    setTaskBusy(true);
    setTaskStatus('🔎 Проверка выполняется…');
    appendRuntimeEvent('🔎 Tool branch start', {
      source: 'frontend:tool_branch',
      thread_id: targetThreadId,
      query: q,
    });
    try {
      const res = await axios.post(
        `${API_BASE}/tasks/resolve`,
        {
          ...buildManualTaskPayload(q, {
            threadId: targetThreadId,
            answerText: options.answerText || '',
            missingFields: options.missingFields || [],
          }),
          force_external: true,
        }
      );
      const packet = res?.data && typeof res.data === 'object' ? res.data : {};
      const packetType = String(packet.packet_type || '').trim();
      const branchResult = buildToolBranchResult(packet);
      const resultText = buildExecutorResultText(branchResult);
      const sourcesText = buildExecutorSourcesText(branchResult.evidence);

      if (packetType === 'needs_clarify_packet') {
        const question = String(packet.question_to_user || packet.summary || 'Нужны уточнения перед проверкой.').trim();
        setTaskStatus(question);
        setLastEvidence([]);
        setManualTaskPendingByThread((prev) => ({
          ...(prev || {}),
          [targetThreadId]: {
            base_query: q,
            question,
            missing_fields: Array.isArray(packet.missing_fields) ? packet.missing_fields : [],
          },
        }));
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: `Уточнение перед проверкой: ${question}`,
          timestamp: new Date().toISOString(),
          thread_id: targetThreadId,
          origin: 'system'
        }]);
        appendRuntimeEvent('ℹ️ Tool branch needs clarify', {
          source: 'frontend:tool_branch',
          thread_id: targetThreadId,
          question,
          missing_fields: packet.missing_fields || [],
        });
        return packet;
      }

      setManualTaskPendingByThread((prev) => {
        const next = { ...(prev || {}) };
        delete next[targetThreadId];
        return next;
      });

      if (packetType === 'answer_packet') {
        setTaskStatus(branchResult.evidence.length > 0 ? `✅ Найдено источников: ${branchResult.evidence.length}` : '✅ Проверка завершена');
        setLastEvidence(branchResult.evidence);
        updateActiveHistory(prev => [...prev, {
          role: 'llm',
          text: sourcesText ? `${resultText}\n\nИсточники:\n${sourcesText}` : resultText,
          timestamp: new Date().toISOString(),
          thread_id: targetThreadId,
          origin: 'manual'
        }]);
        appendRuntimeEvent('✅ Tool branch answer', {
          source: 'frontend:tool_branch',
          thread_id: targetThreadId,
          packet_type: packetType,
          evidence_n: branchResult.evidence.length,
        });
        return packet;
      }

      const insufficiencyText = resultText || String(packet.summary || 'Недостаточно надежных и релевантных источников для уверенного ответа.').trim();
      setTaskStatus(packetType === 'insufficient_evidence_packet' ? '⚠️ Недостаточно доказательств' : '⚠️ Проверка остановлена');
      setLastEvidence(branchResult.evidence);
      updateActiveHistory(prev => [...prev, {
        role: 'llm',
        text: sourcesText ? `${insufficiencyText}\n\nИсточники:\n${sourcesText}` : insufficiencyText,
        timestamp: new Date().toISOString(),
        thread_id: targetThreadId,
        origin: 'manual'
      }]);
      appendRuntimeEvent(packetType === 'insufficient_evidence_packet' ? '⚠️ Tool branch insufficient evidence' : '⚠️ Tool branch blocked', {
        source: 'frontend:tool_branch',
        thread_id: targetThreadId,
        packet_type: packetType || 'blocked_packet',
        error: packet.error || '',
        evidence_n: branchResult.evidence.length,
      });
      return packet;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Network Error';
      setTaskStatus(`⚠️ Ошибка: ${msg}`);
      setLastEvidence([]);
      updateActiveHistory(prev => [...prev, {
        role: 'system',
        text: `⚠️ Ошибка поиска: ${msg}`,
        timestamp: new Date().toISOString(),
        thread_id: targetThreadId,
        origin: 'system'
      }]);
      appendRuntimeEvent('❌ Tool branch error', {
        source: 'frontend:tool_branch',
        thread_id: targetThreadId,
        error: String(msg),
      });
      return null;
    } finally {
      setTaskBusy(false);
    }
  };

  const handleManualTaskClarifyAnswer = async (answerText, pendingTask) => {
    const targetThreadId = String(activeThreadIdRef.current || activeThreadId || '').trim();
    const baseQuery = normalizeTaskQuery(pendingTask?.base_query || '');
    if (!targetThreadId || !baseQuery) return false;
    setTaskBusy(true);
    setTaskStatus('🔎 Уточнение принято, продолжаю проверку…');
    appendRuntimeEvent('↪ Manual task clarify answer bound', {
      source: 'frontend:tasks',
      thread_id: targetThreadId,
      missing_fields: pendingTask?.missing_fields || [],
    });
    try {
      const packet = await resolveManualTask(baseQuery, {
        threadId: targetThreadId,
        answerText,
        missingFields: pendingTask?.missing_fields || [],
      });
      if (!packet) {
        return true;
      }
      return true;
    } finally {
      setTaskBusy(false);
    }
  };

  useEffect(() => {
    if (!runnerSettingsHydrated) return;
    const payload = {
      mode: runnerMode,
      interval: runnerMode === 'auto' ? 0 : runnerInterval,
      paused,
      model: selectedModel
    };
    if (runnerMaxCycles !== null && runnerMaxCycles !== undefined) {
      payload.max_cycles = runnerMaxCycles;
    }
    axios.post(`${API_BASE}/runner/settings`, payload, {
      headers: buildAuthHeaders(),
    }).catch(() => {});
    // eslint-disable-next-line
  }, [runnerSettingsHydrated, runnerMode, runnerInterval, paused, runnerMaxCycles, selectedModel]);

  useEffect(() => {
    let cancelled = false;
    let timerId;

    const loop = async () => {
      let nextDelayMs = AUTO_LOOP_DEFAULT_MS;
      if (cancelled) return;
      if (!runnerSettingsHydrated) {
        timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
        return;
      }
      if (runnerMode !== 'auto' || paused) {
        timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
        return;
      }
      if (runnerMaxCycles !== null && runnerCycleCountRef.current >= runnerMaxCycles) {
        timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
        return;
      }
      if (tickBusyRef.current || autoTickInFlightRef.current) {
        timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
        return;
      }
      if (shouldDelayAutoThinkerAfterChat(postChatCooldownUntilRef.current, Date.now())) {
        timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
        return;
      }
      const autoThreadId = resolveAutoThreadId();
      if (!autoThreadId) {
        timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
        return;
      }
      const hasPendingQuestion =
        String(uiQueueThreadId || '') === autoThreadId &&
        Array.isArray(uiPendingItems) &&
        uiPendingItems.length > 0;
      if (hasPendingQuestion) {
        timerId = setTimeout(loop, AUTO_LOOP_AWAIT_USER_MS);
        return;
      }
      if (autoAwaitingContinuationRef.current[String(autoThreadId || '').trim()]) {
        timerId = setTimeout(loop, AUTO_LOOP_AWAIT_USER_MS);
        return;
      }
      const participantFields = buildParticipantFieldsForThread(autoThreadId);
      let started = false;
      try {
        autoTickInFlightRef.current = true;
        setTickBusySafe(true);
        started = true;
        appendRuntimeEvent('▶ Auto tick start', { mode: 'thinker', source: 'frontend:auto' });
        const res = await axios.post(
          `${API_BASE}/runner/tick`,
          buildThinkerTickPayload({
            model: selectedModel,
            username: participantFields.participant_name || username,
            threadId: autoThreadId,
            participantId: participantFields.participant_id,
            participantName: participantFields.participant_name,
          }),
          {
            headers: buildAuthHeaders(),
          }
        );
        if (isBusyResponse(res)) {
          setStatusServer(true);
          setStatusDetail('busy');
          appendRuntimeEvent('⏳ Auto tick busy', {
            mode: 'thinker',
            source: 'frontend:auto',
            stage: res?.data?.stage || ''
          });
          timerId = setTimeout(loop, AUTO_LOOP_IDLE_MS);
          return;
        }
        if (isMissingContinuationResponse(res)) {
          setStatusServer(true);
          setStatusDetail('idle');
          const detail = res?.data?.detail || 'no_autonomous_query_available';
          const firstWait = markAutoAwaitingContinuation(autoThreadId, detail);
          if (firstWait) {
            appendRuntimeEvent('⏸ Auto tick waiting for continuation', {
              mode: 'thinker',
              source: 'frontend:auto',
              detail,
              thread_id: autoThreadId,
            });
          }
          timerId = setTimeout(loop, AUTO_LOOP_AWAIT_USER_MS);
          return;
        }
        clearAutoAwaitingContinuation(autoThreadId);
        const text = extractTickText(res.data);
        const trace = buildTrace(res.data);
        const autoHoldDetail = getAutoHoldDetailFromTrace(trace);
        try {
          applyQuestionQueueFromTrace(trace, autoThreadId);
        } catch (err) {
          appendRuntimeEvent('⚠ Question queue parse failed', {
            source: 'frontend:auto',
            error: String(err),
          });
        }
        if (autoHoldDetail) {
          const firstWait = markAutoAwaitingContinuation(autoThreadId, autoHoldDetail);
          if (firstWait) {
            appendRuntimeEvent('⏸ Auto tick waiting for continuation', {
              mode: 'thinker',
              source: 'frontend:auto',
              detail: autoHoldDetail,
              thread_id: autoThreadId,
            });
          }
        }
        const isDuplicate =
          text &&
          lastRunnerStatusRef.current?.text &&
          text === lastRunnerStatusRef.current.text;
        const turnType = String(trace?.dialogue_turn?.turn_type || trace?.turn_type || '').toLowerCase();
        const askUserRepeat = hasPendingQuestion && turnType === 'ask_user';
        const awaitingUser = !!(
          trace?.awaiting_user_answer ||
          trace?.user_interaction?.awaiting_user_answer ||
          trace?._runtime?.awaiting_user_answer
        );

        const showInChat = !!text && (!filterDuplicates || !isDuplicate) && !askUserRepeat;
        if (showInChat) {
          setThreads((prev) => prev.map((t) => {
            if (!t || t.id !== autoThreadId) return t;
            const nextHistory = [
              ...(t.history || []).filter((m) => m.role !== 'system'),
              {
                role: 'llm',
                text,
                timestamp: new Date().toISOString(),
                trace,
                model: selectedModel,
                thread_id: autoThreadId,
                origin: 'auto'
              }
            ];
            return { ...t, history: nextHistory };
          }));
          setLastRunnerStatus({ text, timestamp: Date.now() });
        } else if (text && filterDuplicates && (isDuplicate || askUserRepeat)) {
          const nowTs = Date.now();
          if (nowTs - Number(lastAutoDuplicateNoticeAtRef.current || 0) > 8000) {
            lastAutoDuplicateNoticeAtRef.current = nowTs;
            appendRuntimeEvent('↪ Auto tick duplicate reply skipped', {
              mode: 'thinker',
              source: 'frontend:auto',
              goal_id: res?.data?.goal_id || '',
              thread_id: res?.data?.thread_id || '',
              reply_head: String(text || '').slice(0, 120),
              ask_user_repeat: askUserRepeat,
            });
          }
        }
        if (askUserRepeat || isDuplicate) {
          nextDelayMs = AUTO_LOOP_REPEAT_MS;
        }
        if (awaitingUser) {
          nextDelayMs = Math.max(nextDelayMs, AUTO_LOOP_AWAIT_USER_MS);
        }
        // Count every successful auto tick, even when duplicate reply is hidden in UI.
        setRunnerCycleCount(prev => {
          const next = prev + 1;
          runnerCycleCountRef.current = next;
          return next;
        });
        setStatusServer(true);
        setStatusDetail('ok');
        appendRuntimeEvent('✅ Auto tick done', {
          mode: 'thinker',
          source: 'frontend:auto',
          goal_id: res?.data?.goal_id || '',
          thread_id: res?.data?.thread_id || '',
          duplicate_skipped: !!(text && filterDuplicates && isDuplicate),
          reply_head: String(text || '').slice(0, 180)
        });
      } catch (e) {
        const busy = e?.response?.data?.error === 'busy';
        if (busy) {
          setStatusServer(true);
          setStatusDetail('busy');
        } else {
          setStatusServer(false);
          setStatusDetail(String(e));
          appendRuntimeEvent('❌ Auto tick error', {
            mode: 'thinker',
            source: 'frontend:auto',
            error: String(e)
          });
        }
      } finally {
        if (started) setTickBusySafe(false);
        autoTickInFlightRef.current = false;
      }
      timerId = setTimeout(loop, nextDelayMs);
    };

    loop();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
    // eslint-disable-next-line
  }, [
    runnerMode,
    runnerSettingsHydrated,
    paused,
    runnerMaxCycles,
    filterDuplicates,
    activeThreadId,
    selectedModel,
    username,
    uiQueueThreadId,
    uiPendingItems
  ]);

  useEffect(() => {
    if (autoscroll && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history, autoscroll]);

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
    if (activeThreadId === RUNTIME_THREAD_ID) {
      setUiDialogOpen(false);
      return;
    }
    if (uiQueueThreadId && activeThreadId !== uiQueueThreadId) {
      setUiDialogOpen(false);
      return;
    }
    if (uiQueueThreadId && activeThreadId === uiQueueThreadId && uiPendingItems.length > 0) {
      setUiDialogOpen(true);
    }
  }, [activeThreadId, uiQueueThreadId, uiPendingItems.length]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const statusThreadId = resolveStatusThreadId();
        const globalStatus = await fetchStatus('', { lite: true });
        let threadStatus = globalStatus;
        if (statusThreadId) {
          try {
            const scoped = await fetchStatus(statusThreadId, { lite: true });
            if (scoped?.ok) threadStatus = scoped;
          } catch {
            // Keep the global status for the live machine HUD.
          }
        }
        const liveStatus = globalStatus?.ok ? globalStatus : threadStatus;
        const status = threadStatus?.ok ? threadStatus : liveStatus;
        let monitorStatus = liveStatus;
        if (liveStatus?.ok) {
          if (runtimeMonitorTraceFromStatus(liveStatus)) {
            lastFullMonitorStatusRef.current = liveStatus;
            lastFullMonitorStatusAtRef.current = Date.now();
          } else {
            const nowMs = Date.now();
            if (nowMs - Number(lastFullMonitorStatusAtRef.current || 0) > 4500) {
              try {
                const fullGlobalStatus = await fetchStatus('');
                if (fullGlobalStatus?.ok && runtimeMonitorTraceFromStatus(fullGlobalStatus)) {
                  monitorStatus = fullGlobalStatus;
                  lastFullMonitorStatusRef.current = fullGlobalStatus;
                  lastFullMonitorStatusAtRef.current = nowMs;
                }
              } catch {
                // Keep the last full monitor payload if the heavy status call fails.
              }
            } else if (lastFullMonitorStatusRef.current?.ok) {
              monitorStatus = lastFullMonitorStatusRef.current;
            }
          }
          const sequenceThreadId = String(liveStatus?.runner_sequence_ocm?.thread_id || '').trim();
          const sequenceStatus = String(liveStatus?.runner_sequence_ocm?.status || '').trim().toLowerCase();
          const monitorTraceForStatus = runtimeMonitorTraceFromStatus(monitorStatus) || (
            monitorStatus?.last_trace && typeof monitorStatus.last_trace === 'object' ? monitorStatus.last_trace : {}
          );
          const monitorTraceThreadId = String(
            monitorTraceForStatus?.thread_id ||
            monitorTraceForStatus?._runtime?.participant_window_thread_id ||
            ''
          ).trim();
          const sequenceCanDriveMonitor =
              sequenceThreadId &&
              ['queued', 'running', 'stopping', 'completed'].includes(sequenceStatus || 'completed') &&
              (
              !runtimeMonitorTraceFromStatus(monitorStatus) ||
              monitorTraceThreadId !== sequenceThreadId
              );
          if (sequenceCanDriveMonitor) {
            try {
              const sequenceMonitorStatus = await fetchStatus(sequenceThreadId);
              if (sequenceMonitorStatus?.ok && runtimeMonitorTraceFromStatus(sequenceMonitorStatus)) {
                monitorStatus = sequenceMonitorStatus;
                lastFullMonitorStatusRef.current = sequenceMonitorStatus;
                lastFullMonitorStatusAtRef.current = Date.now();
              }
            } catch {
              // Sequence status is observability-only here; keep the global HUD stable.
            }
          }
        }
        if (!mounted) return;
        const hasStatus = liveStatus && liveStatus.ok;
        if (hasStatus && typeof liveStatus.tick_in_progress !== 'undefined') {
          const nextRunnerSequence = runnerSequenceHasMonitorValue(liveStatus.runner_sequence_ocm)
            ? liveStatus.runner_sequence_ocm
            : (runnerSequenceHasMonitorValue(monitorStatus?.runner_sequence_ocm) ? monitorStatus.runner_sequence_ocm : null);
          if (nextRunnerSequence) {
            setRunnerSequenceStatus(nextRunnerSequence);
          }
          setLiveTraceStatus(buildLiveTraceSnapshot(liveStatus));
          const nextMonitorTrace = runtimeMonitorTraceFromStatus(monitorStatus);
          if (nextMonitorTrace) {
            persistRuntimeMonitorCache(monitorStatus);
            setLiveTraceForMonitor(nextMonitorTrace);
          } else if (lastFullMonitorStatusRef.current?.ok) {
            const cachedMonitorTrace = runtimeMonitorTraceFromStatus(lastFullMonitorStatusRef.current);
            if (cachedMonitorTrace) {
              setLiveTraceForMonitor(cachedMonitorTrace);
            }
          } else {
            const fallbackTrace = monitorStatus?.last_trace && typeof monitorStatus.last_trace === 'object'
              ? monitorStatus.last_trace
              : null;
            setLiveTraceForMonitor((prev) => (traceHasRuntimeMonitor(prev) ? prev : fallbackTrace));
          }
          const tickInProgress = !!liveStatus?.tick_in_progress;
          const tickLockHeld = !!liveStatus?.tick_lock_held;
          const tickStage = String(liveStatus?.tick_stage || '').trim();
          const tickStartedAt = Number(liveStatus?.tick_started_at || 0);
          const nowSec = Date.now() / 1000;
          const freshLock =
            tickLockHeld &&
            (
              tickInProgress ||
              !!tickStage ||
              (tickStartedAt > 0 && (nowSec - tickStartedAt) < 15)
            );
          const backendBusy = tickInProgress || freshLock;
          const busyReason = tickInProgress ? 'local_tick' : (freshLock ? 'global_lock' : 'none');
          setTickBusySafe(backendBusy);
          if (backendBusy && !statusBusyRef.current) {
            appendRuntimeEvent('🟡 Backend tick started', {
              source: 'backend:status',
              mode: liveStatus?.last_meta?.mode || 'unknown',
              stage: liveStatus?.tick_stage || '',
              busy_reason: busyReason
            });
          } else if (!backendBusy && statusBusyRef.current) {
            const fin = liveStatus?.tick_finished_at || '';
            if (fin && fin !== lastTickFinishedRef.current) {
              lastTickFinishedRef.current = fin;
              appendRuntimeEvent(liveStatus?.last_tick_error ? '🔴 Backend tick finished with error' : '🟢 Backend tick finished', {
                source: 'backend:status',
                mode: liveStatus?.last_meta?.mode || 'unknown',
                goal_id: liveStatus?.last_meta?.goal_id || '',
                thread_id: liveStatus?.last_meta?.thread_id || '',
                stage: liveStatus?.last_tick_stage || '',
                error: liveStatus?.last_tick_error || '',
                busy_reason: busyReason
              });
              // D0-82: emit one-line KPIs into Runtime chat so we don't have to open /metrics.
              appendRuntimeKpiForTick(fin, {
                mode: liveStatus?.last_meta?.mode || 'unknown',
                goal_id: liveStatus?.last_meta?.goal_id || '',
                thread_id: liveStatus?.last_meta?.thread_id || ''
              });
            }
          }
          const traceScopedToActiveThread = statusTraceMatchesThread(status, statusThreadId);
          const traceKey = buildStatusTraceFingerprint(status, statusThreadId);
          const traceEventKey = `${traceScopedToActiveThread ? 'thread' : 'monitor'}:${traceKey}`;
          if (status?.last_trace && traceKey && traceEventKey !== lastTraceKeyRef.current) {
            lastTraceKeyRef.current = traceEventKey;
            const runtimeMode = String(
              status?.last_trace?._runtime?.mode ||
              status?.last_meta?.mode ||
              ''
            ).toLowerCase();
            const runtimeSource = String(
              status?.last_trace?._runtime?.source ||
              status?.last_meta?.source ||
              ''
            ).toLowerCase();
            const runtimeModel = String(
              status?.last_trace?._runtime?.model ||
              status?.last_meta?.model ||
              ''
            ).trim();
            if (runtimeModel) {
              setLastTickModel(runtimeModel);
            }
            if (traceScopedToActiveThread) {
              maybeAppendRecoveredChatReplyFromStatus(status, traceKey);
              const runtimeReplyText = String(
                status?.last_trace?.reply_user ||
                status?.last_trace?.reply_text ||
                ''
              ).trim();
              if (runtimeReplyText || isMeaningfulTraceRuntimeOutput(status?.last_trace)) {
                emitRuntimeTraceEvent(
                  status?.last_trace,
                  {
                    source: 'backend:trace',
                    mode: status?.last_meta?.mode || 'unknown',
                    goal_id: status?.last_meta?.goal_id || '',
                    thread_id: status?.last_meta?.thread_id || '',
                    runtime_mode: runtimeMode,
                    runtime_source: runtimeSource,
                  },
                  { origin: 'auto' }
                );
              }
              const focusLine = _formatRuntimeFocusLine(status?.last_meta, status?.last_trace?._runtime);
              if (focusLine) {
                appendRuntimeEvent(focusLine, {
                  source: 'backend:runtime',
                  mode: status?.last_meta?.mode || 'unknown',
                  goal_id: status?.last_meta?.goal_id || '',
                  thread_id: status?.last_meta?.thread_id || ''
                });
              }
              const traceQuestionItems = Array.isArray(status?.last_trace?.question_items)
                ? status.last_trace.question_items.map((it) => String(it?.text || '').trim()).filter(Boolean).slice(0, 3)
                : [];
              const traceQuestions = Array.isArray(status?.last_trace?.questions_to_user)
                ? status.last_trace.questions_to_user.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 3)
                : [];
              const traceInteractionQuestions = Array.isArray(status?.last_trace?.user_interaction?.pending_questions)
                ? status.last_trace.user_interaction.pending_questions.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 3)
                : [];
              const mergedQuestions = (
                traceQuestionItems.length > 0
                  ? traceQuestionItems
                  : (traceQuestions.length > 0 ? traceQuestions : traceInteractionQuestions)
              ).slice(0, 3);
              const awaitingUserAnswer = !!(
                status?.last_trace?.awaiting_user_answer ||
                status?.last_trace?._runtime?.awaiting_user_answer ||
                status?.last_trace?.user_interaction?.awaiting_user_answer
              );
              try {
                applyQuestionQueueFromTrace(status?.last_trace || {}, status?.last_meta?.thread_id || '');
              } catch (err) {
                appendRuntimeEvent('⚠ Status question-queue sync failed', {
                  source: 'backend:status',
                  error: String(err),
                });
              }
              if (awaitingUserAnswer) {
                const qText = mergedQuestions.length > 0
                  ? `❓ Вопросы к пользователю\n${mergedQuestions.map((q) => `• ${q}`).join('\n')}`
                  : '❓ Ожидается ответ пользователя.';
                appendRuntimeEvent(qText, {
                  source: 'backend:user_interaction',
                  mode: status?.last_meta?.mode || 'unknown',
                  goal_id: status?.last_meta?.goal_id || '',
                  thread_id: status?.last_meta?.thread_id || '',
                  awaiting_user_answer: true,
                  questions_n: mergedQuestions.length
                });
              }
            }
          }
          statusBusyRef.current = backendBusy;
          setStatusServer(true);
          setStatusDetail(backendBusy ? 'busy' : 'ok');
          return;
        }
        const res = await pingApi();
        if (!mounted) return;
        const keepOnline = tickBusyRef.current || statusBusyRef.current;
        setStatusServer(keepOnline ? true : res.ok);
        setStatusDetail(keepOnline ? 'busy' : (res.detail || (res.ok ? 'ok' : res.error || 'offline')));
        if (keepOnline) {
          setTickBusySafe(true);
        }
      } catch (err) {
        if (!mounted) return;
        const keepOnline = tickBusyRef.current || autoTickInFlightRef.current;
        statusBusyRef.current = false;
        if (!keepOnline) setTickBusySafe(false);
        setStatusServer(keepOnline ? true : false);
        setStatusDetail(keepOnline ? 'busy' : 'status_error');
      }
    };
    tick();
    const t = setInterval(tick, 1500);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  const extractTickText = (data) => {
    if (isAwaitUserMaintenanceHoldTrace(data)) return '';
    const replyUser = String(
      data?.reply_user ||
      data?.parsed?.reply_user ||
      data?.arbiter?.reply_user ||
      ''
    ).trim();
    if (replyUser) return replyUser;
    return (
      data?.parsed?.reply_text ||
      data?.raw ||
      data?.parsed?.reply_md ||
      data?.parsed?.reflection_md ||
      '(пусто)'
    );
  };

  const buildTrace = (data) => {
    const roles = (data?.roles || []).map((r) => {
      const parsed = r?.data?.parsed;
      return {
        role: r?.role,
        summary: r?.summary,
        parsed,
        valid_json: r?.data?.valid_json
      };
    });
    const arb = data?.arbiter || data?.parsed || {};
    const state = arb?.state || {};
    const arbRuntime = arb?._runtime && typeof arb._runtime === 'object' ? arb._runtime : {};
    const dataRuntime = data?._runtime && typeof data._runtime === 'object' ? data._runtime : {};
    const outputControllerPacket =
      data?.output_controller_packet ||
      dataRuntime.output_controller_packet ||
      arbRuntime.output_controller_packet ||
      null;
    const interlocutorSignal =
      data?.interlocutor_signal ||
      data?.interlocutor_signal_packet ||
      dataRuntime.interlocutor_signal_packet ||
      arbRuntime.interlocutor_signal_packet ||
      null;
    const runtime = {
      ...dataRuntime,
      ...arbRuntime,
      ...(outputControllerPacket ? { output_controller_packet: outputControllerPacket } : {}),
      ...(interlocutorSignal ? { interlocutor_signal_packet: interlocutorSignal } : {}),
    };
    const quietMaintenanceHold = isAwaitUserMaintenanceHoldTrace({
      ...data,
      arbiter: arb,
      _runtime: runtime,
    });
    return {
      roles,
      reply_text: quietMaintenanceHold ? '' : (arb?.reply_text || ''),
      reply_user: quietMaintenanceHold ? '' : (data?.reply_user || arb?.reply_user || ''),
      questions_to_user: data?.questions_to_user || arb?.questions_to_user || [],
      question_items: data?.question_items || arb?.question_items || [],
      awaiting_user_answer: !!(data?.awaiting_user_answer ?? arb?.awaiting_user_answer),
      user_interaction: data?.user_interaction || arb?.user_interaction || {},
      dialogue_turn: data?.dialogue_turn || arb?.dialogue_turn || {},
      turn_type: data?.turn_type || arb?.turn_type || '',
      goal_id: data?.goal_id || arb?.goal_id || '',
      thread_id: data?.thread_id || arb?.thread_id || '',
      _runtime: runtime,
      output_controller_packet: outputControllerPacket,
      interlocutor_signal_packet: interlocutorSignal,
      ocm_outcome_handoff: data?.ocm_outcome_handoff || arb?.ocm_outcome_handoff || null,
      suture: data?.suture || arb?.suture || null,
      rivet: data?.rivet || arb?.rivet || null,
      continuation_diff: data?.continuation_diff || arb?.continuation_diff || null,
      internal_tick_delta: data?.internal_tick_delta || arb?.internal_tick_delta || {},
      internal_plan: arb?.internal_plan || [],
      memory_writes: arb?.memory_writes || [],
      actions: arb?.actions || [],
      next_focus: state?.next_focus || [],
      intent_state: state?.intent_state || null,
      policy: state?.policy || null,
      meta_critic: state?.meta_critic || null
    };
  };

  const isStockUserQuestion = (text) => {
    const low = String(text || '').trim().toLowerCase().split(/\s+/).join(' ');
    if (!low) return false;
    if (low.includes('о чем хочешь поговорить')) return true;
    if (low.includes('давай пообщаемся')) return true;
    return false;
  };

  const questionTextKey = (text) => {
    const raw = String(text || '').trim().toLowerCase();
    if (!raw) return '';
    const firstQuestionMark = raw.indexOf('?');
    const scoped = firstQuestionMark >= 0 ? raw.slice(0, firstQuestionMark + 1) : raw;
    return scoped
      .replace(/[^a-zа-яё0-9?\s]+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const normalizeQuestionItemsFromTrace = (trace, fallbackThreadId = '') => {
    const threadId = String(trace?.thread_id || fallbackThreadId || '').trim();
    if (!threadId) return { threadId: '', items: [], awaiting: false };
    const rawItems = Array.isArray(trace?.question_items)
      ? trace.question_items
      : (
        Array.isArray(trace?.user_interaction?.pending_items)
          ? trace.user_interaction.pending_items
          : []
      );
    const textFallback = Array.isArray(trace?.questions_to_user) ? trace.questions_to_user : [];
    const items = [];
    const seen = new Set();
    const seenTextKeys = new Set();
    for (let i = 0; i < rawItems.length; i += 1) {
      const it = rawItems[i] || {};
      const text = String(it.text || it.question || '').trim();
      if (!text) continue;
      if (isStockUserQuestion(text)) continue;
      const textKey = questionTextKey(text);
      if (textKey && seenTextKeys.has(textKey)) continue;
      const qid = String(it.question_id || it.id || `uq_fallback_${i + 1}`).trim();
      if (!qid || seen.has(qid)) continue;
      seen.add(qid);
      if (textKey) seenTextKeys.add(textKey);
      items.push({
        question_id: qid,
        text: text.slice(0, 220),
        source: String(it.source || ''),
        asked_at_tick: Number(it.asked_at_tick ?? 0),
      });
    }
    if (items.length === 0) {
      for (let i = 0; i < textFallback.length; i += 1) {
        const text = String(textFallback[i] || '').trim();
        if (!text) continue;
        if (isStockUserQuestion(text)) continue;
        const textKey = questionTextKey(text);
        if (textKey && seenTextKeys.has(textKey)) continue;
        const qid = `uq_text_${i + 1}_${text.toLowerCase().replace(/[^\w]+/g, '_').slice(0, 20)}`;
        if (seen.has(qid)) continue;
        seen.add(qid);
        if (textKey) seenTextKeys.add(textKey);
        items.push({
          question_id: qid,
          text: text.slice(0, 220),
          source: 'trace.questions_to_user',
          asked_at_tick: 0,
        });
      }
    }
    const awaiting = !!(
      trace?.awaiting_user_answer ||
      trace?._runtime?.awaiting_user_answer ||
      trace?.user_interaction?.awaiting_user_answer
    );
    return { threadId, items: items.slice(0, 5), awaiting };
  };

  const applyQuestionQueueFromTrace = (trace, fallbackThreadId = '') => {
    const { threadId, items, awaiting } = normalizeQuestionItemsFromTrace(trace, fallbackThreadId);
    if (!threadId) return;
    const currentThread = String(activeThreadIdRef.current || activeThreadId || '').trim();
    if (threadId !== currentThread || currentThread === RUNTIME_THREAD_ID) return;
    if (awaiting && items.length > 0) {
      setUiQueueThreadId(threadId);
      setUiPendingItems(items);
      setUiDialogOpen(true);
      setUiAnswerDraftById((prev) => {
        const next = { ...(prev || {}) };
        for (const it of items) {
          const qid = String(it.question_id || '').trim();
          if (!qid) continue;
          if (!(qid in next)) next[qid] = '';
        }
        return next;
      });
      return;
    }
    setUiPendingItems([]);
    setUiDialogOpen(false);
    setUiQueueThreadId('');
  };

  const handleUiAnswerDraftChange = (questionId, value) => {
    const qid = String(questionId || '').trim();
    if (!qid) return;
    setUiAnswerDraftById((prev) => ({ ...(prev || {}), [qid]: value }));
  };

  const handleUiSkipQuestion = async (item, skipAll = false) => {
    if (uiActionBusy) return;
    const targetThread = String(uiQueueThreadId || activeThreadIdRef.current || activeThreadId || '').trim();
    if (!targetThread || targetThread === RUNTIME_THREAD_ID) return;
    const participantFields = buildParticipantFieldsForThread(targetThread);
    const qid = String(item?.question_id || '').trim();
    const qtext = String(item?.text || '').trim();
    setUiActionBusy(true);
    clearAutoAwaitingContinuation(targetThread);
    try {
      const res = await axios.post(`${API_BASE}/runner/user_interaction`, {
        thread_id: targetThread,
        action: skipAll ? 'skip_all' : 'skip',
        question_id: qid,
        question_text: qtext,
        mode: 'chat',
        ...participantFields,
      });
      const ui = res?.data?.user_interaction || {};
      const pending = Array.isArray(ui?.pending_items)
        ? ui.pending_items.map((x, i) => ({
          question_id: String(x?.question_id || `uq_${i + 1}`),
          text: String(x?.text || '').trim(),
          source: String(x?.source || ''),
          asked_at_tick: Number(x?.asked_at_tick ?? 0),
        })).filter((x) => x.text)
        : [];
      setUiPendingItems(pending.slice(0, 5));
      setUiQueueThreadId(targetThread);
      setUiDialogOpen(pending.length > 0);
      appendRuntimeEvent(
        skipAll ? '⏭ Пропущена вся очередь вопросов пользователя' : '⏭ Вопрос пользователя помечен как skipped',
        {
          source: 'frontend:user_interaction',
          action: skipAll ? 'skip_all' : 'skip',
          thread_id: targetThread,
          question_id: qid,
        }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'skip failed';
      appendRuntimeEvent('❌ Ошибка skip user-interaction', {
        source: 'frontend:user_interaction',
        error: String(msg),
      });
    } finally {
      setUiActionBusy(false);
    }
  };

  const handleUiAnswerQuestion = async (item) => {
    const qid = String(item?.question_id || '').trim();
    const qtext = String(item?.text || '').trim();
    const answerText = String((uiAnswerDraftById || {})[qid] || '').trim();
    const targetThread = String(uiQueueThreadId || activeThreadIdRef.current || activeThreadId || '').trim();
    if (!qid || !answerText || !targetThread || targetThread === RUNTIME_THREAD_ID) return;
    const participantFields = buildParticipantFieldsForThread(targetThread);
    const speakerName = participantFields.participant_name || username;
    if (loading || uiActionBusy) return;
    if (tickBusyRef.current) {
      updateActiveHistory(prev => [...prev, {
        role: 'system',
        text: '⏳ Система занята. Ответ на вопрос будет отправлен после завершения текущего тика.',
        timestamp: new Date().toISOString(),
        thread_id: targetThread,
        origin: 'user'
      }]);
      return;
    }
    setUiActionBusy(true);
    setLoading(true);
    let started = false;
    setTickBusySafe(true);
    clearAutoAwaitingContinuation(targetThread);
    started = true;
    try {
      const res = await axios.post(`${API_BASE}/runner/tick`, {
        user: answerText,
        model: selectedModel,
        expect_json: true,
        mode: 'chat',
        username: speakerName,
        thread_id: targetThread,
        ...participantFields,
        user_interaction_action: 'answer',
        user_interaction_question_id: qid,
        user_interaction_question_text: qtext,
      });
      if (isBusyResponse(res)) {
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: '⏳ Система занята. Повторите ответ чуть позже.',
          timestamp: new Date().toISOString(),
          thread_id: targetThread,
          origin: 'user'
        }]);
        setStatusServer(true);
        setStatusDetail('busy');
        return;
      }

      maybeRenameThread(answerText);
      updateActiveHistory(prev => [...prev, {
        role: 'user',
        text: answerText,
        username: speakerName,
        timestamp: new Date().toISOString(),
        thread_id: targetThread,
      }]);

      const text = extractTickText(res.data);
      const debug = res.data?.prompt_head || res.data?.debug;
      if (debug) setDebugPrompt(debug);
      const trace = buildTrace(res.data);
      updateActiveHistory(prev => [...prev, {
        role: 'llm',
        text,
        timestamp: new Date().toISOString(),
        trace,
        model: selectedModel,
        thread_id: targetThread,
        origin: 'user'
      }]);
      emitRuntimeTraceEvent(trace, {
        source: 'frontend:user_interaction',
        mode: 'chat',
        goal_id: res?.data?.goal_id || trace?.goal_id || '',
        thread_id: targetThread,
      }, { origin: 'user' });
      applyQuestionQueueFromTrace(trace, targetThread);
      setUiAnswerDraftById((prev) => ({ ...(prev || {}), [qid]: '' }));
      setStatusServer(true);
      setStatusDetail('ok');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Network Error';
      updateActiveHistory(prev => [...prev, {
        role: 'llm',
        text: `❌ Ошибка отправки ответа: ${msg}`,
        timestamp: new Date().toISOString(),
        thread_id: targetThread,
        origin: 'user'
      }]);
      setStatusServer(false);
      setStatusDetail(msg);
    } finally {
      setUiActionBusy(false);
      setLoading(false);
      if (started) setTickBusySafe(false);
    }
  };

  const handleSendMessage = async () => {
    if ((message.trim() === '' && !imageFile) || loading) return;
    if (activeThreadId === RUNTIME_THREAD_ID) {
      updateActiveHistory(prev => [...prev, {
        role: 'system',
        text: '⚠ Runtime-поток только для мониторинга. Переключись на обычный чат для отправки сообщений.',
        timestamp: new Date().toISOString(),
        thread_id: activeThreadId,
        origin: 'system'
      }]);
      return;
    }
    const participantFields = buildParticipantFieldsForThread(activeThreadId);
    const speakerName = participantFields.participant_name || username;
    const pendingManualTask = (!imageFile && activeManualTaskPending && typeof activeManualTaskPending === 'object')
      ? activeManualTaskPending
      : null;
    if (pendingManualTask) {
      const userMessage = {
        role: 'user',
        text: message,
        username: speakerName,
        image: null,
        timestamp: new Date().toISOString(),
        thread_id: activeThreadId
      };
      maybeRenameThread(message);
      updateActiveHistory(prev => [...prev, userMessage]);
      setMessage('');
      setImageFile(null);
      await handleManualTaskClarifyAnswer(message, pendingManualTask);
      return;
    }
    if (tickBusyRef.current) {
      updateActiveHistory(prev => [...prev, {
        role: 'system',
        text: '⏳ Система занята. Дождитесь завершения цикла.',
        timestamp: new Date().toISOString(),
        thread_id: activeThreadId,
        origin: 'user'
      }]);
      setStatusServer(true);
      setStatusDetail('busy');
      return;
    }

    clearAutoAwaitingContinuation(activeThreadId);
    setLoading(true);
    let started = false;
    let keepTickBusyFromStatus = false;
    setTickBusySafe(true);
    started = true;

    try {
      if (!imageFile) {
        appendRuntimeEvent('▶ Chat tick start', { mode: 'chat', source: 'frontend:user', thread_id: activeThreadId, participant: participantFields.participant_name });
        const res = await axios.post(`${API_BASE}/runner/tick/async`, {
          user: message,
          model: selectedModel,
          expect_json: true,
          mode: 'chat',
          username: speakerName,
          thread_id: activeThreadId,
          ...participantFields
        }, {
          headers: buildAuthHeaders(),
        });

        if (isBusyResponse(res)) {
          updateActiveHistory(prev => [...prev, {
            role: 'system',
            text: '⏳ Система занята. Повторите запрос чуть позже.',
            timestamp: new Date().toISOString(),
            thread_id: activeThreadId,
            origin: 'user'
          }]);
          setStatusServer(true);
          setStatusDetail('busy');
          return;
        }
        if (res?.data?.accepted && res?.data?.async) {
          markAcceptedChatTickPending({
            threadId: activeThreadId,
            text: message,
            username: speakerName,
            jobId: res?.data?.job_id || '',
          });
          keepTickBusyFromStatus = true;
          appendRuntimeEvent('▶ Chat tick accepted async', {
            mode: 'chat',
            source: 'frontend:user',
            thread_id: activeThreadId,
            participant: participantFields.participant_name,
            job_id: res?.data?.job_id || '',
          });
          return;
        }
        maybeRenameThread(message);
        updateActiveHistory(prev => [...prev, {
          role: 'user',
          text: message,
          username: speakerName,
          image: null,
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
        }]);
        const text = extractTickText(res.data);
        const debug = res.data?.prompt_head || res.data?.debug;
        if (debug) setDebugPrompt(debug);
        const trace = buildTrace(res.data);
        const holdDetail = getAutoHoldDetailFromTrace(trace);
        if (holdDetail) {
          markAutoAwaitingContinuation(activeThreadId, holdDetail);
        }

        updateActiveHistory(prev => [...prev, {
          role: 'llm',
          text,
          timestamp: new Date().toISOString(),
          trace,
          model: selectedModel,
          thread_id: activeThreadId,
          origin: 'user'
        }]);
        emitRuntimeTraceEvent(trace, {
          source: 'frontend:user',
          mode: 'chat',
          goal_id: res?.data?.goal_id || trace?.goal_id || '',
          thread_id: activeThreadId,
        }, { origin: 'user' });
        applyQuestionQueueFromTrace(trace, activeThreadId);
        postChatCooldownUntilRef.current = Date.now() + AUTO_LOOP_POST_CHAT_COOLDOWN_MS;
        appendRuntimeEvent('✅ Chat tick done', {
          mode: 'chat',
          source: 'frontend:user',
          goal_id: res?.data?.goal_id || '',
          thread_id: res?.data?.thread_id || '',
          reply_head: String(text || '').slice(0, 180)
        });
        if (authAccount && activeThreadId && activeThreadId !== RUNTIME_THREAD_ID) {
          await loadServerChatHistory(activeThreadId).catch(() => {});
        }
        setStatusServer(true);
        setStatusDetail('ok');
      } else {
        const formData = new FormData();
        formData.append('text', message);
        formData.append('model', selectedModel);
        formData.append('username', speakerName);
        formData.append('thread_id', activeThreadId);
        formData.append('participant_id', participantFields.participant_id);
        formData.append('participant_name', participantFields.participant_name);
        formData.append('mode', 'chat');
        formData.append('image', imageFile);

        const res = await axios.post(`${API_BASE}/message`, formData, {
          headers: buildAuthHeaders(),
        });
        if (isBusyResponse(res)) {
          updateActiveHistory(prev => [...prev, {
            role: 'system',
            text: '⏳ Система занята. Повторите запрос чуть позже.',
            timestamp: new Date().toISOString(),
            thread_id: activeThreadId,
            origin: 'user'
          }]);
          setStatusServer(true);
          setStatusDetail('busy');
          return;
        }

        const debug = res.data?.llm_response?.debug || res.data?.debug;
        if (debug) setDebugPrompt(debug);

        const immediate =
          res.data?.llm_response?.text ||
          res.data?.llm_response?.reply ||
          res.data?.text ||
          null;
        const trace = buildTrace(res.data);
        const holdDetail = getAutoHoldDetailFromTrace(trace);
        if (holdDetail) {
          markAutoAwaitingContinuation(activeThreadId, holdDetail);
        }

        maybeRenameThread(message);
        updateActiveHistory(prev => [...prev, {
          role: 'user',
          text: message,
          username: speakerName,
          image: imageFile ? URL.createObjectURL(imageFile) : null,
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
        }]);

        if (immediate) {
          updateActiveHistory(prev => [...prev, {
            role: 'llm',
            text: immediate,
            timestamp: new Date().toISOString(),
            trace,
            model: selectedModel,
            thread_id: activeThreadId,
            origin: 'user'
          }]);
          emitRuntimeTraceEvent(trace, {
            source: 'frontend:user',
            mode: 'chat',
            goal_id: res?.data?.goal_id || trace?.goal_id || '',
            thread_id: activeThreadId,
          }, { origin: 'user' });
          applyQuestionQueueFromTrace(trace, activeThreadId);
          if (authAccount && activeThreadId && activeThreadId !== RUNTIME_THREAD_ID) {
            await loadServerChatHistory(activeThreadId).catch(() => {});
          }
        } else {
          updateActiveHistory(prev => [...prev, {
            role: 'system',
            text: '🧠 Запрос получен. ИИ подумает и ответит чуть позже...',
            timestamp: new Date().toISOString(),
            thread_id: activeThreadId,
            origin: 'user'
          }]);
        }

        setStatusServer(true);
        setStatusDetail('ok');
      }
    } catch (err) {
      const busy = err?.response?.data?.error === 'busy';
      const msg = err?.response?.data?.detail || err.message || 'Network Error';
      if (busy) {
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: '⏳ Система занята. Повторите запрос чуть позже.',
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'user'
        }]);
        setStatusServer(true);
        setStatusDetail('busy');
      } else {
        const recovered = !imageFile && await recoverAcceptedChatTickFromStatus({
          threadId: activeThreadId,
          text: message,
          username: speakerName,
          errorMessage: msg,
        });
        if (recovered) {
          keepTickBusyFromStatus = true;
          appendRuntimeEvent('↻ Chat tick will be recovered from status', {
            mode: 'chat',
            source: 'frontend:user',
            thread_id: activeThreadId,
            error: String(msg),
          });
          return;
        }
        updateActiveHistory(prev => [...prev, {
          role: 'llm',
          text: `❌ Ошибка запроса: ${msg}`,
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'user'
        }]);
        setStatusServer(false);
        setStatusDetail(msg);
        appendRuntimeEvent('❌ Chat tick error', {
          mode: 'chat',
          source: 'frontend:user',
          error: String(msg)
        });
      }
    } finally {
      setMessage('');
      setImageFile(null);
      setLoading(false);
      if (started && !keepTickBusyFromStatus) setTickBusySafe(false);
    }
  };

  const handleRunTick = async () => {
    if (loading) return;
    if (tickBusyRef.current) {
      updateActiveHistory(prev => [...prev, {
        role: 'system',
        text: '⏳ Система занята. Дождитесь завершения цикла.',
        timestamp: new Date().toISOString(),
        thread_id: activeThreadId,
        origin: 'manual'
      }]);
      setStatusServer(true);
      setStatusDetail('busy');
      return;
    }
    setLoading(true);
    let started = false;
    setTickBusySafe(true);
    started = true;
    try {
      const participantFields = buildParticipantFieldsForThread(activeThreadId);
      appendRuntimeEvent('▶ Manual tick start', {
        mode: 'thinker',
        source: 'frontend:manual',
        thread_id: activeThreadId,
        participant: participantFields.participant_name,
      });
      const res = await axios.post(
        `${API_BASE}/runner/tick`,
        buildThinkerTickPayload({
          model: selectedModel,
          username: participantFields.participant_name || username,
          threadId: activeThreadId,
          allowWeakServiceContinue: true,
          participantId: participantFields.participant_id,
          participantName: participantFields.participant_name,
        }),
        {
          headers: buildAuthHeaders(),
        }
      );
      if (isBusyResponse(res)) {
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: '⏳ Система занята. Повторите запуск чуть позже.',
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'manual'
        }]);
        setStatusServer(true);
        setStatusDetail('busy');
        return;
      }
      if (isMissingContinuationResponse(res)) {
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: '⏸ Продолжение пока не сформировано: системе нечего разгонять без нового carry.',
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'manual'
        }]);
        setStatusServer(true);
        setStatusDetail('idle');
        appendRuntimeEvent('⏸ Manual tick waiting for continuation', {
          mode: 'thinker',
          source: 'frontend:manual',
          detail: res?.data?.detail || 'no_autonomous_query_available',
          thread_id: activeThreadId,
        });
        return;
      }
      const text = extractTickText(res.data);
      const debug = res.data?.prompt_head || res.data?.debug;
      if (debug) setDebugPrompt(debug);
      const trace = buildTrace(res.data);
      const holdDetail = getAutoHoldDetailFromTrace(trace);
      if (holdDetail) {
        markAutoAwaitingContinuation(activeThreadId, holdDetail);
      }
      updateActiveHistory(prev => [...prev, {
        role: 'llm',
        text,
        timestamp: new Date().toISOString(),
        trace,
        model: selectedModel,
        thread_id: activeThreadId,
        origin: 'manual'
      }]);
      emitRuntimeTraceEvent(trace, {
        source: 'frontend:manual',
        mode: 'thinker',
        goal_id: res?.data?.goal_id || trace?.goal_id || '',
        thread_id: activeThreadId,
      }, { origin: 'manual' });
      applyQuestionQueueFromTrace(trace, activeThreadId);
      appendRuntimeEvent('✅ Manual tick done', {
        mode: 'thinker',
        source: 'frontend:manual',
        goal_id: res?.data?.goal_id || '',
        thread_id: res?.data?.thread_id || '',
        reply_head: String(text || '').slice(0, 180)
      });
      setStatusServer(true);
      setStatusDetail('ok');
    } catch (err) {
      const busy = err?.response?.data?.error === 'busy';
      const msg = err?.response?.data?.detail || err.message || 'Network Error';
      if (busy) {
        updateActiveHistory(prev => [...prev, {
          role: 'system',
          text: '⏳ Система занята. Повторите запуск чуть позже.',
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'manual'
        }]);
        setStatusServer(true);
        setStatusDetail('busy');
      } else {
        updateActiveHistory(prev => [...prev, {
          role: 'llm',
          text: `❌ Ошибка запроса: ${msg}`,
          timestamp: new Date().toISOString(),
          thread_id: activeThreadId,
          origin: 'manual'
        }]);
        setStatusServer(false);
        setStatusDetail(msg);
        appendRuntimeEvent('❌ Manual tick error', {
          mode: 'thinker',
          source: 'frontend:manual',
          error: String(msg)
        });
      }
    } finally {
      setLoading(false);
      if (started) setTickBusySafe(false);
    }
  };

  const handleStartSequence = async () => {
    if (runnerSequenceStatus?.running || tickBusyRef.current) return;
    const participantFields = buildParticipantFieldsForThread(activeThreadId);
    const maxSteps = Number(runnerMaxCycles || 0) > 0 ? Number(runnerMaxCycles) : 30;
    try {
      const res = await axios.post(`${API_BASE}/runner/sequence/start`, {
        mode: 'thinker',
        thread_id: activeThreadId || '',
        model: selectedModel,
        max_steps: maxSteps,
        interval_s: 0,
        allow_weak_service_continue: true,
        username: participantFields.participant_name || username,
        participant_id: participantFields.participant_id,
        participant_name: participantFields.participant_name,
      }, {
        headers: buildAuthHeaders(),
      });
      const seq = res?.data?.sequence || null;
      setRunnerSequenceStatus(seq);
      appendRuntimeEvent(res?.data?.ok === false ? '❌ Sequence start rejected' : '▶ Sequence start', {
        source: 'frontend:sequence',
        thread_id: activeThreadId,
        max_steps: maxSteps,
        status: seq?.status || '',
        error: res?.data?.error || '',
      });
    } catch (err) {
      appendRuntimeEvent('❌ Sequence start error', {
        source: 'frontend:sequence',
        error: String(err?.response?.data?.detail || err?.response?.data?.error || err.message || err),
      });
    }
  };

  const handleStartRoadTest = async () => {
    const seed = String(roadTestSeed || '').trim();
    if (!seed || runnerSequenceStatus?.running || tickBusyRef.current) return;
    const participantFields = buildParticipantFieldsForThread(activeThreadId);
    const maxSteps = Math.max(1, Math.min(10, Number(roadTestSteps || 6)));
    const intervalSeconds = Math.max(0, Math.min(60, Number(roadTestInterval || 0)));
    try {
      const res = await axios.post(`${API_BASE}/runner/sequence/start`, {
        mode: 'thinker',
        thread_id: activeThreadId || '',
        model: selectedModel,
        query: seed,
        max_steps: maxSteps,
        interval_s: intervalSeconds,
        allow_weak_service_continue: true,
        runner_sequence_allow_branch_tools: true,
        username: participantFields.participant_name || username,
        participant_id: participantFields.participant_id,
        participant_name: participantFields.participant_name,
      }, {
        headers: buildAuthHeaders(),
      });
      const seq = res?.data?.sequence || null;
      setRunnerSequenceStatus(seq);
      setRuntimeMonitorTab('sequence');
      appendRuntimeEvent(res?.data?.ok === false ? '❌ Road Test rejected' : '🏁 Road Test started', {
        source: 'frontend:road_test',
        thread_id: activeThreadId,
        max_steps: maxSteps,
        status: seq?.status || '',
        error: res?.data?.error || '',
      });
    } catch (err) {
      appendRuntimeEvent('❌ Road Test start error', {
        source: 'frontend:road_test',
        error: String(err?.response?.data?.detail || err?.response?.data?.error || err.message || err),
      });
    }
  };

  const handleStopSequence = async () => {
    try {
      const res = await axios.post(`${API_BASE}/runner/sequence/stop`, {
        reason: 'frontend_stop',
      }, {
        headers: buildAuthHeaders(),
      });
      setRunnerSequenceStatus(res?.data?.sequence || null);
      appendRuntimeEvent('⏹ Sequence stop requested', {
        source: 'frontend:sequence',
        status: res?.data?.sequence?.status || '',
      });
    } catch (err) {
      appendRuntimeEvent('❌ Sequence stop error', {
        source: 'frontend:sequence',
        error: String(err?.response?.data?.detail || err?.response?.data?.error || err.message || err),
      });
    }
  };

  const handleSwitchEngine = async (targetModel) => {
    const modelToUse = String(targetModel || selectedModel || '').trim();
    if (!modelToUse) return { ok: false, error: 'empty_model' };
    if (engineSwitching) return { ok: false, error: 'busy' };
    setEngineSwitching(true);
    try {
      const res = await axios.post(`${API_BASE}/engine/switch`, { model: modelToUse });
      const data = res?.data || {};
      if (!data.ok) return { ok: false, error: data.error || 'switch_failed' };
      const switched = String(data.model || modelToUse).trim();
      // Keep runner settings in sync immediately to avoid model drift
      // between chat tick and background/manual runner tick.
      try {
        const payload = {
          mode: runnerMode,
          interval: runnerInterval,
          paused,
          model: switched
        };
        if (runnerMaxCycles !== null && runnerMaxCycles !== undefined) {
          payload.max_cycles = runnerMaxCycles;
        }
        await axios.post(`${API_BASE}/runner/settings`, payload);
      } catch {
        // non-fatal: engine is already switched, UI will retry on next settings sync
      }
      setActiveEngineModel(switched);
      setSelectedModel(switched);
      if (Array.isArray(data.models) && data.models.length > 0) {
        setModelsList(data.models);
      }
      appendRuntimeEvent(`🧠 Engine switched: ${switched}`, {
        source: 'frontend:engine_switch',
        model: switched
      });
      return { ok: true, model: switched };
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.response?.data?.error || String(e);
      appendRuntimeEvent('❌ Engine switch error', {
        source: 'frontend:engine_switch',
        error: String(errMsg)
      });
      return { ok: false, error: String(errMsg) };
    } finally {
      setEngineSwitching(false);
    }
  };

  if (view === 'demo') {
    return (
      <DemoStand apiBase={API_BASE} onExit={() => setView('chat')} />
    );
  }

  if (view === 'ocm') {
    return (
      <OcmTimeline apiBase={API_BASE} onExit={() => setView('chat')} />
    );
  }

  return (
    <Container maxWidth={false} className={`app-shell ${darkMode ? 'theme-dark' : 'theme-light'}`}>
      <Dialog open={askNameOpen} onClose={skipName}>
        <DialogTitle>Кто с нами говорит?</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Ваше имя"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmName();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={skipName}>Позже</Button>
          <Button variant="contained" onClick={confirmName}>Сохранить</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uiDialogOpen && uiPendingItems.length > 0}
        onClose={() => setUiDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Вопросы от системы</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Можно ответить сейчас или пропустить. При пропуске система продолжает автономно.
          </Typography>
          {uiPendingItems.map((item, idx) => {
            const qid = String(item?.question_id || `uq_${idx + 1}`);
            const qtext = String(item?.text || '').trim();
            const draft = String((uiAnswerDraftById || {})[qid] || '');
            return (
              <Box key={qid} sx={{ border: '1px solid #e1e1e1', borderRadius: 2, p: 1.2, mb: 1.2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {`Вопрос ${idx + 1}`}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {qtext}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Ваш ответ"
                  value={draft}
                  onChange={(e) => handleUiAnswerDraftChange(qid, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleUiAnswerQuestion(item);
                    }
                  }}
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={uiActionBusy || loading || !draft.trim()}
                    onClick={() => handleUiAnswerQuestion(item)}
                  >
                    Ответить
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={uiActionBusy || loading}
                    onClick={() => handleUiSkipQuestion(item, false)}
                  >
                    Скип
                  </Button>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUiDialogOpen(false)}>Свернуть</Button>
          <Button
            color="warning"
            onClick={() => handleUiSkipQuestion(null, true)}
            disabled={uiActionBusy || loading || uiPendingItems.length === 0}
          >
            Скип все
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={traceDrawerOpen}
        onClose={() => setTraceDrawerOpen(false)}
        PaperProps={{ className: 'trace-drawer' }}
      >
        <Box className="trace-drawer__head">
          <Typography variant="caption" className="section-kicker">Trace Drawer</Typography>
          <Typography variant="h6" className="trace-drawer__title">
            Последний semantic tick
          </Typography>
          <Typography variant="body2" className="trace-drawer__subtle">
            {activeThread?.title || 'Без названия'} · {threadShortId}
          </Typography>
        </Box>
        <Divider />
        <Box className="trace-drawer__body">
          {traceDrawerSections.length > 0 ? traceDrawerSections.map((section) => (
            <Box key={section.title} className="trace-drawer__section">
              <Box className="trace-drawer__section-head">
                <Typography variant="caption" className="trace-label">
                  {section.title}
                </Typography>
                <span className={`trace-drawer__owner-badge trace-drawer__owner-badge--${latestOwnerTone}`}>
                  {section.title === 'Runtime Snapshot' ? latestOwnerLabel : 'trace node'}
                </span>
              </Box>
              <pre className="trace-pre">{JSON.stringify(section.payload, null, 2)}</pre>
            </Box>
          )) : (
            <Typography variant="body2" className="semantic-hud__empty">
              Для этого треда ещё нет свежего trace.
            </Typography>
          )}
        </Box>
      </Drawer>

      <AppBar position="static" elevation={0} className="app-topbar">
        <Toolbar className="app-toolbar">
          <IconButton edge="start" color="inherit" onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography className="app-brand-kicker">
              Fractal Core
            </Typography>
            <Typography variant="h5" className="app-brand-title">
              Continuation Cockpit
            </Typography>
          </Box>
          <Box className="status-strip">
            <span className="status-pill">
              <span className="status-pill__label">API</span>
              <span className="status-pill__value" style={{ color: serverLampColor }}>{serverLampText}</span>
            </span>
            <span className="status-pill">
              <span className="status-pill__label">Runner</span>
              <span className="status-pill__value" style={{ color: runnerLampColor }}>{runnerLampText}</span>
            </span>
            <span className="status-pill">
              <span className="status-pill__label">Sequence</span>
              <span className="status-pill__value" style={{ color: sequenceLampColor }}>{sequenceStatusText}</span>
            </span>
            <span className="status-pill">
              <span className="status-pill__label">Tick</span>
              <span className="status-pill__value" style={{ color: tickLampColor }}>{tickLampText}</span>
            </span>
            <span className="status-pill status-pill--wide">
              <span className="status-pill__label">Model</span>
              <span className="status-pill__value status-pill__value--mono">
                {(activeEngineModel || selectedModel || 'n/a').slice(0, 18)}
                {lastTickModel && lastTickModel !== (activeEngineModel || selectedModel)
                  ? ` / ${String(lastTickModel).slice(0, 14)}`
                  : ''}
                {engineSwitching ? ' / switch…' : ''}
              </span>
            </span>
          </Box>
          <IconButton color="inherit" onClick={() => setView('ocm')} title="OCM Timeline" style={{ fontSize: 14, fontWeight: 700, borderRadius: 6, padding: '4px 8px' }}>
            OCM
          </IconButton>
          <IconButton color="inherit" onClick={() => setView('demo')} title="Demo Stand">
            <SwapHorizIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        autoscroll={autoscroll} setAutoscroll={setAutoscroll}
        darkMode={darkMode} setDarkMode={setDarkMode}
        handleClearHistory={() => {
          updateActiveHistory([]);
        }}
        handleClearHistoryServer={async () => {
          try {
            await axios.post(`${API_BASE}/clear_history`);
            updateActiveHistory([]);
          } catch (e) {
            alert('Ошибка при очистке на сервере');
          }
        }}
        handleDownloadHistory={() => {
          const payload = {
            thread_id: activeThreadId,
            title: activeThread?.title || '',
            history
          };
          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'history.json';
          link.click();
        }}
        handleUploadHistory={() => {}}
        selectedModel={selectedModel} setSelectedModel={setSelectedModel}
        activeEngineModel={activeEngineModel}
        engineSwitching={engineSwitching}
        onSwitchEngine={handleSwitchEngine}
        modelsList={modelsList} setModelsList={setModelsList}
        username={username} setUsername={setUsername}
        authAccount={authAccount}
        authBusy={authBusy}
        authBootstrapRequired={authBootstrapRequired}
        authError={authError}
        managedAccounts={managedAccounts}
        onAuthLogin={handleAuthLogin}
        onAuthBootstrap={handleAuthBootstrap}
        onAuthLogout={handleAuthLogout}
        onAuthCreateAccount={handleAuthCreateAccount}
        onRefreshServerChats={async () => {
          if (!authAccount) return;
          await syncServerChats({ requestedActive: activeThreadId });
        }}
        statusServer={statusServer}
        onImageUpload={setImageFile} imageFile={imageFile}
        debugPrompt={debugPrompt}
        runnerMode={runnerMode} setRunnerMode={setRunnerMode}
        runnerInterval={runnerInterval} setRunnerInterval={setRunnerInterval}
        lastRunnerStatus={lastRunnerStatus}
        paused={paused} setPaused={setPaused}
        runnerMaxCycles={runnerMaxCycles} setRunnerMaxCycles={setRunnerMaxCycles}
        runnerCycleCount={runnerCycleCount} setRunnerCycleCount={setRunnerCycleCount}
        runnerSequenceStatus={runnerSequenceStatus}
        onStartSequence={handleStartSequence}
        onStopSequence={handleStopSequence}
        filterDuplicates={filterDuplicates} setFilterDuplicates={setFilterDuplicates}
        runnerBusy={tickBusy}
        runnerSettingsHydrated={runnerSettingsHydrated}
        activeThreadId={activeThreadId}
        taskStats={taskStats}
        onRunExecutor={runExecutorOnce}
        onQuickVerify={async (text) => {
          const q = normalizeTaskQuery(text);
          if (!q) return;
          await resolveManualTask(q, { threadId: activeThreadId });
        }}
        onRefreshTasks={refreshTaskStats}
        onEnqueueTask={enqueueTask}
        onManualTick={handleRunTick}
      />

      <Paper elevation={0} className="workspace-panel" sx={{
        p: 3,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        mt: 2,
        bgcolor: 'transparent'
      }}>
        <Box className="workspace-grid" sx={{ display: 'flex', gap: 2, flexGrow: 1, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box className="thread-rail" sx={{
            width: { xs: '100%', md: 220 },
            pr: { xs: 0, md: 2 },
            pb: { xs: 1, md: 0 },
            borderRight: { xs: 'none', md: '1px solid var(--panel-line)' },
            borderBottom: { xs: '1px solid var(--panel-line)', md: 'none' }
          }}>
            <Typography className="rail-caption">Потоки</Typography>
            <Box className="operator-card">
              <Typography variant="caption" className="operator-card__kicker">
                Operator
              </Typography>
              <Typography variant="h6" className="operator-card__name">
                {operatorLabel}
              </Typography>
              <Box className="operator-card__meta">
                <span>Режим: {runnerLampText}</span>
                <span>Потоков: {threads.filter((t) => t.id !== RUNTIME_THREAD_ID).length}</span>
              </Box>
            </Box>
            <Button variant="outlined" fullWidth onClick={handleNewThread} sx={{ borderRadius: 999, mb: 1.2 }}>
              + Собеседник
            </Button>
            <Button
              color="error"
              variant="text"
              fullWidth
              onClick={handleDeleteActiveThread}
              disabled={!activeThreadId || activeThreadId === RUNTIME_THREAD_ID}
              sx={{ mt: 0.5, borderRadius: 999 }}
            >
              Удалить окно
            </Button>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1, mt: 2, overflowX: { xs: 'auto', md: 'visible' } }}>
              {normalThreads.map((t) => (
                <Box
                  key={t.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    minWidth: { xs: 180, md: 'auto' },
                    gap: 0.5,
                  }}
                >
                  <Button
                    variant={t.id === activeThreadId ? 'contained' : 'text'}
                    onClick={() => setActiveThreadId(t.id)}
                    sx={{
                      justifyContent: 'flex-start',
                      flexGrow: 1,
                      borderRadius: 3,
                      px: 1.4,
                      py: 1.2,
                      textTransform: 'none',
                      background: t.id === activeThreadId ? 'var(--thread-active-bg)' : 'transparent',
                      color: t.id === activeThreadId ? 'var(--thread-active-fg)' : 'var(--muted-ink)',
                      border: t.id === activeThreadId ? '1px solid transparent' : '1px solid var(--thread-line)'
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden' }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                        {buildThreadDisplayTitle(t)}
                      </span>
                      <span style={{ fontSize: '0.74rem', opacity: 0.72, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                        {buildThreadMetaLabel(t)}
                      </span>
                    </Box>
                  </Button>
                  <IconButton
                    aria-label="Удалить окно"
                    onClick={() => handleDeleteActiveThread(t.id)}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid var(--thread-line)',
                      color: 'var(--muted-ink)',
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button
              variant={activeThreadId === RUNTIME_THREAD_ID ? 'contained' : 'outlined'}
              fullWidth
              onClick={() => setActiveThreadId(RUNTIME_THREAD_ID)}
              sx={{ mt: 1.5, borderRadius: 999 }}
            >
              ⚙ Runtime Monitor
            </Button>
          </Box>

          <Box className="chat-stage" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Box className="continuity-deck">
              <Box className="continuity-card continuity-card--identity">
                <Typography variant="caption" className="section-kicker">
                  Active Window
                </Typography>
                <Typography variant="body1" className="continuity-card__title">
                  {buildThreadDisplayTitle(activeThread)}
                </Typography>
                <Box className="continuity-card__meta">
                  <span>ID {threadShortId}</span>
                  <span>{activeThreadId === RUNTIME_THREAD_ID ? buildThreadMetaLabel(activeThread) : `speaker: ${activeParticipant.name}`}</span>
                </Box>
              </Box>
              <Box className={`continuity-card continuity-card--health continuity-card--${continuityHealth.tone}`}>
                <Typography variant="caption" className="section-kicker">
                  Thread Health
                </Typography>
                <Typography variant="body1" className="continuity-card__title continuity-card__title--caps">
                  {continuityHealth.label}
                </Typography>
                <Box className="continuity-card__meta">
                  <span>{continuityHealth.detail}</span>
                </Box>
              </Box>
              <Box className="continuity-card">
                <Typography variant="caption" className="section-kicker">
                  Semantic Owner
                </Typography>
                <Box className="continuity-card__owner-row">
                  <Typography variant="body1" className="continuity-card__title continuity-card__title--mono">
                    {latestContinuityOwner}
                  </Typography>
                  <span className={`owner-badge owner-badge--${latestOwnerTone}`}>
                    {latestOwnerLabel}
                  </span>
                </Box>
                <Box className="continuity-card__meta">
                  <span>{latestWinner ? `winner: ${latestWinner}` : 'winner: n/a'}</span>
                </Box>
              </Box>
              <Box className={`continuity-card continuity-card--tool continuity-card--${latestToolTone}`}>
                <Typography variant="caption" className="section-kicker">
                  Tool Branch
                </Typography>
                <Box className="continuity-card__owner-row">
                  <Typography variant="body1" className="continuity-card__title continuity-card__title--mono">
                    {latestToolDecision || 'idle'}
                  </Typography>
                  <span className={`owner-badge owner-badge--${latestToolTone}`}>
                    {latestToolLabel}
                  </span>
                </Box>
                <Box className="continuity-card__meta">
                  <span>{latestToolQuestion ? latestToolQuestion : 'preflight idle'}</span>
                </Box>
              </Box>
              <Box className={`continuity-card continuity-card--wide continuity-card--owner continuity-card--${latestOwnerTone}`}>
                <Typography variant="caption" className="section-kicker">
                  Carry Snapshot
                </Typography>
                <Typography variant="body1" className="continuity-card__title">
                  {latestContinuityMove || 'Пока без зафиксированного carry move'}
                </Typography>
                <Box className="continuity-card__meta">
                  <span>{latestContinuityReason ? `reason: ${latestContinuityReason}` : 'reason: idle'}</span>
                </Box>
              </Box>
            </Box>
            <Box className="semantic-hud">
              <Box className="semantic-hud__panel">
                <Typography variant="caption" className="section-kicker">
                  Semantic Mini-Timeline
                </Typography>
                <Box className="semantic-timeline">
                  {semanticTimeline.length > 0 ? semanticTimeline.map((item) => (
                    <Box key={item.id} className={`timeline-chip timeline-chip--${item.tone}`} title={item.move}>
                      <span className="timeline-chip__owner">{item.owner}</span>
                      <span className="timeline-chip__move">{item.move}</span>
                    </Box>
                  )) : (
                    <Typography variant="body2" className="semantic-hud__empty">
                      Пока нет зафиксированной цепочки carry moves.
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="semantic-hud__panel semantic-hud__panel--inspector">
                <Typography variant="caption" className="section-kicker">
                  Inspector
                </Typography>
                <Box className="inspector-grid">
                  <Box className="inspector-card">
                    <span className="inspector-card__label">Tool</span>
                    <strong>{latestToolDecision || 'idle'}</strong>
                    <span>{latestToolBranch !== 'none' ? `${latestToolBranch} branch` : 'no tool branch'}</span>
                  </Box>
                  <Box className="inspector-card">
                    <span className="inspector-card__label">L1 Reader</span>
                    <strong>{latestL1ReaderSource || 'idle'}</strong>
                    <span>{latestL1ReaderElided ? 'raw signal elided' : 'raw signal kept'}</span>
                  </Box>
                  <Box className="inspector-card">
                    <span className="inspector-card__label">L1 Lane</span>
                    <strong>{latestL1LaneSource || 'idle'}</strong>
                    <span>{latestL1LaneObject ? `${latestL1LaneObject}${latestL1LaneTerminal ? ` / ${latestL1LaneTerminal}` : ''}${latestL1LaneClose ? ` / ${latestL1LaneClose}` : ''}` : 'no lane packet'}</span>
                  </Box>
                  <Box className={`inspector-card inspector-card--lamp inspector-card--${monitorL2Tone}`}>
                    <span className="inspector-card__label">L2</span>
                    <strong data-testid="l2-inspector-lamp" title={monitorL2Source || 'L2 tactical advisory status'}>
                      <span aria-hidden="true" />
                      {monitorL2Label}
                    </strong>
                    <span>
                      {monitorL2AdvisoryN > 0 ? `adv ${monitorL2AdvisoryN}` : 'adv 0'}
                      {monitorL2ShadowN > 0 ? ` / shadow ${monitorL2ShadowN}` : ''}
                    </span>
                  </Box>
                  <Box className="inspector-card">
                    <span className="inspector-card__label">SUTURE</span>
                    <strong>{String(sutureMeta.reason_code || 'idle')}</strong>
                    <span>{sutureMeta.packet_valid ? 'packet valid' : 'no packet'}</span>
                  </Box>
                  <Box className="inspector-card">
                    <span className="inspector-card__label">RIVET</span>
                    <strong>{String(rivetMeta.reason_code || latestRuntime.rivet_task_reason || 'idle')}</strong>
                    <span>{latestRuntime.rivet_task_seed ? 'task frozen' : 'no task seed'}</span>
                  </Box>
                  <Box className="inspector-card">
                    <span className="inspector-card__label">OHB</span>
                    <strong>{String(ohbMeta.decision || latestRuntime.ohb_decision || 'idle')}</strong>
                    <span>{latestRuntime.ohb_dialogue_move_candidate ? 'dialogue carry' : 'no carry move'}</span>
                  </Box>
                </Box>
                <Box sx={{ mt: 1.2 }}>
                  <Button variant="outlined" size="small" sx={{ borderRadius: 999 }} onClick={() => setTraceDrawerOpen(true)}>
                    Open Trace Drawer
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box className="runtime-monitor-shell" data-testid="runtime-monitor-shell">
              <Box className="runtime-monitor-shell__head">
                <Box>
                  <Typography variant="caption" className="section-kicker">
                    Runtime Monitor
                  </Typography>
                  <Typography variant="body2" className="section-title">
                    OCM time / потоки
                  </Typography>
                </Box>
                <Box className="ocm-monitor__head">
                  <span className="ocm-monitor__time">
                    {latestRuntimeMonitor?.current_ocm_label || 'OCM n/a'}
                  </span>
                  {monitorThreadId ? (
                    <span className="ocm-monitor__meta">
                      thread: {monitorThreadId.slice(0, 42)}
                    </span>
                  ) : null}
                  {monitorParticipantName ? (
                    <span className="ocm-monitor__meta">
                      participant: {monitorParticipantName}
                    </span>
                  ) : null}
                  {monitorRuntime?.ocm_focused_id ? (
                    <span className="ocm-monitor__meta">
                      ocm_ref={_formatOcmFocusRef(monitorRuntime.ocm_focused_id)}
                    </span>
                  ) : null}
                  {monitorRuntime?.ocm_phase ? (
                    <span className="ocm-monitor__meta">
                      phase: {monitorRuntime.ocm_phase}
                    </span>
                  ) : null}
                  {monitorRuntime?.ocm_focus_last_seen_tick !== undefined ? (
                    <span className="ocm-monitor__meta">
                      seen tick: {monitorRuntime.ocm_focus_last_seen_tick}
                    </span>
                  ) : null}
                  {monitorRuntime?.ocm_focus_age_ticks !== undefined ? (
                    <span className="ocm-monitor__meta">
                      age: {monitorRuntime.ocm_focus_age_ticks}
                    </span>
                  ) : null}
                  {runnerSequenceVisible ? (
                    <span className="ocm-monitor__meta">
                      seq: {runnerSequenceCurrentStep > 0 ? runnerSequenceCurrentStep : runnerSequenceCompletedSteps}/{runnerSequenceMaxSteps || runnerSequenceSteps.length || 'n/a'}
                    </span>
                  ) : null}
                  {monitorRuntime?.ocm_registry_n !== undefined ? (
                    <span className="ocm-monitor__meta">
                      registry: {monitorRuntime.ocm_registry_n}
                    </span>
                  ) : null}
                  {monitorRetainedRegistryN !== undefined ? (
                    <span className="ocm-monitor__meta">
                      retained registry: {monitorRetainedRegistryN}
                    </span>
                  ) : null}
                  {monitorRuntime?.tcg_node_n !== undefined ? (
                    <span className="ocm-monitor__meta">
                      chain: {monitorRuntime.tcg_node_n}/{monitorRuntime.tcg_edge_n ?? 0}
                    </span>
                  ) : null}
                  {monitorRuntime?.query_source ? (
                    <span className="ocm-monitor__meta">
                      qsrc: {monitorRuntime.query_source}
                    </span>
                  ) : null}
                  {latestRuntimeMonitor?.current?.foreground_flow_id ? (
                    <span className="ocm-monitor__meta">
                      current flow: {latestRuntimeMonitor.current.foreground_flow_id}
                    </span>
                  ) : null}
                  <span className="ocm-monitor__meta">
                    flows: {latestRuntimeMonitor?.current?.flow_count ?? latestRuntimeMonitorFlows.length}
                  </span>
                  {monitorL1ContractVisible ? (
                    <span className="ocm-monitor__meta">
                      l1 contract: req={monitorL1ContractRequiredText || 'n/a'} valid={monitorL1ContractValidText || 'n/a'}
                    </span>
                  ) : null}
                  {monitorL1ContractVisible ? (
                    <span className="ocm-monitor__meta">
                      l1 gap: {monitorL1ContractGapN}
                    </span>
                  ) : null}
                  {monitorL1ContractDecision ? (
                    <span className="ocm-monitor__meta">
                      l1 decision: {monitorL1ContractDecision}
                    </span>
                  ) : null}
                  <span
                    className={`ocm-monitor__indicator ocm-monitor__indicator--${monitorL2Tone}`}
                    data-testid="l2-monitor-lamp"
                    title={monitorL2Source || 'L2 tactical advisory status'}
                  >
                    <span aria-hidden="true" />
                    <strong>L2 {monitorL2Label}</strong>
                    <span>
                      {monitorL2AdvisoryN > 0 ? `adv ${monitorL2AdvisoryN}` : 'adv 0'}
                      {monitorL2ShadowN > 0 ? ` / shadow ${monitorL2ShadowN}` : ''}
                    </span>
                  </span>
                </Box>
              </Box>
              <div className="ocm-monitor__tabs" role="tablist" aria-label="runtime monitor tabs">
                {visibleRuntimeMonitorTabs.map((tab) => {
                  const tabId = String(tab.id || '').trim() || 'flows';
                  const selected = activeRuntimeMonitorTab === tabId;
                  return (
                    <button
                      key={`runtime-monitor-top-tab-${tabId}`}
                      type="button"
                      className={`ocm-monitor__tab${selected ? ' is-active' : ''}`}
                      onClick={() => setRuntimeMonitorTab(tabId)}
                      aria-pressed={selected}
                    >
                      {tab.label || tabId}
                      {tab.count !== undefined ? <span>{tab.count}</span> : null}
                    </button>
                  );
                })}
              </div>
              {activeRuntimeMonitorTab === 'flows' ? (
                <div className="ocm-flow-list ocm-flow-list--top">
                  {latestRuntimeMonitorFlows.length > 0 ? latestRuntimeMonitorFlows.map((flow) => {
                    const detail = buildMonitorFlowDetail(monitorTrace, flow);
                    return (
                      <details
                        key={`runtime-monitor-top-flow-${flow.number || flow.flow_id}`}
                        className={`ocm-flow-row${flow.is_current ? ' is-current' : ''}`}
                        open={!!flow.is_current}
                      >
                        <summary className="ocm-flow-row__summary">
                          <span className="ocm-flow-row__head">
                            <span className="ocm-flow-row__number">#{flow.number || '?'}</span>
                            <span className="ocm-flow-row__id">{flow.flow_id_short || flow.flow_id || 'flow'}</span>
                            {flow.lane ? <span className="ocm-flow-row__badge">{flow.lane}</span> : null}
                            {flow.last_processed_ocm !== undefined && flow.last_processed_ocm !== null ? (
                              <span className="ocm-flow-row__ocm">last OCM {flow.last_processed_ocm}</span>
                            ) : null}
                            {flow.needs_l1_now ? <span className="ocm-flow-row__badge">L1</span> : null}
                          </span>
                          <span className="ocm-flow-row__content">
                            {(() => { const t = flow.content || flow.summary || ''; return t.length > 120 ? t.slice(0, 120) + '…' : (t || 'no content'); })()}
                          </span>
                          <span className="ocm-flow-row__foot">
                            {flow.flow_kind ? <span>{flow.flow_kind}</span> : null}
                            {flow.lifecycle_state ? <span>{flow.lifecycle_state}</span> : null}
                            {flow.origin_ocm !== undefined && flow.origin_ocm !== null ? <span>origin OCM {flow.origin_ocm}</span> : null}
                            {flow.age_ocm !== undefined && flow.age_ocm !== null ? <span>age {flow.age_ocm}</span> : null}
                          </span>
                        </summary>
                        <div className="ocm-flow-row__details">
                          {(flow.content || flow.summary) ? (
                            <div className="ocm-flow-row__detail-section">
                              <strong>Контент</strong>
                              <div className="ocm-flow-history__fulltext">{flow.content || flow.summary}</div>
                            </div>
                          ) : null}
                          {flow.history && flow.history.length > 0 ? (
                            <div className="ocm-flow-row__detail-section">
                              <strong>История OCM</strong>
                              <div className="ocm-flow-history">
                                {flow.history.map((ev, i) => (
                                  <div key={`runtime-monitor-flow-${flow.flow_id}-hist-${i}`} className="ocm-flow-history__item">
                                    {ev.ocm_id !== undefined && ev.ocm_id !== null ? (
                                      <span className="ocm-flow-history__ocm">#{ev.ocm_id}</span>
                                    ) : null}
                                    {ev.lane ? <span className="ocm-flow-history__lane">{ev.lane}</span> : null}
                                    <span className="ocm-flow-history__text">{ev.summary || '—'}</span>
                                    {ev.decision ? <span className="ocm-flow-history__cmd">{ev.decision}</span> : null}
                                    {ev.flow_cmd && ev.flow_cmd !== ev.decision ? (
                                      <span className="ocm-flow-history__cmd">{ev.flow_cmd}</span>
                                    ) : null}
                                    {ev.next_ocm ? <span className="ocm-flow-history__cmd ocm-flow-history__cmd--next">{ev.next_ocm}</span> : null}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          {detail.meta.length > 0 ? (
                            <div className="ocm-flow-row__detail-grid">
                              {detail.meta.map((line, i) => (
                                <span key={`runtime-monitor-flow-${flow.flow_id}-meta-${i}`}>{line}</span>
                              ))}
                            </div>
                          ) : null}
                          {detail.sections.length > 0 ? detail.sections.map((section) => (
                            <div
                              className="ocm-flow-row__detail-section"
                              key={`runtime-monitor-flow-${flow.flow_id}-${section.title}`}
                            >
                              <strong>{section.title}</strong>
                              {section.lines.map((line, i) => (
                                <div key={`runtime-monitor-flow-${flow.flow_id}-${section.title}-${i}`}>- {line}</div>
                              ))}
                            </div>
                          )) : (
                            <div className="ocm-flow-row__detail-section">
                              <strong>Details</strong>
                              <div>- no detailed flow payload in current trace</div>
                            </div>
                          )}
                        </div>
                      </details>
                    );
                  }) : (
                    <Typography variant="body2" className="runtime-monitor-shell__empty">
                      Нет свежего OCM trace для активного окна.
                    </Typography>
                  )}
                </div>
              ) : null}
              {activeRuntimeMonitorTab === 'chain' ? (
                <div className="trace-card__body">
                  {latestOperatorDigest.chain.length > 0 ? latestOperatorDigest.chain.map((step, i) => (
                    <div key={`runtime-monitor-top-chain-${i}`}>
                      {step?.step || i + 1}. {step?.owner || 'unknown'}: {step?.status || 'unknown'} - {step?.summary || ''}
                    </div>
                  )) : <div>Нет свежей цепочки.</div>}
                </div>
              ) : null}
              {activeRuntimeMonitorTab === 'status' ? (
                <div className="trace-card__body">
                  {latestOperatorDigest.contourStatus.length > 0 ? latestOperatorDigest.contourStatus.map((item, i) => (
                    <div key={`runtime-monitor-top-status-${i}`}>
                      {item?.contour || 'unknown'}: {item?.state || 'unknown'} - {item?.proof || ''}
                    </div>
                  )) : <div>Нет свежего статуса контуров.</div>}
                </div>
              ) : null}
              {activeRuntimeMonitorTab === 'gaps' ? (
                <div className="trace-card__body">
                  {latestOperatorDigest.openGaps.length > 0 ? latestOperatorDigest.openGaps.map((line, i) => (
                    <div key={`runtime-monitor-top-gap-${i}`}>• {line}</div>
                  )) : <div>Нет зафиксированных разрывов.</div>}
                </div>
              ) : null}
              {activeRuntimeMonitorTab === 'sequence' ? (
                <div className="ocm-sequence-timeline" data-testid="ocm-sequence-timeline">
                  <div className="road-test-controls">
                    <div className="road-test-controls__copy">
                      <strong>Ходовые испытания</strong>
                      <span>Один твой стартовый сигнал, дальше система идёт сама. Без подсказанных промежуточных ходов.</span>
                    </div>
                    <TextField
                      size="small"
                      label="Стартовая ситуация или задача"
                      value={roadTestSeed}
                      onChange={(e) => setRoadTestSeed(e.target.value)}
                      disabled={!!runnerSequenceStatus?.running}
                      inputProps={{ 'data-testid': 'road-test-seed' }}
                      sx={{ minWidth: 300, flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="OCM"
                      value={roadTestSteps}
                      onChange={(e) => setRoadTestSteps(Math.max(1, Math.min(10, Number(e.target.value || 1))))}
                      inputProps={{ min: 1, max: 10, 'data-testid': 'road-test-steps' }}
                      sx={{ width: 92 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Пауза OCM, сек"
                      value={roadTestInterval}
                      onChange={(e) => setRoadTestInterval(Math.max(0, Math.min(60, Number(e.target.value || 0))))}
                      disabled={!!runnerSequenceStatus?.running}
                      inputProps={{ min: 0, max: 60, step: 0.5, 'data-testid': 'road-test-interval' }}
                      helperText="0 = сразу"
                      sx={{ width: 142 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleStartRoadTest}
                      disabled={!roadTestSeed.trim() || !!runnerSequenceStatus?.running || tickBusy}
                    >
                      ▶ Старт
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleStopSequence}
                      disabled={!runnerSequenceStatus?.running}
                    >
                      ■ Стоп
                    </Button>
                  </div>
                  <div className="ocm-sequence-timeline__meta">
                    <span>status={runnerSequenceStatus?.status || 'idle'}</span>
                    <span>steps={Number(runnerSequenceStatus?.completed_steps || 0)}/{Number(runnerSequenceStatus?.max_steps || 0)}</span>
                    {runnerSequenceStatus?.thread_id ? <span>thread={String(runnerSequenceStatus.thread_id).slice(0, 42)}</span> : null}
                    {runnerSequenceStatus?.stop_reason ? <span>stop={runnerSequenceStatus.stop_reason}</span> : null}
                    {runnerSequenceStatus?.error ? <span>error={runnerSequenceStatus.error}</span> : null}
                  </div>
                  {runnerSequenceSteps.length > 0 ? runnerSequenceSteps.map((step) => (
                    <div
                      key={`runtime-monitor-sequence-step-${step.key}`}
                      className={`ocm-sequence-step is-${step.status || 'pending'}`}
                    >
                      <span className="ocm-sequence-step__number">#{step.number}</span>
                      <span className="ocm-sequence-step__state">{step.status || 'pending'}</span>
                      {step.ocm_ordinal !== null ? <span>OCM {step.ocm_ordinal}</span> : null}
                      {step.ocm_id ? <span>ocm_ref={_formatOcmFocusRef(step.ocm_id)}</span> : null}
                      {step.query_source ? <span>qsrc={step.query_source}</span> : null}
                      {step.stop_reason ? <span>stop={step.stop_reason}</span> : null}
                      {step.terminal_kind ? <span>terminal={step.terminal_kind}</span> : null}
                      {step.error ? <span>error={step.error}</span> : null}
                      {step.has_user_signal ? <span>user-signal</span> : null}
                      {step.has_explicit_query ? <span>explicit-query</span> : null}
                      {step.reply_head ? <strong>{step.reply_head}</strong> : null}
                      {step.road?.packet_version ? (
                        <div className={`road-test-step road-test-step--${step.road.phase || 'idle'}`}>
                          <span className="road-test-step__phase">{step.road.phase || 'idle'}</span>
                          {step.road.foreground_flow_id ? <span>flow={step.road.foreground_flow_id}</span> : null}
                          {step.road.semantic_delta ? <span className="road-test-step__wide">мысль: {step.road.semantic_delta}</span> : null}
                          {step.road.next_move_kind ? <span>ход={step.road.next_move_kind}</span> : null}
                          {step.road.flow_fate ? <span>fate={step.road.flow_fate}</span> : null}
                          {step.road.capability_id ? <span>орган={step.road.capability_id}</span> : null}
                          {step.road.action_instruction ? <span className="road-test-step__wide">эксперимент: {step.road.action_instruction}</span> : null}
                          {step.road.action_id ? <span>action={step.road.action_id}</span> : null}
                          {step.road.capability_id ? <span>payload={step.road.payload_present ? (step.road.payload_keys.join(', ') || 'present') : 'EMPTY'}</span> : null}
                          {step.road.branch_plan_status ? <span>branch-plan={step.road.branch_plan_status}</span> : null}
                          {step.road.payload_fingerprint ? <span>payload-fp={step.road.payload_fingerprint}</span> : null}
                          {Object.keys(step.road.payload_preview || {}).length > 0 ? (
                            <span className="road-test-step__wide">параметры: {JSON.stringify(step.road.payload_preview)}</span>
                          ) : null}
                          {step.road.branch_plan_missing_inputs.length > 0 ? (
                            <span className="road-test-step__wide road-test-step__reject">не дошло из L1: {step.road.branch_plan_missing_inputs.join(', ')}</span>
                          ) : null}
                          {step.road.action_status ? <span>action-status={step.road.action_status}</span> : null}
                          {step.road.observation_summary ? <span className="road-test-step__wide">наблюдение: {step.road.observation_summary}</span> : null}
                          {step.road.observation_files.length > 0 ? <span className="road-test-step__wide">прочитано: {step.road.observation_files.join(', ')}</span> : null}
                          {step.road.evidence_ids.length > 0 ? <span className="road-test-step__wide">evidence: {step.road.evidence_ids.join(', ')}</span> : null}
                          {step.road.l4_match ? <span>L4={step.road.l4_match}</span> : null}
                          {step.road.l4_prediction_error ? <span className="road-test-step__wide">ошибка прогноза: {step.road.l4_prediction_error}</span> : null}
                          {step.road.contract_gap_n > 0 ? <span>contract-gap={step.road.contract_gap_n}</span> : null}
                          {step.road.rejection_reason ? <span className="road-test-step__wide road-test-step__reject">стоп: {step.road.rejection_reason}</span> : null}
                        </div>
                      ) : null}
                    </div>
                  )) : (
                    <Typography variant="body2" className="runtime-monitor-shell__empty">
                      Sequence ещё не отдаёт шаги для текущего окна.
                    </Typography>
                  )}
                </div>
              ) : null}
            </Box>
            <Box className="task-ribbon" sx={{
              mb: 1,
              p: 1,
              borderRadius: 2,
              background: 'transparent',
              border: '1px solid var(--panel-line)'
            }}>
              <Typography variant="caption" className="section-kicker" sx={{ display: 'block', mb: 0.5 }}>
                Executor Ribbon
              </Typography>
              <Typography variant="body2" className="section-title" sx={{ mb: 1 }}>
                Быстрая факт-проверка и evidence snapshot
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Введи запрос для проверки…"
                  value={taskQuick}
                  onChange={(e) => setTaskQuick(e.target.value)}
                  sx={{ minWidth: 280, flex: 1 }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={taskBusy || !taskQuick.trim()}
                  onClick={async () => {
                    const q = normalizeTaskQuery(taskQuick);
                    if (!q) return;
                    setTaskQuick('');
                    await resolveManualTask(q, { threadId: activeThreadId });
                  }}
                >
                  Проверить
                </Button>
              </Box>
              {taskStatus && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
                  {taskStatus}
                </Typography>
              )}
              {lastEvidence.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  {lastEvidence.slice(0, 3).map((ev, idx) => (
                    <Box key={idx} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        • {ev.title || ev.url}
                      </Typography>
                      {ev.url && (
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                          {ev.url}
                        </Typography>
                      )}
                      {ev.snippet && (
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                          ↳ {ev.snippet.slice(0, 180)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {hasCollapsedPendingQuestions ? (
              <Box sx={{ mb: 1 }} className="pending-banner">
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => setUiDialogOpen(true)}
                >
                  {`Вопросы от системы: ${uiPendingItems.length} (развернуть)`}
                </Button>
              </Box>
            ) : null}
            {activeThreadId !== RUNTIME_THREAD_ID && liveTraceStatus?.runtimePhase ? (
              <Box
                className={`cycle-phase-panel is-${liveTraceStatus.runtimePhase.status}`}
                data-testid="cycle-phase-panel"
              >
                <Box className="cycle-phase-panel__head">
                  <Box>
                    <Typography variant="caption" className="section-kicker">
                      Фазы ОЦМ
                    </Typography>
                    <Typography variant="body2" className="cycle-phase-panel__headline">
                      {liveTraceStatus.runtimePhase.status === 'running' ? 'Сейчас' : 'Состояние'}: {liveTraceStatus.runtimePhase.phaseLabel}
                    </Typography>
                  </Box>
                  <Box className="cycle-phase-panel__meta">
                    {liveTraceStatus.participantName ? (
                      <span>участник {liveTraceStatus.participantName}</span>
                    ) : null}
                    {liveTraceStatus.runtimePhase.elapsedS > 0 ? (
                      <span>{Math.round(liveTraceStatus.runtimePhase.elapsedS)}s</span>
                    ) : null}
                    {liveTraceStatus.runtimePhase.timeoutS > 0 ? (
                      <span>limit {Math.round(liveTraceStatus.runtimePhase.timeoutS)}s</span>
                    ) : null}
                    {liveTraceStatus.runtimeStageLastError ? (
                      <span>error</span>
                    ) : null}
                  </Box>
                </Box>
                <Box className="cycle-phase-rail" role="list" aria-label="OCM cycle phase">
                  {liveTraceStatus.runtimePhase.steps.map((step) => (
                    <Box
                      key={`ocm-phase-${step.key}`}
                      className={`cycle-phase-step is-${step.state}`}
                      role="listitem"
                    >
                      <span className="cycle-phase-step__dot" />
                      <span className="cycle-phase-step__label">{step.label}</span>
                    </Box>
                  ))}
                </Box>
                {(liveTraceStatus.runtimeStageHeadline || liveTraceStatus.runtimePhase.detail || liveTraceStatus.runtimeStageLastError) ? (
                  <Typography variant="body2" className="cycle-phase-panel__detail">
                    {liveTraceStatus.runtimeStageLastError || liveTraceStatus.runtimeStageHeadline || liveTraceStatus.runtimePhase.detail}
                    {liveTraceStatus.runtimeStageDetail ? `, ${liveTraceStatus.runtimeStageDetail}` : ''}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            {traceDebugEnabled && activeThreadId !== RUNTIME_THREAD_ID && liveTraceStatus ? (
              <Box className={`online-trace-panel${liveTraceStatus.tickActive ? ' is-live' : ''}`} data-testid="online-trace-panel">
                <Box className="online-trace-panel__head">
                  <Typography variant="caption" className="section-kicker">
                    Online Trace
                  </Typography>
                  <Typography variant="body2" className="online-trace-panel__headline">
                    {liveTraceStatus.headline}
                  </Typography>
                </Box>
                <Box className="online-trace-panel__chips">
                  {liveTraceStatus.participantName ? <span className="online-trace-chip">participant: {liveTraceStatus.participantName}</span> : null}
                  {liveTraceStatus.owner ? <span className="online-trace-chip">owner: {liveTraceStatus.owner}</span> : null}
                  {liveTraceStatus.mode ? <span className="online-trace-chip">mode: {liveTraceStatus.mode}</span> : null}
                  {liveTraceStatus.runtimeStagePacketVersion ? <span className="online-trace-chip">stage-packet: {liveTraceStatus.runtimeStagePacketVersion}</span> : null}
                  {liveTraceStatus.runtimeStageContour ? <span className="online-trace-chip">contour: {liveTraceStatus.runtimeStageContour}</span> : null}
                  {liveTraceStatus.runtimeStageRole ? <span className="online-trace-chip">role: {liveTraceStatus.runtimeStageRole}</span> : null}
                  {liveTraceStatus.runtimeStageProcess ? <span className="online-trace-chip">process: {liveTraceStatus.runtimeStageProcess}</span> : null}
                  {liveTraceStatus.runtimeStageAttempt ? <span className="online-trace-chip">attempt: {liveTraceStatus.runtimeStageAttempt}</span> : null}
                  {liveTraceStatus.runtimeStageElapsedS > 0 ? <span className="online-trace-chip">stage-age: {Math.round(liveTraceStatus.runtimeStageElapsedS)}s</span> : null}
                  {liveTraceStatus.runtimeStageTimeoutS > 0 ? <span className="online-trace-chip">stage-timeout: {Math.round(liveTraceStatus.runtimeStageTimeoutS)}s</span> : null}
                  {liveTraceStatus.routeFamily ? <span className="online-trace-chip">route: {liveTraceStatus.routeFamily}</span> : null}
                  {liveTraceStatus.selectedSource ? <span className="online-trace-chip">selected: {liveTraceStatus.selectedSource}</span> : null}
                  {liveTraceStatus.toolDecision ? <span className="online-trace-chip">tool: {liveTraceStatus.toolDecision}</span> : null}
                  {liveTraceStatus.toolBranch ? <span className="online-trace-chip">branch: {liveTraceStatus.toolBranch}</span> : null}
                  {liveTraceStatus.turnType ? <span className="online-trace-chip">turn: {liveTraceStatus.turnType}</span> : null}
                  {liveTraceStatus.ingressCandidates > 0 ? <span className="online-trace-chip">ingress: {liveTraceStatus.ingressCandidates}</span> : null}
                  {liveTraceStatus.ingressBranchActionStateN > 0 ? <span className="online-trace-chip">ingress-branch-state: {liveTraceStatus.ingressBranchActionStateN}</span> : null}
                  {liveTraceStatus.flowCount > 0 ? <span className="online-trace-chip">flows: {liveTraceStatus.flowCount}</span> : null}
                  {liveTraceStatus.activeFlowKind ? <span className="online-trace-chip">fg-flow: {liveTraceStatus.activeFlowKind}</span> : null}
                  {liveTraceStatus.flowAccountingVersion ? <span className="online-trace-chip">flow-acct: {liveTraceStatus.flowAccountingDeliveredN}/{liveTraceStatus.flowCount}</span> : null}
                  {liveTraceStatus.attentionGateContract ? <span className="online-trace-chip">gate: {liveTraceStatus.attentionGateContract}</span> : null}
                  {liveTraceStatus.attentionGateDeliveryN > 0 ? <span className="online-trace-chip">gate-delivery: {liveTraceStatus.attentionGateDeliveryN}</span> : null}
                  {liveTraceStatus.attentionGateBindingWarningN > 0 ? <span className="online-trace-chip">gate-warn: {liveTraceStatus.attentionGateBindingWarningN}</span> : null}
                  {liveTraceStatus.flowAccountingObligationN > 0 ? <span className="online-trace-chip">obligations: {liveTraceStatus.flowAccountingObligationN}</span> : null}
                  {liveTraceStatus.flowAccountingNotDeliveredN > 0 ? <span className="online-trace-chip">flow-not-l1: {liveTraceStatus.flowAccountingNotDeliveredN}</span> : null}
                  {liveTraceStatus.flowAccountingOrphanCandidateN > 0 ? <span className="online-trace-chip">orphan-cand: {liveTraceStatus.flowAccountingOrphanCandidateN}</span> : null}
                  {liveTraceStatus.flowAccountingL1ActionGapN > 0 ? <span className="online-trace-chip">gap:l1-egress {liveTraceStatus.flowAccountingL1ActionGapN}</span> : null}
                  {liveTraceStatus.flowAccountingEgressDispatchGapN > 0 ? <span className="online-trace-chip">gap:egress-dispatch {liveTraceStatus.flowAccountingEgressDispatchGapN}</span> : null}
                  {liveTraceStatus.flowAccountingFeedbackGapN > 0 ? <span className="online-trace-chip">gap:dispatch-feedback {liveTraceStatus.flowAccountingFeedbackGapN}</span> : null}
                  {liveTraceStatus.flowAccountingFeedbackRealityGapN > 0 ? <span className="online-trace-chip">gap:feedback-reality {liveTraceStatus.flowAccountingFeedbackRealityGapN}</span> : null}
                  {liveTraceStatus.l1ReaderSource ? <span className="online-trace-chip">l1-reader: {liveTraceStatus.l1ReaderSource}</span> : null}
                  {liveTraceStatus.l1ReaderElided ? <span className="online-trace-chip">raw-elided</span> : null}
                  {liveTraceStatus.l1ActiveObject ? <span className="online-trace-chip">l1-object: {liveTraceStatus.l1ActiveObject}</span> : null}
                  {liveTraceStatus.l1ReplySource ? <span className="online-trace-chip">l1-egress: {liveTraceStatus.l1ReplySource}</span> : null}
                  {liveTraceStatus.l1EgressVersion ? <span className="online-trace-chip">egress-pkg: {liveTraceStatus.l1EgressVersion}</span> : null}
                  {liveTraceStatus.l1EgressIntegrityVersion ? <span className="online-trace-chip">egress-audit: {liveTraceStatus.l1EgressIntegrityValid ? 'ok' : 'gap'}</span> : null}
                  {liveTraceStatus.l1EgressIntegrityGapN > 0 ? <span className="online-trace-chip">egress-gaps: {liveTraceStatus.l1EgressIntegrityGapN}</span> : null}
                  {liveTraceStatus.l1EgressIntegrityRequiredGapN > 0 ? <span className="online-trace-chip">egress-required-gap: {liveTraceStatus.l1EgressIntegrityRequiredGapN}</span> : null}
                  {liveTraceStatus.l1EgressDecisionKind ? <span className="online-trace-chip">l1-decision: {liveTraceStatus.l1EgressDecisionKind}</span> : null}
                  {liveTraceStatus.l1BranchOutcomeDecisionKind ? <span className="online-trace-chip">l1-branch-decision: {liveTraceStatus.l1BranchOutcomeDecisionKind}</span> : null}
                  {liveTraceStatus.l1ActionCommandN > 0 ? <span className="online-trace-chip">l1-action-cmd: {liveTraceStatus.l1ActionCommandN}</span> : null}
                  {liveTraceStatus.l1EgressFlowUpdateN > 0 ? <span className="online-trace-chip">egress-flow-upd: {liveTraceStatus.l1EgressFlowUpdateN}</span> : null}
                  {liveTraceStatus.l1EgressNextOcm ? <span className="online-trace-chip">egress-next-ocm</span> : null}
                  {liveTraceStatus.l1EgressSuppressedN > 0 ? <span className="online-trace-chip">egress-suppressed: {liveTraceStatus.l1EgressSuppressedN}</span> : null}
                  {liveTraceStatus.l1DirectNoopRepair ? <span className="online-trace-chip">direct-noop: {liveTraceStatus.l1DirectNoopReason || 'marked'}</span> : null}
                  {liveTraceStatus.actionDispatchVersion ? <span className="online-trace-chip">action-shadow: {liveTraceStatus.actionDispatchFeedbackN}</span> : null}
                  {liveTraceStatus.actionDispatchCommandN > 0 ? <span className="online-trace-chip">action-cmd: {liveTraceStatus.actionDispatchCommandN}</span> : null}
                  {liveTraceStatus.actionDispatchWorkOrderN > 0 ? <span className="online-trace-chip">work-order: {liveTraceStatus.actionDispatchWorkOrderN}</span> : null}
                  {liveTraceStatus.actionDispatchWorkOrderAdapterN > 0 ? <span className="online-trace-chip">adapter-work: {liveTraceStatus.actionDispatchWorkOrderAdapterN}</span> : null}
                  {liveTraceStatus.actionDispatchWorkOrderBlockedN > 0 ? <span className="online-trace-chip">work-blocked: {liveTraceStatus.actionDispatchWorkOrderBlockedN}</span> : null}
                  {liveTraceStatus.actionDispatchDeliveryOk ? <span className="online-trace-chip">action-delivered</span> : null}
                  {liveTraceStatus.actionDispatchQueuedN > 0 ? <span className="online-trace-chip">action-queued: {liveTraceStatus.actionDispatchQueuedN}</span> : null}
                  {liveTraceStatus.actionDispatchCompletedN > 0 ? <span className="online-trace-chip">action-completed: {liveTraceStatus.actionDispatchCompletedN}</span> : null}
                  {liveTraceStatus.actionDispatchFailedN > 0 ? <span className="online-trace-chip">action-failed: {liveTraceStatus.actionDispatchFailedN}</span> : null}
                  {liveTraceStatus.actionDispatchSearchExecutedN > 0 ? <span className="online-trace-chip">action-search: {liveTraceStatus.actionDispatchSearchExecutedN}</span> : null}
                  {liveTraceStatus.actionDispatchBlockedN > 0 ? <span className="online-trace-chip">action-blocked: {liveTraceStatus.actionDispatchBlockedN}</span> : null}
                  {liveTraceStatus.capabilityRegistryKnownN > 0 ? <span className="online-trace-chip">capability-reg: {liveTraceStatus.capabilityRegistryKnownN}</span> : null}
                  {liveTraceStatus.capabilityRegistryAvailableN > 0 ? <span className="online-trace-chip">cap-available: {liveTraceStatus.capabilityRegistryAvailableN}</span> : null}
                  {liveTraceStatus.capabilityRegistryShadowN > 0 ? <span className="online-trace-chip">cap-shadow: {liveTraceStatus.capabilityRegistryShadowN}</span> : null}
                  {liveTraceStatus.capabilityRegistryBusyN > 0 ? <span className="online-trace-chip">cap-busy: {liveTraceStatus.capabilityRegistryBusyN}</span> : null}
                  {liveTraceStatus.capabilityRegistryBlockedN > 0 ? <span className="online-trace-chip">cap-blocked: {liveTraceStatus.capabilityRegistryBlockedN}</span> : null}
                  {liveTraceStatus.capabilityRegistryOfflineN > 0 ? <span className="online-trace-chip">cap-offline: {liveTraceStatus.capabilityRegistryOfflineN}</span> : null}
                  {liveTraceStatus.actionDispatchCapabilityLeaseN > 0 ? <span className="online-trace-chip">action-lease: {liveTraceStatus.actionDispatchCapabilityLeaseN}</span> : null}
                  {liveTraceStatus.actionDispatchCapabilityLeaseBlockedN > 0 ? <span className="online-trace-chip">lease-blocked: {liveTraceStatus.actionDispatchCapabilityLeaseBlockedN}</span> : null}
                  {liveTraceStatus.actionDispatchCapabilityLeaseReleasedN > 0 ? <span className="online-trace-chip">lease-released: {liveTraceStatus.actionDispatchCapabilityLeaseReleasedN}</span> : null}
                  {liveTraceStatus.lastActionDestination ? <span className="online-trace-chip">action-dest: {liveTraceStatus.lastActionDestination}</span> : null}
                  {liveTraceStatus.lastAdapterResultPacket ? <span className="online-trace-chip">adapter-result: {liveTraceStatus.lastAdapterResultPacket}</span> : null}
                  {liveTraceStatus.lastAdapterFailurePacket ? <span className="online-trace-chip">adapter-failure: {liveTraceStatus.lastAdapterFailurePacket}</span> : null}
                  {liveTraceStatus.nextOcmPosture ? <span className="online-trace-chip">next-ocm: {liveTraceStatus.nextOcmPosture}</span> : null}
                  {liveTraceStatus.actionLadderCompleteN > 0 ? <span className="online-trace-chip">action-ladder: {liveTraceStatus.actionLadderCompleteN}</span> : null}
                  {liveTraceStatus.actionLadderReadyForIngressN > 0 ? <span className="online-trace-chip">reality-ingress: {liveTraceStatus.actionLadderReadyForIngressN}</span> : null}
                  {liveTraceStatus.l4TailLifecycleReturnIngressN > 0 ? <span className="online-trace-chip">l4-return: {liveTraceStatus.l4TailLifecycleReturnIngressN}</span> : null}
                  {liveTraceStatus.l4TailLifecycleCompleteAckN > 0 ? <span className="online-trace-chip">l4-ack: {liveTraceStatus.l4TailLifecycleCompleteAckN}</span> : null}
                  {liveTraceStatus.l4TailLifecycleReturnAckGapN > 0 ? <span className="online-trace-chip">gap:l4-return-ack {liveTraceStatus.l4TailLifecycleReturnAckGapN}</span> : null}
                  {liveTraceStatus.memoryControllerVersion ? <span className="online-trace-chip">memory-shadow: {liveTraceStatus.memoryControllerAcceptedN}</span> : null}
                  {liveTraceStatus.memoryControllerRejectedN > 0 ? <span className="online-trace-chip">memory-rejected: {liveTraceStatus.memoryControllerRejectedN}</span> : null}
                  {liveTraceStatus.memoryControllerRetrievalN > 0 ? <span className="online-trace-chip">memory-retrieve: {liveTraceStatus.memoryControllerRetrievalN}</span> : null}
                  {liveTraceStatus.searchControllerVersion ? <span className="online-trace-chip">search: {liveTraceStatus.searchControllerOutcome || 'seen'}</span> : null}
                  {liveTraceStatus.searchControllerEvidenceN > 0 ? <span className="online-trace-chip">search-evidence: {liveTraceStatus.searchControllerEvidenceN}</span> : null}
                  {liveTraceStatus.searchControllerFailureKind ? <span className="online-trace-chip">search-failure: {liveTraceStatus.searchControllerFailureKind}</span> : null}
                  {liveTraceStatus.emergencyControllerVersion ? <span className="online-trace-chip">emergency-failures: {liveTraceStatus.emergencyBranchFailureN}</span> : null}
                  {liveTraceStatus.emergencySignalN > 0 ? <span className="online-trace-chip">emergency-signals: {liveTraceStatus.emergencySignalN}</span> : null}
                  {liveTraceStatus.emergencyHaltRecommended ? <span className="online-trace-chip">emergency-halt</span> : null}
                  {liveTraceStatus.branchActionStateVersion ? <span className="online-trace-chip">branch-state: {liveTraceStatus.branchActionStateActionN}</span> : null}
                  {liveTraceStatus.branchActionStateQueuedN > 0 ? <span className="online-trace-chip">branch-queued: {liveTraceStatus.branchActionStateQueuedN}</span> : null}
                  {liveTraceStatus.branchActionStateCompletedN > 0 ? <span className="online-trace-chip">branch-completed: {liveTraceStatus.branchActionStateCompletedN}</span> : null}
                  {liveTraceStatus.branchActionStateBlockedN > 0 ? <span className="online-trace-chip">branch-blocked: {liveTraceStatus.branchActionStateBlockedN}</span> : null}
                  {liveTraceStatus.branchActionStateFailureN > 0 ? <span className="online-trace-chip">branch-failures: {liveTraceStatus.branchActionStateFailureN}</span> : null}
                  {liveTraceStatus.branchActionStateOutcomeDigestN > 0 ? <span className="online-trace-chip">branch-digests: {liveTraceStatus.branchActionStateOutcomeDigestN}</span> : null}
                  {liveTraceStatus.branchActionStateZombieRiskN > 0 ? <span className="online-trace-chip">branch-zombie-risk: {liveTraceStatus.branchActionStateZombieRiskN}</span> : null}
                  {liveTraceStatus.l1CloseStatus ? <span className="online-trace-chip">l1-close: {liveTraceStatus.l1CloseStatus}</span> : null}
                  {liveTraceStatus.l1MemorySuppressed > 0 ? <span className="online-trace-chip">l1-memory-suppressed: {liveTraceStatus.l1MemorySuppressed}</span> : null}
                </Box>
                <Box className="online-trace-panel__body">
                  {liveTraceStatus.participantName ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>participant:</strong> {liveTraceStatus.participantName}
                      {liveTraceStatus.participantId ? ` (${liveTraceStatus.participantId})` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.runtimeStageHeadline || liveTraceStatus.runtimeStageRaw ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>runtime stage:</strong> {liveTraceStatus.runtimeStageHeadline || liveTraceStatus.runtimeStageRaw}
                      {liveTraceStatus.runtimeStageDetail ? `, ${liveTraceStatus.runtimeStageDetail}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.query ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>query:</strong> {liveTraceStatus.query}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.question ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>{liveTraceStatus.awaiting ? 'pending:' : 'question:'}</strong> {liveTraceStatus.question}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.reply ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>reply:</strong> {liveTraceStatus.reply}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.ingressCandidates > 0 ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>ingress:</strong> {liveTraceStatus.ingressCandidates} candidates
                    </Typography>
                  ) : null}
                  {Array.isArray(liveTraceStatus.ingressBranchActionStatePreview) && liveTraceStatus.ingressBranchActionStatePreview.length > 0 ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>branch-state ingress:</strong> {liveTraceStatus.ingressBranchActionStatePreview.join(' | ')}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.flowCount > 0 ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>flows:</strong> {liveTraceStatus.flowCount}
                      {liveTraceStatus.activeFlowKind ? `, foreground = ${liveTraceStatus.activeFlowKind}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.flowAccountingVersion ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>flow proof:</strong> delivered {liveTraceStatus.flowAccountingDeliveredN}
                      {liveTraceStatus.flowAccountingObligationN > 0 ? `, obligations ${liveTraceStatus.flowAccountingObligationN}` : ''}
                      {liveTraceStatus.attentionGateContract ? `, gate ${liveTraceStatus.attentionGateContract}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.l1EgressIntegrityVersion ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>egress audit:</strong> {liveTraceStatus.l1EgressIntegrityValid ? 'ok' : 'gap'}
                      {`, gaps ${liveTraceStatus.l1EgressIntegrityGapN}`}
                      {liveTraceStatus.l1EgressIntegrityRequiredGapN > 0 ? `, required ${liveTraceStatus.l1EgressIntegrityRequiredGapN}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.capabilityRegistryKnownN > 0 ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>capability proof:</strong> registry {liveTraceStatus.capabilityRegistryKnownN}
                      {`, available ${liveTraceStatus.capabilityRegistryAvailableN}`}
                      {`, shadow ${liveTraceStatus.capabilityRegistryShadowN}`}
                      {liveTraceStatus.capabilityRegistryBusyN > 0 ? `, busy ${liveTraceStatus.capabilityRegistryBusyN}` : ''}
                      {liveTraceStatus.capabilityRegistryBlockedN > 0 ? `, blocked ${liveTraceStatus.capabilityRegistryBlockedN}` : ''}
                      {liveTraceStatus.capabilityRegistryOfflineN > 0 ? `, offline ${liveTraceStatus.capabilityRegistryOfflineN}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.lastActionDestination || liveTraceStatus.actionDispatchCapabilityLeaseN > 0 || liveTraceStatus.nextOcmPosture ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>action dispatch:</strong> {liveTraceStatus.lastActionDestination || 'no destination'}
                      {liveTraceStatus.actionDispatchWorkOrderN > 0 ? `, work orders ${liveTraceStatus.actionDispatchWorkOrderN}` : ''}
                      {liveTraceStatus.actionDispatchWorkOrderAdapterN > 0 ? `, adapter work ${liveTraceStatus.actionDispatchWorkOrderAdapterN}` : ''}
                      {liveTraceStatus.actionDispatchWorkOrderShadowN > 0 ? `, shadow work ${liveTraceStatus.actionDispatchWorkOrderShadowN}` : ''}
                      {liveTraceStatus.actionDispatchWorkOrderBlockedN > 0 ? `, blocked work ${liveTraceStatus.actionDispatchWorkOrderBlockedN}` : ''}
                      {liveTraceStatus.lastAdapterResultPacket ? `, result ${liveTraceStatus.lastAdapterResultPacket}` : ''}
                      {liveTraceStatus.lastAdapterFailurePacket ? `, failure ${liveTraceStatus.lastAdapterFailurePacket}` : ''}
                      {liveTraceStatus.actionDispatchCapabilityLeaseN > 0 ? `, leases ${liveTraceStatus.actionDispatchCapabilityLeaseN}` : ''}
                      {liveTraceStatus.actionDispatchCapabilityLeaseBlockedN > 0 ? `, lease blocked ${liveTraceStatus.actionDispatchCapabilityLeaseBlockedN}` : ''}
                      {liveTraceStatus.actionDispatchCapabilityLeaseReleasedN > 0 ? `, released ${liveTraceStatus.actionDispatchCapabilityLeaseReleasedN}` : ''}
                      {liveTraceStatus.nextOcmPosture ? `, next ${liveTraceStatus.nextOcmPosture}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.actionLadderCompleteN > 0 || liveTraceStatus.actionLadderReadyForIngressN > 0 || liveTraceStatus.flowAccountingFeedbackRealityGapN > 0 ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>action ladder:</strong> reality {liveTraceStatus.actionLadderCompleteN}
                      {`, next-ingress ${liveTraceStatus.actionLadderReadyForIngressN}`}
                      {liveTraceStatus.flowAccountingFeedbackRealityGapN > 0 ? `, feedback-reality gap ${liveTraceStatus.flowAccountingFeedbackRealityGapN}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.l4TailLifecycleReturnIngressN > 0 || liveTraceStatus.l4TailLifecycleCompleteAckN > 0 || liveTraceStatus.l4TailLifecycleReturnAckGapN > 0 ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>L4 tail:</strong> return {liveTraceStatus.l4TailLifecycleReturnIngressN}
                      {`, ack ${liveTraceStatus.l4TailLifecycleCompleteAckN}`}
                      {liveTraceStatus.l4TailLifecycleReturnAckGapN > 0 ? `, ack gap ${liveTraceStatus.l4TailLifecycleReturnAckGapN}` : ''}
                    </Typography>
                  ) : null}
                  {liveTraceStatus.l1ActiveObject || liveTraceStatus.l1ReplySource ? (
                    <Typography variant="body2" className="online-trace-panel__line">
                      <strong>L1 lane:</strong> {liveTraceStatus.l1ActiveObject || 'no object'}
                      {liveTraceStatus.l1ReplySource ? `, egress = ${liveTraceStatus.l1ReplySource}` : ''}
                      {liveTraceStatus.l1TerminalPacket ? `, terminal = ${liveTraceStatus.l1TerminalPacket}` : ''}
                      {liveTraceStatus.l1CloseStatus ? `, close = ${liveTraceStatus.l1CloseStatus}` : ''}
                      {liveTraceStatus.l1MemorySuppressed > 0 ? `, memory suppressed = ${liveTraceStatus.l1MemorySuppressed}` : ''}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ) : null}
            <ChatWindow
              history={history}
              ref={chatEndRef}
              apiBase={API_BASE}
              debugTrace={traceDebugEnabled}
              liveTraceStatus={liveTraceStatus}
            />
            <Box className="composer-panel" sx={{ display: 'flex', flexDirection: 'column', mt: 2, gap: 1 }}>
              <Box className="composer-head">
                <Typography variant="caption" className="section-kicker">
                  Composer
                </Typography>
                <Typography variant="body2" className="section-title">
                  Сообщение, ручной тик и быстрая подача контекста
                </Typography>
              </Box>
              {activeThreadId !== RUNTIME_THREAD_ID ? (
                <TextField
                  size="small"
                  label="Собеседник"
                  value={activeParticipant.name}
                  disabled={loading}
                  onChange={(e) => updateActiveParticipantName(e.target.value)}
                  helperText={`window ${String(activeParticipant.id || '').slice(0, 22)}`}
                  sx={{ maxWidth: 360, bgcolor: 'var(--input-bg)', color: 'var(--ink)' }}
                />
              ) : null}
              <TextField
                fullWidth
                inputRef={inputRef}
                label="Введите сообщение"
                value={message}
                disabled={loading}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                sx={{ bgcolor: 'var(--input-bg)', color: 'var(--ink)' }}
              />
              <input
                className="composer-upload"
                type="file"
                accept="image/*"
                disabled={loading}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setImageFile(file);
                }}
              />
              {imageFile && (
                <Typography variant="body2" sx={{ color: darkMode ? "#bbb" : "#222" }}>
                  📎 Изображение выбрано: {imageFile.name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleSendMessage} disabled={loading} sx={{ borderRadius: 999, px: 2.2 }}>
                  {loading ? <CircularProgress size={22} /> : 'Отправить'}
                </Button>
                <Button variant="outlined" onClick={handleRunTick} disabled={loading} sx={{ borderRadius: 999, px: 2.2 }}>
                  Тик (system)
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default App;
